/**
 * EN 辞書＝日本語の原文 → 英訳。i18n.ts の t() だけが読む。
 *
 * 書き方の規約:
 *   - キーは画面に出る日本語の【完全一致】（t() に渡した文字列そのまま）
 *   - 画面名の訳語は営業資料 EN 版（docs/sales-kit/content/en/）と揃える
 *   - 通貨・精度の数値クレームを足さない（Decisions）
 */

export const EN: Record<string, string> = {
  // ナビゲーション（Layout）
  ホーム: "Home",
  ダッシュボード: "Dashboard",
  注文書: "Purchase orders",
  "発注済み・入荷": "Ordered & receiving",
  発注点設定: "Reorder points",
  発注元情報: "Company profile",
  在庫調整: "Stock adjustments",
  棚卸し: "Stocktakes",
  拠点間移動: "Transfers",
  セット商品の在庫: "Kit inventory",
  在庫分析: "Inventory analytics",
  資金繰りカレンダー: "Cash-flow calendar",
  "Stocky CSV 取り込み": "Stocky CSV import",
  Stockyデータ取込: "Stocky data import",
  通知設定: "Notifications",
  表示言語: "Display language",
  "自動（Shopify）": "Automatic (Shopify)",
  プラン: "Plans",
  よくある質問: "FAQ",
  "ストアを安全に確認できませんでした。Shopify管理画面からアプリを開き直してください。":
    "We could not safely verify the store. Reopen the app from Shopify admin.",
  ストアを確認中: "Verifying store",
  "ストアを確認中です…": "Verifying the store…",
  "ストア情報を確認できませんでした。時間をおいて再試行してください。":
    "We could not verify the store information. Wait a moment and try again.",
  // ナビのセクション見出し
  発注: "Purchasing",
  倉庫: "Warehouse",
  分析: "Analytics",
  "データ・設定": "Data & settings",

  // ホーム
  "ようこそ、": "Welcome, ",
  Stockyデータ取込はFreeプランで利用できます:
    "Stocky data import is available on the Free plan",
  "Shopifyへの請求は発生しません。ボタンを押すとFreeプランを有効にし、データ取込へ進みます。":
    "There is no Shopify charge. Press the button to activate the Free plan and continue to data import.",
  Freeでデータ取込を始める: "Start data import on Free",
  有料プランを比較する: "Compare paid plans",
  "Free プランを開始できませんでした": "Could not start the Free plan",
  "ストア情報を確認できませんでした。ページを再読み込みして再試行してください。":
    "We could not verify the store. Reload the page and try again.",
  "Free プランの有効化を確認できませんでした。再試行してください。":
    "We could not confirm that the Free plan was activated. Try again.",
  プランを見る: "View plans",
  データ取込を開く: "Open data import",
  困ったときは: "Need help?",
  "エラーで画面が表示されないときの対処や、使い方をまとめています。":
    "Troubleshooting for blank screens and errors, plus how-to guides.",

  // 初期セットアップのチェックリスト（SetupChecklist）
  最初にやること: "Getting started",
  "残り{count}件": "{count} left",
  初期セットアップは完了しています: "Initial setup is complete",
  セットアップの内容を見る: "Review setup",
  閉じる: "Close",
  完了: "Done",
  在庫データを同期する: "Sync your inventory data",
  同期する: "Sync",
  仕入先を登録して商品に割り当てる: "Add suppliers and assign products to them",
  仕入先を設定する: "Set up suppliers",
  "仕入先が未割当の商品が{count}件あります":
    "{count} products have no supplier assigned",
  発注点を設定する: "Set your reorder points",
  発注点を開く: "Open reorder points",
  // 発注点は任意＝無くても数量を手入力して発注できる。「効くようになるもの」として書く。
  設定すると自動提案が効きます: "Set these to get suggested order quantities",
  "設定すると自動提案が効きます（Starter 以上）":
    "Set these to get suggested order quantities (Starter and above)",
  "発注点が未設定の商品が{count}件あります":
    "{count} products have no reorder point set",
  通知の受け取り先を決める: "Choose where alerts are sent",
  "メール（Starter以上）/ Slack・LINE（Pro以上）から選べます":
    "Email on Starter and above; Slack and LINE on Pro and above",
  在庫アラートのメール通知はStarter以上で利用できます:
    "Inventory alert emails are available on Starter and above",
  通知先を設定する: "Set up notifications",
  "Shopifyは、Stocky終了後も少なくとも90日間は読み取り専用でアクセスできると案内しています。このコピー経路を利用できるのは、対応するStockyのページ／UIが利用可能で、構造が変更されていない間だけです。90日を超える期間の利用は保証されません。":
    "Shopify states that read-only access will remain available for at least 90 days after Stocky shuts down. This copy route works only while the supported Stocky pages and UI remain available and structurally unchanged. Use beyond that period is not guaranteed.",

  // よくある質問（Help）
  "UreyukiBox の使い方やトラブルの対処をまとめています。解決しない場合は":
    "Guides and troubleshooting for UreyukiBox. If none of these solve it, email us at",
  "までご連絡ください。": ".",
  解決しないときは: "Still stuck?",
  "までメールでお問い合わせください。ストアの URL と、できれば表示されたエラー内容を添えていただくと、確認がスムーズです。":
    "— email us. Including your store URL and the exact error message helps us investigate faster.",
  お問い合わせ: "Contact",
  お問い合わせを開く: "Open contact",
  "問題がございましたら、お電話ください": "If you run into any issues, call us",
  "UreyukiBox の操作や Stocky 移行についてお困りの点をお伺いします。":
    "We can help with UreyukiBox and your Stocky migration.",
  メールでのお問い合わせ: "Contact us by email",
  "ストアの URL と、できれば表示されたエラー内容を添えていただくと、確認がスムーズです。":
    "Including your store URL and the exact error message helps us investigate faster.",

  "インストールしたのに、エラーが出て画面が表示されません":
    "I installed the app but get an error and nothing loads",
  "多くの場合、次の操作で自動的に復旧します。上から順にお試しください。":
    "In most cases the app recovers on its own. Try these in order.",
  "ページを再読み込みする（またはアプリを開き直す）。":
    "Reload the page (or close and reopen the app).",
  "UreyukiBox は Shopify との連携（認証）が未完了のとき、開き直すと自動的に認証をやり直します。多くの表示エラーはこれで解消します。":
    "If the Shopify connection (authentication) did not finish, reopening the app retries it automatically. This clears most display errors.",
  "少し待ってから「再試行」を押す。": "Wait a moment, then press “Retry”.",
  "一時的な通信エラーやサーバー側の混雑で、まれに読み込みに失敗することがあります。エラー画面の「再試行」ボタンを押すか、少し待ってからもう一度開いてください。":
    "A temporary network issue or server-side congestion can occasionally make a page fail to load. Press “Retry” on the error screen, or wait a moment and open the app again.",
  "一度アンインストールして入れ直した場合も、アプリを開くだけで大丈夫です。":
    "If you uninstalled and reinstalled, simply opening the app is enough.",
  "再インストール後は、アプリを開くと自動的に Shopify と再連携します。特別な操作は要りません。":
    "After reinstalling, opening the app reconnects it to Shopify automatically. No extra steps are needed.",
  "それでも表示されないときは、以下をお試しください。":
    "If it still does not load, try the following.",
  "ブラウザのキャッシュを消す、またはシークレットウィンドウ／別のブラウザで開く。":
    "Clear your browser cache, or open the app in a private window or a different browser.",
  "Shopify 管理画面 →「設定」→「アプリと販売チャネル」から UreyukiBox を一度削除し、あらためてインストールする。":
    "In Shopify admin, go to Settings → Apps and sales channels, remove UreyukiBox, then install it again.",
  上記でも直らない場合は: "If none of the above helps, contact",
  "へ、ストアの URL（◯◯.myshopify.com）と表示されたエラー内容を添えてご連絡ください。":
    "with your store URL (yourstore.myshopify.com) and the error message you saw.",

  "データはいつ反映されますか（同期のタイミング）":
    "When does my data appear? (sync timing)",
  "インストール後、商品・在庫・注文を自動で読み込みます。初回はデータ量に応じて数分かかることがあります。以降は毎日自動で同期され、最新の状態に保たれます。":
    "After installation we import your products, inventory and orders automatically. The first import can take a few minutes depending on volume. After that the app syncs daily and stays up to date.",

  "在庫や注文が勝手に書き換えられませんか（安全性）":
    "Can the app change my inventory or orders on its own? (safety)",
  "Shopify との同期は読み取りのみです。UreyukiBox がひとりでに在庫を書き換えることはありません。":
    "Syncing with Shopify is read-only. UreyukiBox never changes your inventory on its own.",
  "在庫が変わるのは、あなたが操作した次の4つのときだけです：①入荷登録　②在庫調整　③棚卸しの確定　④拠点間移動。注文書は確認・出力のための下書きで、作成しただけでは在庫は動きません。":
    "Inventory changes only when you perform one of these four actions: (1) receiving stock, (2) a stock adjustment, (3) finalizing a stocktake, or (4) a transfer between locations. Purchase orders are drafts for review and export — creating one does not move any stock.",

  "「Shopify注文書用CSV」を取り込もうとしたらエラーになります":
    "The “Shopify purchase order CSV” fails to import",
  "「Shopify注文書用CSV」は Shopify 純正の「注文書」画面専用の取り込みファイルです。商品管理トップの「インポート」（商品一括登録用）や「在庫」画面の「インポート」（在庫数一括更新用）は別のテンプレートのため、同じファイルを入れると「商品名の列は必須です」「無効なCSVヘッダーです」等のエラーになります。":
    "That file is meant only for Shopify’s built-in Purchase orders screen. The Import button on the Products page (for bulk product creation) and the one on the Inventory page (for bulk quantity updates) use different templates, so the same file produces errors such as “Title column is required” or “Invalid CSV header”.",
  "正しい取り込み場所は、Shopify 管理画面の「商品管理」→「注文書」→「注文書を作成」（または既存の注文書を開く）を選び、その画面の中にある「インポート」からファイルを追加する手順です。":
    "The correct place is Shopify admin → Products → Purchase orders → Create purchase order (or open an existing one), then use the Import button inside that screen.",
  "純正側は商品を SKU またはバーコード（JAN）で照合するため、どちらも未設定の商品はこの CSV に含まれません。必要であれば純正の注文書画面で手動追加してください。":
    "Shopify matches items by SKU or barcode, so products with neither are left out of this CSV. Add them by hand in the native purchase order screen if you need them.",
  "この CSV は、Shopify 純正の注文書画面でも入荷予定・受領を管理したい場合に使います。UreyukiBox の中だけで回す場合は不要です（注文書の「注文済みとしてマーク」→「発注済み・入荷」ページで入荷を登録）。同じ発注を両方に登録すると二重に差し引かれるため、どちらか一方で管理してください。":
    "Use this CSV only if you also want to track incoming stock in Shopify’s native purchase order screen. If you work entirely inside UreyukiBox you do not need it — mark the order as placed, then record receipts on the Ordered & receiving page. Recording the same order in both places double-counts it, so pick one.",
  注文書を作成: "Create purchase order",

  "料金・プランについて教えてください": "How does pricing work?",
  "無料の Free プランから利用できます。標準の無料トライアルは1ストア通算14日です。残り日数とプランごとの機能はプラン画面でご確認いただけます。":
    "You can start on the Free plan. The standard free trial is 14 days total per store. See the Plans page for your remaining days and each plan's features.",

  "Stocky からデータを取り込めますか": "Can I import my data from Stocky?",
  "Stockyの公式エクスポートや、終了前に保存した対応CSV・画面表があれば、取得できた範囲をUreyukiBoxへ取り込めます。ファイルにない項目は推測して補完しません。":
    "If you have Stocky's official export or compatible CSV files and onscreen tables saved before shutdown, you can import the data they contain into UreyukiBox. Missing fields are never inferred.",

  アンインストールするとデータはどうなりますか:
    "What happens to my data if I uninstall?",
  "アプリをアンインストールすると、当社が保持しているストアのデータは自動的に削除されます。再度インストールした場合は、Shopify から改めてデータを読み込み直します。":
    "Uninstalling the app automatically deletes the store data we hold. If you install it again, we re-import your data from Shopify.",

  "欲しい機能があります（要望を送りたい）": "I have a feature request",
  "ぜひお聞かせください。「こんな機能が欲しい」「ここが使いにくい」といったご要望は":
    "We would love to hear it. Send requests — a feature you want, or something that feels awkward — to",
  "までお気軽にお送りください。今後のアップデートの参考にさせていただきます。":
    ". We use them to decide what to build next.",

  // プラン（Plans）
  無料期間特典が適用されています: "Your extended free trial is applied",
  "Free 以外のプランを選ぶと、最初の {days} 日間（約 {months} ヶ月）が無料になります。無料期間は Shopify の承認画面にも表示されます。期間が終わると通常料金へ移行します（それまでにいつでも解約できます）。":
    "Choose any paid plan and your first {days} days (about {months} months) are free. The free period is also shown on Shopify’s approval screen. After it ends the plan moves to its regular price — you can cancel any time before that.",
  "いつでもアップグレード・ダウングレード・解約できます。":
    "Upgrade, downgrade or cancel at any time.",
  "Free 以外は最初の {days} 日間が無料です（無料期間特典）。":
    "Paid plans start with {days} free days (extended free trial).",
  "Free 以外は 14 日間の無料トライアル付き。":
    "Paid plans include a 14-day free trial.",
  "有料プランは米ドル（USD）で請求されます（Shopify の請求通貨が日本円の場合は円建て）。正確な請求額は Shopify の承認画面でご確認いただけます。":
    "Paid plans are billed in US dollars (or in yen if your Shopify billing currency is JPY). The exact amount is shown on the Shopify approval screen.",
  "標準の無料トライアルは1ストア通算14日です。現在の残りは {days} 日で、プラン変更でも引き継がれます。":
    "The standard free trial is 14 days total per store. You have {days} days remaining, and that balance carries over when you change plans.",
  "無料トライアルは利用済みです。有料プランはすぐに課金が始まります。":
    "Your free trial has been used. Paid plans start billing right away.",
  "Pro の「在庫分析（評価額・ABC）」を見る":
    "See Pro’s inventory analytics (valuation and ABC)",
  "プラン変更に失敗しました:": "Could not change your plan:",
  "Free プランへ変更しますか？": "Switch to the Free plan?",
  "有料プランを解約して Free へ変更": "Cancel paid plan and switch to Free",
  変更しない: "Keep my current plan",
  "確定すると Shopify の現在の有料契約をすぐに解約し、Free プランへ変更します。":
    "Confirming will immediately cancel your current paid Shopify subscription and switch you to the Free plan.",
  "発注点の自動計算、発注リマインダー、在庫分析など有料プランの機能は利用できなくなります。":
    "Paid features such as automatic reorder points, reorder reminders and inventory analytics will no longer be available.",
  "保存済みのデータは削除されません。再契約すると再び利用できます。":
    "Your saved data will not be deleted. You can use it again after resubscribing.",
  有料プランの変更を確認: "Confirm paid plan change",
  "現在のプラン: {plan}": "Current plan: {plan}",
  "変更先のプラン: {plan}": "New plan: {plan}",
  "{plan}へ変更する": "Switch to {plan}",
  "確認後にShopifyの承認画面へ進みます。承認するまでプランは変更されません。":
    "After confirmation, you will continue to Shopify's approval screen. Your plan will not change until you approve it.",
  "FreeプランではShopifyへの請求は発生しません。まず保存済みStockyデータを取り込み、必要になったときだけ有料プランを比較できます。":
    "The Free plan creates no Shopify charge. Import your saved Stocky data first, then compare paid plans only if you need them.",
  説明を取得できませんでした: "Could not load the explanation",
  "時間をおいて、もう一度「なぜこの数字？」を押してください。":
    "Wait a moment, then press “Why this number?” again.",
  対応済みへの更新結果を確認できませんでした:
    "Could not confirm whether the alert was marked as resolved",
  "通信状況によっては更新済みの可能性があります。最新の状態を再取得しています。表示を確認してから、必要な場合だけもう一度お試しください。":
    "The update may already have completed despite the connection problem. We are loading the latest status. Check it before trying again.",
  リードタイムを一括適用しています: "Applying lead times in bulk",
  "{supplier} の {count} 件をまとめて更新しています。完了するまで、この画面の保存操作はできません。":
    "Updating {count} products for {supplier}. Save actions on this page are disabled until it finishes.",
  リードタイムの一括適用結果を確認できませんでした:
    "Could not confirm the bulk lead-time update",
  "通信状況によっては適用済みの可能性があります。最新の状態を再取得しています。対象: {items}":
    "The update may already have completed despite the connection problem. We are loading the latest status. Targets: {items}",
  保存結果を確認できませんでした:
    "Could not confirm whether the change was saved",
  "通信状況によっては保存済みの可能性があります。最新の状態を再取得しています。表示を確認してから、必要な場合だけもう一度保存してください。":
    "The change may already have been saved despite the connection problem. We are loading the latest status. Check it before saving again.",
  リードタイムを一括適用しました: "Lead times applied",
  "{supplier} の {count} 件をすべて更新しました。":
    "Updated all {count} products for {supplier}.",
  "{items}、ほか {count} 件": "{items}, and {count} more",
  プランの取得に失敗しました: "Could not load plans",
  契約情報の取得に失敗しました: "Could not load billing details",
  "Shopify の請求通貨の取得に失敗しました":
    "Could not load your Shopify billing currency",
  "未保存の編集を破棄しますか？": "Discard unsaved changes?",
  編集を破棄して切り替える: "Discard changes and switch",
  編集を続ける: "Keep editing",
  "数量、除外、追加明細、発注書情報など、この注文書だけの未保存編集があります。キャンセルすると入力を保持したまま元の注文書へ戻ります。":
    "This purchase order has unsaved quantities, exclusions, added lines, or document details. Cancel to return without losing your input.",
  未保存の編集があります: "You have unsaved changes",
  "仕入先・配送先・季節プレビューを切り替える前に確認します。":
    "We will ask before switching supplier, destination, or seasonal preview.",
  利用中: "Current",
  無料: "Free",
  "/ 月": "/ month",
  現在のプラン: "Current plan",
  "{days}日間無料で試す": "Try free for {days} days",
  "残り{days}日間": "{days} days remaining",
  選ぶ: "Choose",

  // プランの説明（PLAN_COPY）
  "Stocky からの移行と手動運用に": "For migrating from Stocky and manual work",
  "商品数 100 件まで": "Up to 100 products",
  "Shopify 在庫の同期": "Shopify inventory sync",
  "手動で注文書を作成（数量手入力・PDF/CSV）":
    "Create purchase orders manually (enter quantities, export PDF/CSV)",
  "売れ筋 TOP 10・在庫の見える化": "Top 10 sellers and inventory visibility",
  発注の自動化を始めたい方へ: "For teams starting to automate purchasing",
  "商品数 1,000 件まで": "Up to 1,000 products",
  "Free のすべて": "Everything in Free",
  "発注点の自動計算（発注点管理）":
    "Automatic reorder point calculation (reorder points)",
  メールで発注リマインダー: "Reorder reminders by email",
  "発注数量を自動提案（自動注文書）":
    "Suggested order quantities (automatic purchase orders)",
  在庫を数字で管理したい: "For managing inventory by the numbers",
  "商品数 10,000 件まで": "Up to 10,000 products",
  "Starter のすべて": "Everything in Starter",
  "在庫分析（評価額・ABC分析）":
    "Inventory analytics (valuation, ABC analysis)",
  "30 日売上履歴": "30-day sales history",
  "LINE・Slack 通知": "LINE and Slack notifications",
  "大規模・多拠点の運用に": "For larger, multi-location operations",
  "商品数 無制限": "Unlimited products",
  "Pro のすべて": "Everything in Pro",
  "拠点数 1": "1 location",
  "拠点数 2 まで": "Up to 2 locations",
  "拠点数 5 まで": "Up to 5 locations",
  "拠点数 無制限": "Unlimited locations",
  "{count}人が登録済み（通知はオフ）": "{count} linked (notifications off)",
  電話サポート: "Phone support",

  // ─────────────────────────────────────────────────────────────────
  // Stocky CSV 取り込み（StockyCsvImport / CsvImportPanel / specs）
  //
  // 🔴 英語圏の Stocky 難民が最初に触る画面＝Shopify/Stocky の実語彙で書く
  //    （supplier / SKU / reorder point / lead time / MOQ / purchase order）。
  // 🔴 件数が入る文は【単数・複数で崩れない言い回し】にする＝t() は複数形の
  //    活用を持たないので "1 rows" のような崩れを文型で避ける
  //    （"Rows skipped (1): …" は 1 でも 5 でも正しい）。
  // ─────────────────────────────────────────────────────────────────

  // ページ（StockyCsvImport）
  "Stocky データのコピー": "Stocky data copy",

  // 棚卸しの履歴（実施日・拠点は CSV に無い。不明のまま保存して後から選べる）
  棚卸しの履歴: "Stocktake history",
  "選ぶファイル: download_csv.csv（棚卸し1件につき1ファイル・複数選択可）":
    "Files to choose: one download_csv.csv per stocktake (you can select multiple files)",
  // ⚠️「メモ（任意）」「SKU の列」「棚卸し」「失敗」は既に辞書にある＝ここで
  //   再定義しない（JS のオブジェクトは後勝ちで黙るので、捕まえるのは tsc だけ）。
  "例: Stocky から取り込み（本店）":
    "e.g. Imported from Stocky (main location)",
  "棚卸しの CSV ファイル（複数選べます）":
    "Stocktake CSV files (you can select more than one)",
  "ファイルを読み込んでいます…": "Reading files…",
  "Stocky で Inventory → Stocktakes → 残したい棚卸しを1件開く → CSV。棚卸し1回につき1ファイルで、download_csv.csv（download_csv (1).csv / (2).csv も同じ形式）です。":
    "In Stocky, go to Inventory → Stocktakes, open one stocktake you want to keep, then choose CSV. Export one file per stocktake; download_csv.csv and browser-renamed (1)/(2) files use the same format.",
  "Stocky の棚卸し CSV を自動認識しました": "Recognized a Stocky stocktake CSV",
  "Shopify ID・SKU・Product・Variant・Adjustment は自動で読み取ります。CSV には Current Stock が商品を数える前か後か書かれていないため、ファイルごとに下から選んでください。迷ったら「分からない（おすすめ）」を選べます。":
    "Shopify ID, SKU, Product, Variant, and Adjustment are read automatically. The CSV does not say whether Current Stock is from before or after the count. Choose an option for each file below; if unsure, choose “Not sure (recommended).”",
  "各ファイルで、Current Stock が商品を数える前か後かを選んでください。迷ったら「分からない（おすすめ）」を選べます。":
    "For each file, choose whether Current Stock is from before or after counting the products. If unsure, choose “Not sure (recommended).”",
  "{name}: Current Stock はどの在庫数ですか？":
    "{name}: Which inventory quantity is Current Stock?",
  "分からない（おすすめ）": "Not sure (recommended)",
  "数量を計算せず、CSV の Current Stock と Adjustment をそのまま保存します。":
    "Save the Current Stock and Adjustment values exactly as they appear in the CSV, without calculating quantities.",
  商品を数えた後の在庫数: "Inventory after counting the products",
  "棚卸しで実際に数え終わった数量です。":
    "The actual quantity after the stocktake was counted.",
  "商品を数える前に Stocky に表示されていた在庫数":
    "Inventory shown in Stocky before counting",
  "棚卸しを始める前の帳簿上の数量です。":
    "The system quantity shown before the stocktake began.",
  フォーマットが違います: "The format is different",
  "Stocky の棚卸し画面からダウンロードした download_csv.csv をアップロードしてください。ファイル名を変更していても同じ形式なら使えます。Stocky以外のCSVを使う場合だけ、詳細設定を開いて列の意味を指定してください。":
    "Upload the download_csv.csv file from the Stocky stocktake screen. You can still use it if you renamed the file, as long as its format is unchanged. Open advanced settings only when importing a non-Stocky CSV, then specify what each column means.",
  "詳細設定（Stocky以外のCSV）を開く":
    "Open advanced settings (non-Stocky CSV)",
  詳細設定を閉じる: "Close advanced settings",
  "（空の列名）": "(blank column name)",
  "同じ列名が複数あります。列名を一意にしてから再度アップロードしてください: {columns}":
    "Duplicate column names were found. Make every column name unique, then upload again: {columns}",
  "同じCSV列を複数の項目に使用できません。項目ごとに別の列を選んでください。":
    "The same CSV column cannot be used for multiple fields. Choose a different column for each field.",
  "選んだすべての棚卸しに同じメモが付きます。":
    "The same note is added to every stocktake you selected.",
  "列の対応付けは選んだファイル全部に使われます（同じ書き出し画面の CSV なら列構成は同じです）。":
    "The column mapping applies to every file you selected — CSVs exported from the same Stocky screen share the same columns.",
  "列の構成が他のファイルと違います。同じ書き出し画面の CSV だけをまとめて選んでください。":
    "This file's columns differ from the others. Select only CSVs exported from the same Stocky screen.",
  "一度に選べるのは {max} ファイルまでです。分けて取り込んでください。":
    "You can select up to {max} files at a time. Please import them in batches.",
  "選んだファイル（{count} 件）": "Selected files ({count})",
  "{rows} 行": "{rows} rows",
  "{name} の実施日": "Date counted for {name}",
  "この日付の棚卸しは既に登録されています。同じファイルを二重に取り込んでいないかご確認ください。":
    "A stocktake already exists for this date. Check that you are not importing the same file twice.",
  "同じ実施日のファイルが他にもあります。":
    "Another selected file has the same date counted.",
  "{name} の実施拠点": "Location for {name}",
  "{name}（無効）": "{name} (inactive)",
  拠点を取得できませんでした: "Could not load locations",
  "画面を再読み込みしてから、もう一度お試しください。":
    "Reload the page, then try again.",
  "{count} 件の内容を確認する": "Check {count} files",
  "{count} 件": "{count} items",
  "処理 {count} 件": "{count} processed",
  "取得・処理 合計 {count} 件": "Processed total: {count}",
  "確認した内容で {count} 件を取り込む": "Import {count} confirmed files",
  "事前確認が完了しました。内容を確認して取り込んでください":
    "Preflight check complete. Review the details before importing.",
  "{count} 件に対象外の行があります。内訳を確認してから取り込んでください":
    "Review excluded lines in {count} file(s) before importing.",
  "このボタンを押すまでは棚卸しを作成しません。取り込んでも Shopify の在庫は変わりません。":
    "No stocktake is created until you press this button. Importing does not change Shopify inventory.",
  "記録対象が0件のファイルは作成していません。内訳を確認し、列の対応付けやCSVを修正してから再確認してください。":
    "Files with zero recordable lines were not created. Review the details, correct the mapping or CSV, then check again.",
  "{done} 件を取り込み、{failed} 件は失敗しました":
    "Imported {done}, failed {failed}",
  "{done} 件の棚卸しを取り込みました（在庫は変わっていません）":
    "Imported {done} stocktakes. Your stock levels have not changed.",
  "取り込めたぶんはそのまま残ります。失敗したファイルだけ、もう一度取り込めます。":
    "Everything that imported stays. You can retry just the files that failed.",
  未取り込み: "Not imported",
  未確認: "Not checked",
  確認中: "Checking",
  確認済み: "Checked",
  同じCSVを確認してください: "Confirm identical CSV",
  記録対象なし: "Nothing to record",
  取り込み中: "Importing",
  取り込み済み: "Imported",
  一部を取り込み済み: "Partially imported",
  "取り込み済み（同じCSVあり）": "Imported (identical CSV exists)",
  すでに取り込み済み: "Already imported",
  取り込めません: "Cannot import",
  "この CSV は棚卸し #{id} として取り込み済みです":
    "This CSV has already been imported as stocktake #{id}",
  "この CSV は取り込み済みです（重複して作成していません）":
    "This CSV has already been imported. No duplicate was created.",
  "この CSV は棚卸し #{id} として取り込み済みです。重複して作成していません。":
    "This CSV has already been imported as stocktake #{id}. No duplicate was created.",
  "同じ内容のCSVを使った履歴が既に {count} 件あります。別の棚卸しならそのまま取り込めます。通信再試行は自動で重複を防ぎます。":
    "There are already {count} stocktake record(s) made from identical CSV content. You can continue if this is a separate stocktake; network retries are deduplicated automatically.",
  同じ内容のCSVを別の棚卸しとして保存するか確認してください:
    "Confirm whether to save the identical CSV as a separate stocktake",
  "通信再試行では重複しません。ファイルを選び直した今回の操作を別の棚卸しとして残す場合だけ、各ファイルの確認欄を選んでください。":
    "Network retries do not create duplicates. Select each confirmation only if this newly selected file represents a separate stocktake.",
  "同じ内容ですが、別の棚卸しとして保存することを確認しました":
    "I confirm this identical content is a separate stocktake",
  "同じ棚卸しを誤って選び直した場合は選ばないでください。通信エラーの再試行にはこの確認は不要です。":
    "Do not select this if you accidentally chose the same stocktake again. Retrying a network error does not require this confirmation.",
  "記録 {recorded} 件・現行商品一致 {matched} 件・原本IDのみ {sourceOnly} 件・原文数量が片方のみ {sourceIncomplete} 件・数量なし {skipped} 件・商品未一致 {unmatched} 件・無効 {invalid} 件":
    "Recorded: {recorded}. Matched current products: {matched}. Source ID only: {sourceOnly}. Only one source quantity: {sourceIncomplete}. No quantity: {skipped}. Unmatched products: {unmatched}. Invalid: {invalid}.",
  "記録できる明細が0件のため、棚卸しは作成していません":
    "No stocktake was created because there were zero recordable lines.",
  "未一致の商品: {keys}": "Unmatched products: {keys}",
  "無効な行: {rows}": "Invalid lines: {rows}",
  "{done} 件を取り込みましたが、対象外の行があります":
    "Imported {done}, but some lines were excluded",
  "{done} 件を取り込みました。同じ内容のCSVが {duplicate} 件ありました":
    "Imported {done}. {duplicate} file(s) had identical CSV content.",
  "確認済みの操作として別の棚卸し履歴を保存しました。全明細を保存し、現在庫は変更していません。":
    "Saved a separate stocktake history after explicit confirmation. All lines were saved and current inventory was not changed.",
  "{done} 件を取り込み、{already} 件は取り込み済みでした":
    "Imported {done}; {already} had already been imported",
  "選んだ {already} 件はすでに取り込み済みでした":
    "All {already} selected files had already been imported",
  "各ファイルの未一致・無効・数量なしの内訳を確認してください。記録済みの明細は在庫を変更していません。":
    "Review the unmatched, invalid and no-quantity lines for each file. Recorded lines did not change your stock levels.",
  "棚卸しの履歴を {recorded} 件の明細で記録しました（在庫は変わっていません）":
    "Recorded stocktake history with {recorded} lines. Your stock levels have not changed.",
  "Shopify のバリアントIDの列（Shopify ID）":
    "Shopify variant ID column (Shopify ID)",
  "商品名の列（Product・削除済み商品の記録用）":
    "Product title column (Product; retained for deleted products)",
  "バリエーション名の列（Variant・削除済み商品の記録用）":
    "Variant title column (Variant; retained for deleted products)",
  "差異（増減）の列（Adjustment）": "Difference column (Adjustment)",
  "実数＝棚卸し後の在庫の列（Current Stock）":
    "Counted quantity — stock after the count (Current Stock)",
  "システム在庫＝棚卸し前の在庫の列":
    "System quantity — stock before the count",
  "未知形式では、商品を特定する列（Shopify のバリアントID または SKU）と数量の列を2つ明示してください。列の意味は自動では推測しません。":
    "For an unknown format, explicitly choose the product identifier column (Shopify variant ID or SKU) and two quantity columns. Column meanings are never guessed.",

  // 取り込みパネル共通（CsvImportPanel）
  "{title}のCSVファイル": "{title} CSV file",
  "選択中: {name}": "Selected: {name}",
  ファイルを読み込めませんでした: "Could not read the file",
  "ファイルを読み込めませんでした。CSV ファイルを選び直してください。":
    "We could not read that file. Please choose a CSV file again.",
  "CSV を読み込めませんでした": "Could not read the CSV",
  "予期しないエラーが発生しました。時間をおいて、もう一度お試しください。":
    "Something went wrong. Please wait a moment and try again.",
  "（使わない）": "(do not import)",
  取り込む: "Import",
  上書きして取り込む: "Overwrite and import",
  "{title}のCSVを{action}": "{action} the {title} CSV",
  取り込みに失敗しました: "Import failed",

  // dry-run の確認バナー
  "既存の{noun} {count} 件を上書きします":
    "{noun} — existing records to overwrite: {count}",
  "上書きされる既存の{noun}はありません":
    "{noun} — no existing records will be overwritten",
  "{summary}。よろしければ「上書きして取り込む」を押してください（まだ何も保存していません）。":
    "{summary}. If that looks right, choose “Overwrite and import”. Nothing has been saved yet.",
  "上書きされるのは対応付けた列だけです（空欄のセル・「（使わない）」にした列の既存値は残ります）。":
    "Only the columns you mapped are overwritten. Existing values are kept for blank cells and for any column set to “(do not import)”.",
  "一致する商品が見つからない SKU: {count} 件（この行は取り込まれません）。":
    "SKUs with no matching product: {count} (those rows are not imported).",
  "読み飛ばす行: {count} 件。": "Rows to skip: {count}.",

  // 取り込み結果バナー
  "一致する商品が見つからなかった SKU（{count} 件）: {skus}。先に在庫同期を実行してから再取り込みしてください。":
    "SKUs with no matching product ({count}): {skus}. Run an inventory sync first, then import the file again.",
  "読み飛ばした行（{count} 件）: {rows}": "Rows skipped ({count}): {rows}",
  "{row}行目（{reason}）": "row {row} ({reason})",

  // ①仕入先（SUPPLIER_SPEC）
  仕入先: "Suppliers",
  "Stocky の仕入先（Suppliers）エクスポートを取り込みます。同じ仕入先名が既にあれば上書きし、無ければ新規に登録します。3つのうち、これを最初に実行してください。":
    "Import your Stocky Suppliers export. A supplier that already exists under the same name is overwritten; a new name is created. Run this one first.",
  "仕入先名の列（必須）": "Supplier name column (required)",
  "Stocky ID の列": "Stocky ID column",
  "非表示（is_hidden）の列": "Hidden status (is_hidden) column",
  会社名の列: "Company name column",
  担当者名の列: "Contact name column",
  メールアドレスの列: "Email address column",
  電話番号の列: "Phone number column",
  "仕入先名の列を選ぶと取り込めます。":
    "Choose the supplier name column to enable the import.",
  "新規に登録 {created} 件・既存を上書き {updated} 件":
    "Created: {created} · Overwritten: {updated}",
  "仕入先を新規 {created} 件・上書き {updated} 件 取り込みました":
    "Suppliers imported — created: {created}, overwritten: {updated}",

  // ②商品と仕入先の紐付け（SUPPLIER_LINK_SPEC）
  商品と仕入先の紐付け: "Product-to-supplier links",
  "SKU と仕入先名の対応を取り込み、商品に仕入先を割り当てます。手元に無い仕入先名は自動で新規作成されます（作成した件数は結果に出ます）。仕入単価・入り数・MOQ・リードタイムの列も対応付けると、仕入先ごとの条件として一緒に入ります。":
    "Import SKU-to-supplier-name pairs to assign a supplier to each product. Supplier names you do not have yet are created automatically (the result shows how many). If you also map unit cost, pack size, MOQ and lead time, they are imported as that supplier’s purchasing terms.",
  "SKU の列（必須）": "SKU column (required)",
  仕入単価の列: "Unit cost column",
  "入り数（1ケースの個数）の列": "Pack size (units per case) column",
  "最低発注数量（MOQ）の列": "Minimum order quantity (MOQ) column",
  "リードタイム（日数）の列": "Lead time (days) column",
  "SKU の列と仕入先名の列を選ぶと取り込めます。":
    "Choose the SKU column and the supplier name column to enable the import.",
  仕入条件: "Purchasing terms",
  "仕入先を紐付ける商品 {linked} 件・自動で作成する仕入先 {created} 件・書き込む仕入条件 {conditions} 件":
    "Products to link: {linked} · Suppliers to create: {created} · Purchasing terms to write: {conditions}",
  "{linked} 件の商品に仕入先を紐付けました（自動で作成した仕入先 {created} 件）":
    "Products linked to a supplier: {linked} (suppliers created automatically: {created})",

  // ③発注点・仕入先別リードタイム（REORDER_POLICY_SPEC）
  "発注点・リードタイム": "Reorder points and lead times",
  "Low Stock・発注点／リードタイム":
    "Low Stock, reorder points, and lead times",
  "Stocky の Low Stock CSV から、商品ごとの発注点（ROP）と Vendor ごとのリードタイムを取り込みます。リードタイムは仕入先別の条件として保存し、他の仕入条件は変更しません。最低発注数量（MOQ）は下の専用貼り付け欄から取り込んでください。":
    "Import product reorder points (ROP) and vendor-specific lead times from Stocky's Low Stock CSV. Lead times are saved as supplier-specific terms without changing other purchasing terms. Import minimum order quantities (MOQ) using the dedicated paste field below.",
  "Stocky で Reports → Low Stock → SKU/Variants → CSV。使う列は SKU・Vendor・ROP（発注点）・Lead Time の4つです。":
    "In Stocky: Reports → Low Stock → SKU/Variants → CSV. Use the SKU, Vendor, ROP (reorder point), and Lead Time columns.",
  "選ぶファイル: {name}": "File to choose: {name}",
  "{rows} 行を検出し、Stockyの列を自動認識しました。列を選び直さず、そのまま件数確認へ進めます。":
    "Detected {rows} rows and recognized the Stocky columns automatically. Continue to count confirmation without remapping columns.",
  "{rows} 行を検出しました。自動認識できない列だけ、詳細設定で選んでください。":
    "Detected {rows} rows. Use advanced settings only for columns that could not be recognized automatically.",
  列の詳細設定を閉じる: "Close column advanced settings",
  "列の詳細設定（通常は変更不要）":
    "Column advanced settings (normally no changes needed)",
  // MOQ の貼り付け取込（StockyCsvImport/MoqPasteImport.tsx）。
  "Stocky で Vendors → 対象の vendor → Settings → Order limits。表を見出しごとコピーして下へ貼り付けます。CSVへの加工は不要です。":
    "In Stocky, go to Vendors → your vendor → Settings → Order limits. Copy the table including its header and paste it below; no CSV conversion is needed.",
  仕入先を読み込み中: "Loading suppliers",
  仕入先を取得できませんでした: "Could not load suppliers",
  "時間をおいて、もう一度お試しください。":
    "Please wait a moment and try again.",
  "この最低発注数量は仕入先ごとに保存します。先に UreyukiBox の「仕入先」で仕入先を登録してください。":
    "Minimum order quantities are stored per supplier. Add the supplier under Suppliers in UreyukiBox first.",
  取り込み先の仕入先: "Supplier to import into",
  "Stocky で開いた vendor と同じ仕入先を選んでください。MOQ はこの仕入先と商品の組み合わせに保存されます。":
    "Choose the same supplier as the vendor you opened in Stocky. Each MOQ is saved for that supplier-product combination.",
  貼り付け欄: "Paste here",
  "見出し行が入っていても、数量が別の行になっていても、そのままで大丈夫です。":
    "It is fine if the header row is included, or if the quantity ends up on its own line.",
  取り込めませんでした: "Could not import",
  "{count} 件の最低発注数量を読み取りました":
    "Read {count} minimum order quantities",
  "仕入先「{name}」の条件として取り込みます。違っていれば仕入先を選び直すか、貼り付け直してください。":
    "These values will be imported as terms for {name}. If that is wrong, choose another supplier or paste the table again.",
  "SKU が1件も見つかりませんでした。対象のベンダーの表を、見出しごと選択してコピーしてください。":
    "No SKUs were found. Select the whole table for that vendor, including the header row, and copy it.",
  "表示は先頭 {limit} 件までです": "Showing the first {limit} entries",
  "全 {count} 件を解析済みです。取り込み時は表示されていない行も含めて全件を処理します。":
    "All {count} entries have been parsed. The import processes every entry, including those not shown here.",
  "ほか {count} 件": "and {count} more",
  数量が読み取れなかった商品があります:
    "Some products had no readable quantity",
  "数量が見つからず取り込まない SKU は {count} 件です。表示例: {skus}":
    "{count} SKUs have no quantity and will be skipped. Examples: {skus}",
  範囲外の数量があります: "Some quantities are out of range",
  "最低発注数量は1以上で入力します。範囲外の {count} 件は取り込みません。":
    "Minimum order quantities must be at least 1. The {count} out-of-range entries will be skipped.",
  "同じ SKU の商品が複数あります": "More than one product has the same SKU",
  "商品を一意に特定できないため、次の SKU は取り込みません: {skus}":
    "These SKUs will be skipped because they do not identify a unique product: {skus}",
  "同じ SKU に異なる数量があります": "The same SKU has conflicting quantities",
  "どちらを使うか決められないため、次の SKU は取り込みません。Stocky の表を確認してください: {skus}":
    "These SKUs will be skipped because the import cannot safely choose a quantity. Check the table in Stocky: {skus}",
  "このストアに無い SKU があります": "Some SKUs are not in this store",
  "商品が見つからない SKU は {count} 件です（同期がまだか、SKU が違う可能性があります）。表示例: {skus}":
    "No product matched {count} SKUs; the store may not be synced yet, or the SKUs may differ. Examples: {skus}",
  "対応する SKU が見つからない数量は {count} 件です。表示例: {values}":
    "{count} quantities have no matching SKU. Examples: {values}",
  "仕入先「{name}」の最低発注数量を新規 {created} 件・更新 {updated} 件 取り込みました":
    "Imported minimum order quantities for {name}: {created} new and {updated} updated",
  "商品が見つからなかった SKU: {skus}": "SKUs with no matching product: {skus}",
  読み取り内容を確認: "Check what was read",
  上書き件数を確認: "Check overwrite counts",
  "既存の最低発注数量 {count} 件を上書きします":
    "This will overwrite {count} existing minimum order quantities",
  "最低発注数量を新規 {count} 件 取り込みます":
    "This will import {count} new minimum order quantities",
  "新規 {created} 件・既存を上書き {updated} 件です。":
    "New: {created}. Existing values to overwrite: {updated}.",
  "取り込まないデータ: 不正 {invalid} 件・未一致 SKU {unmatched} 件・数量なし {skipped} 件・SKU に結び付かない数量 {orphan} 件":
    "Not imported: invalid {invalid}; unmatched SKUs {unmatched}; missing quantities {skipped}; quantities without an SKU {orphan}.",
  "まだ保存していません。件数と仕入先を確認してから確定してください。":
    "Nothing has been saved yet. Check the counts and supplier, then confirm.",
  "100万物理行を超えるため、運用データへは反映せず保全します":
    "This exceeds 1,000,000 physical rows, so it will be retained without changing operational data",
  "物理行数が安全上限1,000,000行を超えています。最低発注数量には1件も書き込まず、送信された正確な原本と解析可能な全行を汎用JSONLとして保持します（{rows}行すべて運用変換は未処理）。":
    "The paste exceeds the 1,000,000-physical-row safety limit. No minimum order quantities will be written. The exact submitted source and every parseable row will be retained as generic JSONL ({rows} rows remain unprocessed for operational conversion).",
  "保存後は、この画面の「保存済みデータの取り出し」から原本をダウンロードし、汎用JSONLを分割取得できます。":
    "After saving, use Saved data exports on this page to download the source and retrieve the generic JSONL in chunks.",
  原本と解析可能な全行を保全できます:
    "The source and every parseable row can be retained",
  "原本と汎用JSONLを保存しました（最低発注数量は未反映）":
    "Source and generic JSONL saved (minimum order quantities were not changed)",
  "{rows}行は最低発注数量へ1件も書き込んでいません。保存済みデータの取り出しから原本をダウンロードし、解析可能な全行の汎用JSONLを分割取得できます。":
    "No minimum order quantities were written for the {rows} rows. Use Saved data exports to download the source and retrieve generic JSONL for every parseable row in chunks.",
  "まだ保存していません。運用データへ書かない保全内容を確認してください":
    "Nothing has been saved yet. Review retaining the data without writing to operational models.",
  "最低発注数量には1件も書き込まず、送信された正確な原本と解析可能な全行を汎用JSONLとして保持します（{rows}行すべて運用変換は未処理）。":
    "No minimum order quantities will be written. The exact submitted source and every parseable row will be retained as generic JSONL ({rows} rows remain unprocessed for operational conversion).",
  "最低発注数量へ書き込まず、正確な原本と解析可能な全行を汎用JSONLとして保全することを確認する":
    "I confirm that no minimum order quantities will be written and that the exact source and every parseable row will be retained as generic JSONL",
  原本と汎用JSONLを保存する: "Save source and generic JSONL",
  この内容を確認して取り込む: "Confirm and import",
  "一部のみ取り込みました。対象外の内訳を確認してください。":
    "Only part of the table was imported. Review the excluded items below.",
  "詳細一覧は表示上限までです。上の件数が全体件数です。":
    "The detail list is limited for display. The counts above are the full totals.",
  "表示は先頭 {shown} 件までです。該当する SKU は全 {count} 件です。":
    "Only the first {shown} entries are shown. {count} SKUs are affected in total.",

  // 棚卸しの実数 CSV 一括投入（stocktakeCountCsvSpec.ts）。
  // ⚠️ spec の固定文字列は CsvImportPanel が描画時に t() する＝カバレッジ検査
  //    （t() の引数を見る）では拾えない。ここの取りこぼしは実際に描画して探す。
  実数をCSVでまとめて入力: "Enter counts in bulk from a CSV",
  "数えた実数の CSV を取り込みます。ハンディ端末の書き出しや Excel の集計表をそのまま使えます。CSV に無い商品は「未カウント」のまま残るので、何回かに分けて取り込めます。1,000商品を超える場合、CSV が突合するのは現在の棚卸し範囲だけです。この範囲を反映後、次の範囲の棚卸しを開始してください。":
    "Import the counts you recorded. Exports from a barcode scanner or an Excel tally sheet work as they are. Products missing from the CSV stay uncounted, so you can import in several passes. For catalogues over 1,000 products, the CSV matches only the current stocktake range. Apply this range, then start the next one.",
  "この取り込みでは在庫は変わりません。実数が入るだけで、Shopify への反映は「在庫へ反映する」を押したときだけです。":
    "This import does not change your stock. It only fills in the counts — nothing reaches Shopify until you press “Apply to inventory”.",
  "実数の列（必須）": "Counted quantity column (required)",
  "SKU の列": "SKU column",
  "Shopify のバリアントIDの列": "Shopify variant ID column",
  "実数の列と、商品を特定する列（SKU または Shopify のバリアントID）を選ぶと取り込めます。":
    "Choose the counted quantity column plus a column that identifies each product — SKU or Shopify variant ID — to enable the import.",
  // 「実数」は棚卸し表の見出しで既出（下方に定義あり）＝ここには置かない。
  // 上書き確認の「既存の実数 N 件を上書きします」も同じ訳語で通る。
  "実数を入れる商品 {count} 件": "{count} products will get a count",
  "{count} 件の実数を取り込みました（在庫はまだ変わっていません）":
    "Imported {count} counts (your stock has not changed yet)",
  CSVの形式: "CSV format",
  "必須: 実数の列と、商品を特定する Shopify バリアントID または SKU のどちらか1列。両方を対応付けた場合は Shopify バリアントID を使います。":
    "Required: a counted-quantity column and either a Shopify variant ID or SKU column to identify each product. If you map both, Shopify variant ID is used.",
  "自動認識する商品列: Shopify ID / shopify_id / Variant ID / Shopify Variant ID、SKU / 品番 / 商品コード。実数列: Counted / Count / 実数 / 実棚 / カウント / 棚卸数 / Actual。別の見出しも、読み込み後に列を選べます。":
    "Automatically recognized product headers: Shopify ID / shopify_id / Variant ID / Shopify Variant ID, SKU, and common item-code aliases. Count headers: Counted / Count / Actual and common physical-count aliases. For other headers, select the matching columns after loading the file.",
  "例: Shopify ID,SKU,Counted / 8001,HONEY-500,24":
    "Example: Shopify ID,SKU,Counted / 8001,HONEY-500,24",
  "文字コードは UTF-8（BOM可）、Shift_JIS / CP932、Windows-1252、BOM付きUTF-16に対応します。上限は5MBです。":
    "Supported encodings are UTF-8 (with or without BOM), Shift_JIS / CP932, Windows-1252, and UTF-16 with a BOM. The file-size limit is 5 MB.",
  "空の実数は未カウントのままです。小数は切り上げます。負数・読めない値・この範囲にない商品は取り込まず、同じIDまたはSKUが複数行にある商品も合算や後勝ちにせず除外します。確認画面で件数とエラーを確認してから実数だけを保存し、この時点では在庫を変更しません。":
    "A blank count stays uncounted, and decimals are rounded up. Negative or unreadable values and products outside this range are not imported. If the same ID or SKU appears on multiple rows, that product is excluded instead of being summed or overwritten. Review counts and errors before saving counts only; inventory is not changed at this stage.",
  "入力用CSVには現在の棚卸し範囲の Shopify ID、SKU、保存済みの実数だけを入れます。画面でまだ保存していない入力は含みません。":
    "The input CSV contains only Shopify IDs, SKUs, and saved counts for the current stocktake range. Unsaved entries currently on this screen are not included.",
  現在の範囲を入力用CSVでダウンロード:
    "Download an input CSV for the current range",
  商品数が多いため範囲を分けています:
    "Products are split into manageable ranges",
  "処理を安定させるため、現在の商品全{total}件を{batchSize}件ごとの範囲に分けています。新しい棚卸しを開始するときに範囲を選んでください。":
    "For stable processing, the current catalogue of {total} products is split into ranges of {batchSize}. Choose a range when you start a new stocktake.",
  "{title} / SKU: {sku} / Shopify バリアントID: {id}":
    "{title} / SKU: {sku} / Shopify variant ID: {id}",
  棚卸し対象の商品候補: "Stocktake product suggestions",
  "商品名 / SKU / Shopify バリアントID":
    "Product name / SKU / Shopify variant ID",

  // 書き出し経路は Stocky の実 UI の文言＝**訳さずそのまま**（Stocky は英語UIしか
  // 無いので、英語のメニュー名を日本語に訳すと画面上で探せなくなる）。
  書き出し方: "How to export",
  "仕入先（Vendor）の列": "Supplier (Vendor) column",
  発注点の列: "Reorder point column",
  "SKU の列と、発注点またはリードタイムの列を選んでください。リードタイムを選ぶ場合は仕入先（Vendor）の列も必要です。":
    "Choose the SKU column and either the reorder point or lead time column. A supplier (Vendor) column is also required when importing lead times.",
  設定: "Settings",
  "発注点: 新規 {created} 件・上書き {updated} 件 / 仕入先別リードタイム: 新規 {leadCreated} 件・上書き {leadUpdated} 件（一致した商品 {matched} 件）":
    "Reorder points — created: {created}, overwritten: {updated} / supplier lead times — created: {leadCreated}, overwritten: {leadUpdated} (matching products: {matched})",
  "変更なし: 発注点 {unchanged} 件・リードタイム {leadUnchanged} 件 / 既定仕入先の整合修復 {preferredRepaired} 件":
    "Unchanged — reorder points: {unchanged}, lead times: {leadUnchanged} / Preferred-supplier consistency repairs: {preferredRepaired}",
  "発注点を新規 {created} 件・更新 {updated} 件、仕入先別リードタイムを新規 {leadCreated} 件・更新 {leadUpdated} 件取り込みました":
    "Imported reorder points — created: {created}, updated: {updated}; supplier lead times — created: {leadCreated}, updated: {leadUpdated}",
  "同じ SKU の商品が複数あるため取り込まない行: {count} 件。":
    "Rows skipped because the SKU matches multiple products: {count}.",
  "同じ SKU の商品が複数あり特定できなかった行（{count} 件）: {values}":
    "Rows not resolved because the SKU matches multiple products ({count}): {values}",
  "一致する仕入先が見つからない Vendor: {count} 件（この行は取り込まれません）。":
    "Vendors with no matching supplier: {count}; those rows are not imported.",
  "一致する仕入先が見つからなかった Vendor（{count} 件）: {values}":
    "Vendors with no matching supplier ({count}): {values}",
  "同じ名前の仕入先が複数ある Vendor: {count} 件（この行は取り込まれません）。":
    "Vendors matching multiple suppliers: {count}; those rows are not imported.",
  "同じ名前の仕入先が複数あり特定できなかった Vendor（{count} 件）: {values}":
    "Vendors not resolved because multiple suppliers share the name ({count}): {values}",
  // 実測リードタイムの提示 → 採用（LeadTimeSuggestionCard）
  入荷実績からリードタイムを提案: "Suggest lead times from receiving history",
  "これまでの発注から入荷までの実測平均です。採用するとその仕入先の商品にまとめて設定され、発注点の自動計算が効きます。":
    "Measured averages from your own purchase orders to first receipt. Adopting one fills it in for every product from that supplier, so reorder points can be calculated automatically.",
  "実測平均 {days}日（発注{count}件）／未設定の商品 {fillable}件":
    "{days} days on average ({count} orders) · {fillable} products still unset",
  採用する: "Adopt",
  名称未設定の仕入先: "Unnamed supplier",
  実測リードタイムを採用しました: "Measured lead time adopted",
  採用に失敗しました: "Could not adopt the measured lead time",
  "{n}件にリードタイムを設定しました。": "Lead time set on {n} products.",
  "既に入力済みの{n}件はそのままにしました。":
    "{n} products already had a lead time and were left unchanged.",
  "うち{n}件は在庫日数の目標が未設定（または発注点を手入力済み）のため、発注点はまだ変わりません。":
    "{n} of them still have no days-of-supply target (or use a manually entered reorder point), so their reorder point has not changed yet.",
  // 発注間隔の提示 → 採用（ReplenishSuggestionCard）
  発注の間隔から補充日数を提案: "Suggest cover days from your ordering rhythm",
  "実際にその仕入先へ発注している間隔です。採用すると1回の発注でその日数ぶんまで補充するようになります（発注のタイミングは変わらず、数量だけ増えます）。":
    "How often you actually order from this supplier. Adopting it tops each order up to that many days of stock — the timing of your orders does not change, only the quantity.",
  "いつもの間隔 {days}日（発注{count}回ぶん）／未設定の商品 {fillable}件":
    "Usually every {days} days ({count} orders) · {fillable} products still unset",
  発注間隔から補充日数を設定しました:
    "Cover days set from your ordering rhythm",
  "{n}件に補充日数を設定しました。": "Cover days set on {n} products.",

  // ── 発注書・注文書 ──────────────────────────────────────────
  注文書の取得に失敗しました: "Could not load purchase orders",
  "注文書は仕入先ごとにまとめます。仕入先を登録し、商品を割り当てると注文書ドラフトが作られます。":
    "Purchase orders are grouped by supplier. Add a supplier and assign products to it, and a draft purchase order appears here.",
  注文書を作成する: "Create a purchase order",
  注文済みとしてマーク: "Mark as ordered",
  注文済みへの登録に失敗しました: "Could not mark the order as placed",
  発注書の取得に失敗しました: "Could not load the purchase order",
  発注書の送信先メールアドレス: "Email address to send the purchase order to",
  発注済みの発注書はまだありません: "No orders have been placed yet",
  発注が必要な商品はありません: "Nothing needs reordering right now",
  発注数量を手入力してください: "Enter the order quantity by hand",
  発注数: "Order qty",
  発注番号: "Order number",
  発注元: "Ordered by",
  "発注元 登録番号": "Your registration number",
  発注元情報の取得に失敗しました: "Could not load your company profile",
  "発注書.html": "purchase-order.html",
  発注点が未設定の商品が含まれています:
    "Some products have no reorder point set",
  発注点まで: "To reorder point",
  "発注点（リードタイム）設定": "Reorder points (lead time)",
  "販売速度・在庫・入荷予定から発注点を割った商品をサプライヤー別に集計します":
    "Groups products that have fallen below their reorder point — based on sales velocity, stock on hand and incoming stock — by supplier.",
  希望納品日: "Requested delivery date",
  参照番号: "Reference number",
  発行日: "Issue date",
  配送先: "Ship to",
  既定の配送先: "Default ship-to",
  サプライヤーへのメモ: "Note to supplier",
  サプライヤー: "Supplier",
  "サプライヤー 登録番号": "Supplier registration number",
  サプライヤー変更: "Change supplier",
  "サプライヤーが未登録（免税事業者等）— 仕入税額控除は経過措置の対象です":
    "Supplier is not registered (tax-exempt business) — input tax credit falls under transitional rules",
  "以後この数量は、入荷を登録するまで発注提案から差し引かれます。":
    "From now on this quantity is deducted from future suggestions until you record its receipt.",
  "取り消すには「発注済み・入荷」ページでキャンセルしてください。":
    "To undo this, cancel it on the Ordered & receiving page.",
  "Shopify 純正の注文書で入荷予定（転送）を作った分をここでも登録すると、同じ発注が二重に差し引かれて次回の提案数量が少なめに出ます。どちらか一方で管理してください":
    "If you also created an incoming transfer in Shopify’s native purchase orders, recording it here too double-counts the order and makes the next suggestion too low. Track it in one place only.",
  Shopify注文書用CSV: "Shopify purchase order CSV",
  "Shopify 純正の「注文書」画面で入荷・受領まで管理する場合の取込形式（SKU・数量・原価・税率／商品一括登録・在庫のインポート欄では使えません）":
    "Import format for tracking receipts in Shopify’s native Purchase orders screen (SKU, quantity, cost, tax rate). It does not work in the product or inventory import fields.",
  CSVダウンロード: "Download CSV",
  CSVファイル: "CSV file",
  "PDF（印刷・保存）": "PDF (print or save)",
  "仕入先へ送る発注書の正本（明細・税区分・登録番号入り）":
    "The purchase order of record you send to the supplier — line items, tax categories and registration number included",

  // ── 入荷・在庫の書き込み ────────────────────────────────────
  入荷を記録する: "Record a receipt",
  入荷予定: "Incoming",
  "入荷登録した数量を Shopify の在庫へ加算します。Shopify 側でも同じ入荷を処理する場合はオフにしてください（二重計上防止）。":
    "Adds the received quantity to your Shopify inventory. Turn this off if you also process the same receipt in Shopify, to avoid double-counting.",
  "「注文済みとしてマーク」で登録した発注書の入荷を記録します。入荷待ちの数量は発注提案から自動で差し引かれます":
    "Record receipts for orders you marked as placed. Quantities awaiting delivery are deducted from reorder suggestions automatically.",
  "実際に商品が入った拠点を選んでください。ここで選んだ拠点の在庫に加算されます。":
    "Choose the location the goods actually arrived at. Stock is added to the location you pick here.",
  反映先の拠点: "Location to update",
  複数拠点のため在庫への反映は使えません:
    "Writing back to inventory is unavailable because you have multiple locations",
  この設定を記憶できませんでした: "Could not remember this setting",
  操作に失敗しました: "That action failed",
  "入荷した商品に貼るJANラベル。発注数量ぶんの枚数を出します（A4・3列）":
    "Barcode labels for received goods — one per unit ordered (A4, 3 columns)",
  "バーコードラベル（印刷）": "Barcode labels (print)",
  印刷できるバーコードラベルがありませんでした:
    "There were no barcode labels to print",
  "絞り込み中の {n} 件の棚札を1枚ずつ出します（A4・3列）。バーコード未設定の商品は出ません":
    "Prints one shelf label for each of the {n} products currently filtered (A4, 3 columns). Products without a barcode are skipped.",

  // ── 在庫調整・棚卸し・拠点間移動 ────────────────────────────
  在庫調整に失敗しました: "The stock adjustment failed",
  在庫調整の情報の取得に失敗しました: "Could not load stock adjustment data",
  "在庫調整を記録しました。Shopify の在庫にも反映済みです。":
    "Stock adjustment recorded and applied to your Shopify inventory.",
  "Shopify の在庫に即時反映されます。よろしいですか？":
    "This is applied to your Shopify inventory immediately. Continue?",
  数量の増減: "Quantity change",
  "減らすときはマイナス（例: -3）。0 は指定できません。":
    "Use a negative number to decrease (for example −3). Zero is not allowed.",
  棚卸しの操作に失敗しました: "The stocktake action failed",
  "棚卸しを中止しました。在庫は変更していません。":
    "Stocktake cancelled. No inventory was changed.",
  "棚卸しを適用しました。Shopify の在庫にも反映済みです。":
    "Stocktake applied and written back to your Shopify inventory.",
  "棚卸しを開始しました（商品 {from}〜{to} / {total} 件）。この範囲を反映後、次の範囲を選んでください。":
    "Stocktake started (products {from}–{to} of {total}). Apply this range, then select the next one.",
  "棚卸しを開始しました。システム在庫を記録しています。":
    "Stocktake started. Your system quantities have been recorded.",
  棚卸しを開始できませんでした: "Could not start the stocktake",
  棚卸しする商品範囲: "Products to stocktake",
  "商品 {from}〜{to} / {total} 件": "Products {from}–{to} of {total}",
  "1回に最大1,000件です。1つの範囲を反映したら、次の範囲を開始してください。":
    "Each stocktake covers up to 1,000 products. Apply one range, then start the next.",
  "この棚卸しを中止します。入力した実数は破棄され、在庫は変更されません。よろしいですか？":
    "This cancels the stocktake. The counted quantities are discarded and no inventory changes. Continue?",
  システム在庫: "System qty",
  メモなし: "No note",
  "このメモはUreyukiBoxの棚卸しメモです。Stockyでの棚卸し名やCSV原本の内容とは別に保存されています。":
    "This is the UreyukiBox stocktake note. It is stored separately from the Stocky stocktake name and the contents of the source CSV.",
  "最大2,000文字。開始後も履歴と詳細で確認できます。":
    "Up to 2,000 characters. You can review the note during the stocktake and in its history details.",
  "この棚卸しのメモを編集できます。保存後は履歴にも表示されます。":
    "Edit the note for this stocktake. After saving, it also appears in history.",
  メモを保存: "Save note",
  "メモを保存しました。": "Note saved.",
  実数: "Counted qty",
  差異: "Variance",
  カウント: "Count",
  拠点間の在庫移動: "Move stock between locations",
  拠点間移動の情報の取得に失敗しました: "Could not load transfer data",
  "移動の下書きを作成しました。まだ在庫は動いていません — 実際に品物を出したら「出庫する」を押してください。":
    "Draft transfer created. No stock has moved yet — press Ship when the goods actually leave.",
  移動の操作に失敗しました: "The transfer action failed",
  "移動を取り消しました。": "Transfer cancelled.",
  "この移動を取り消します。まだ出庫していないため在庫は動きません。よろしいですか？":
    "This cancels the transfer. Nothing has shipped yet, so no stock moves. Continue?",
  "出庫しました。移動元の在庫から差し引いています。":
    "Shipped. The quantity has been deducted from the source location.",
  "入庫を記録しました。移動先の在庫へ全量を反映しました。":
    "Receipt recorded. The full quantity was added to the destination location.",
  "入庫を記録しました。残りはあとから受領できます。":
    "Receipt recorded. You can receive the remainder later.",
  移動する数量: "Quantity to move",
  移動元: "From",
  移動先: "To",
  移動元の拠点: "Source location",
  移動先の拠点: "Destination location",
  "移動元と移動先には別の拠点を指定してください。":
    "Choose two different locations for source and destination.",
  出庫日時: "Shipped at",

  // ── バーコード・スキャン ────────────────────────────────────
  バーコード: "Barcode",
  バーコードをスキャン: "Scan a barcode",
  "バーコードリーダーで読み取るか、バーコード／SKU を入力して Enter を押してください。":
    "Scan with a barcode reader, or type a barcode or SKU and press Enter.",
  "読み取るたびに、その商品の実数が 1 ずつ増えます。読み終えたら「実数を保存」を押してください。":
    "Each scan increases that product’s counted quantity by one. Press Save counts when you are done.",
  "読み取るたびに、その明細の入荷数が 1 ずつ増えます（残数が上限）。":
    "Each scan increases that line’s received quantity by one, up to the amount outstanding.",
  "読み取ると下の「商品」が切り替わります。数量は手で入力してください（スキャンでは増減しません）。":
    "Scanning switches the selected product below. Enter the quantity by hand — scanning does not change it.",
  "この商品には SKU が設定されていないため、スキャンで照合できません。数量欄に直接入力してください。":
    "This product has no SKU, so it cannot be matched by scanning. Enter the quantity directly.",

  // ── 仕入先 ──────────────────────────────────────────────────
  仕入先が未登録です: "No suppliers yet",
  仕入先を追加: "Add supplier",
  // ── 仕入先ページ（/suppliers・2026-08-03 新設） ──
  "＋ 新規仕入先を登録…": "+ Register a new supplier…",
  "保存できませんでした。時間をおいて再度お試しください。":
    "Could not save. Please try again in a moment.",
  "{label}を編集": "Edit {label}",
  "割当商品 {n} 件": "{n} assigned products",
  "{n}社": "{n} suppliers",
  仕入先の取得に失敗しました: "Could not load suppliers",
  "仕入先は3つの経路で登録できます。①同期＝Shopify商品のベンダー名から自動作成 ②保存済みStockyデータの取込 ③このページから手動追加。":
    "Suppliers can be registered three ways: automatically from Shopify product vendors during sync, by importing saved Stocky data, or manually from this page.",
  "ベンダー（Shopify 商品の属性）とは別物です。同期時にベンダー名から同名の仕入先を自動作成しますが、その後は独立して管理できます（商品ごとの割当は注文書・発注点設定から変更）。":
    "Suppliers are separate from vendors (a Shopify product attribute). Sync auto-creates a supplier with the vendor's name, but they are managed independently afterwards (change per-product assignments from Purchase orders or Reorder points).",
  "仕入先を追加できませんでした。時間をおいて再度お試しください。":
    "Could not add the supplier. Please wait a moment and try again.",
  仕入先を割り当てる: "Assign a supplier",
  仕入先を選択: "Select a supplier",
  仕入先の割り当てに失敗しました: "Could not assign the supplier",
  仕入先の連絡先メール: "Supplier contact email",
  仕入先名: "Supplier name",
  仕入先数: "Suppliers",
  "仕入先 未割当": "No supplier assigned",
  "仕入先 登録番号": "Supplier registration number",
  この仕入先: "This supplier",
  "この仕入先に一括適用（日）":
    "Apply to all products from this supplier (days)",
  この仕入先に今必要な発注はありません:
    "Nothing needs ordering from this supplier right now",
  一括で割り当てる仕入先: "Supplier to assign in bulk",
  "商品に仕入先を割り当てると、注文書ドラフトに表示されます。発注しない商品は割り当てなくて構いません。":
    "Assign a supplier to a product and it appears in the draft purchase order. Products you never reorder can be left unassigned.",
  "仕入先ごとにリードタイム（発注〜入荷までの日数）と安全在庫日数を入力すると、販売速度から発注点を自動計算します":
    "Enter a lead time (days from order to delivery) and safety-stock days per supplier, and reorder points are calculated from your sales velocity.",
  連絡先メール未登録: "No contact email",
  "任意。登録すると注文書画面から自動提案の発注書を送信できます。":
    "Optional. With an address saved you can email suggested purchase orders straight from the order screen.",

  // ── 発注点・リードタイム ────────────────────────────────────
  リードタイム日数: "Lead time (days)",
  "リードタイム（日）": "Lead time (days)",
  "発注点（手動）": "Reorder point (manual)",
  自動計算: "Auto",
  "自動計算 {point}": "Auto: {point}",
  "この値で固定中（自動計算・季節係数は効きません）。空欄なら自動計算で {point} になります。":
    "Fixed at this value (auto-calculation and seasonal factors are off). Clear the field and it becomes {point}.",
  "空欄＝販売速度から自動計算。いまは {point}（日販 {demand} × {days}日分 ＋ 統計的安全在庫 {safety}）。":
    "Blank = calculated from sales velocity. Currently {point} ({demand}/day × {days} days + {safety} statistical safety stock).",
  "空欄＝販売速度から自動計算。いまは {point}（日販 {demand} × {days}日分）。":
    "Blank = calculated from sales velocity. Currently {point} ({demand}/day × {days} days).",
  "空欄＝販売速度から自動計算。リードタイムと安全在庫日数を入れると数値が決まります。":
    "Blank = calculated from sales velocity. Enter a lead time and safety-stock days to fix the number.",
  発注点を上書きする: "Override the reorder point",
  "発注〜入荷の実績から自動で入れました":
    "Filled in automatically from your order-to-delivery history",
  "{point} で固定中（自動計算・季節係数は効きません）":
    "Fixed at {point} (auto-calculation and seasonal factors are off)",
  "自動計算で {point}（日販 {demand} × {days}日分 ＋ 統計的安全在庫 {safety}）":
    "Auto: {point} ({demand}/day × {days} days + {safety} statistical safety stock)",
  "自動計算で {point}（日販 {demand} × {days}日分）":
    "Auto: {point} ({demand}/day × {days} days)",
  リードタイムと安全在庫日数を入れると自動計算が始まります:
    "Enter a lead time and safety-stock days to start the auto-calculation",
  "安全在庫（日）": "Safety stock (days)",
  補充日数: "Days of cover",
  "1回の発注で何日分まで補充するか":
    "How many days of demand one order should cover",
  最低発注数量: "Minimum order quantity",
  "最低発注数量（MOQ）": "Minimum order quantity (MOQ)",
  この倍数へ切上: "Round up to a multiple of",
  入数: "Units per case",
  "1以上で入力してください。": "Enter 1 or more.",
  "1以上の整数で入力": "Enter a whole number of 1 or more",
  StockyのCSVから取り込む: "Import from a Stocky CSV",
  "StockyのLow Stock CSVを取り込む": "Import a Stocky Low Stock CSV",
  上書きされる既存の設定はありません:
    "No existing settings will be overwritten",
  実際の日付で計算: "Calculate from actual dates",

  // ── 季節性 ──────────────────────────────────────────────────
  // 🔴 イベント名（お中元・クリスマス商戦 …）は **backend が店の国の暦から配り、
  // backend 側の EN 辞書で訳す**＝ここには持たない（国を増やすたびに片方が古くなる）。
  季節補正プレビュー: "Seasonality preview",
  季節補正は現在適用されていません:
    "No seasonal adjustment is active right now",
  "通常期（1.00倍）": "Normal season (×1.00)",
  "{name}（{multiplier}倍）": "{name} (×{multiplier})",
  "このストアの所在国の暦（{names}）の期間には、販売速度を補正して発注点と推奨数量を計算します。通常期は1.00倍です。":
    "During the peaks on this store's local calendar ({names}), sales velocity is adjusted before reorder points and suggested quantities are calculated. Outside those periods the multiplier is 1.00.",
  "このストアの所在国には固定カレンダーの季節補正がありません。販売速度をそのまま使って発注点と推奨数量を計算します。":
    "We do not have a fixed seasonal calendar for this store's country, so reorder points and suggested quantities use the raw sales velocity.",
  次の引き下げ: "Next step down",
  見通し期間: "Forecast window",

  // ── 在庫分析 ────────────────────────────────────────────────
  在庫分析の取得に失敗しました: "Could not load inventory analytics",
  "在庫評価額と売れ筋（ABC分析）で在庫を金額と貢献度の2軸で見える化します":
    "See your inventory on two axes — value on hand and contribution to sales (ABC analysis).",
  ABCランクは参考値です: "ABC ranks are indicative",
  "ABC分析だけは常に全拠点合算です（売上データが拠点を持たないため）。在庫評価額と不動在庫は、選んだ拠点の在庫で計算します。":
    "Only ABC analysis always covers all locations combined (sales data is not tied to a location). Inventory valuation and dead stock are calculated from the selected location's stock.",
  "取扱商品が少ないため、ABCランクは参考値です。ABC分析は売上の偏り（パレートの法則）を見る手法で、商品数が少ないと『一番の売れ筋でもCランク』のように偏った結果になります。商品が増えるほど精度が上がります。":
    "With only a few products, ABC ranks are indicative. ABC analysis measures how concentrated your sales are (the Pareto principle), so with a small catalogue it can produce odd results — your best seller may still land in rank C. Accuracy improves as your catalogue grows.",
  ランク: "Rank",
  評価額: "Valuation",
  累積構成比: "Cumulative share",
  滞留在庫はありません: "No slow-moving stock",
  滞留資金: "Capital tied up",
  最終販売: "Last sold",
  在庫のある商品がありません: "No products currently in stock",
  対象期間の売上がありません: "No sales in this period",
  "最初の同期が完了すると、在庫評価額・ABC分析・滞留在庫がここに表示されます。":
    "Once the first sync completes, inventory valuation, ABC analysis and dead stock appear here.",
  売れ筋データの取得に失敗しました: "Could not load best-seller data",
  売上: "Sales",
  販売数: "Units sold",
  仕入単価: "Unit cost",
  仕入単価は目安です: "Unit costs are approximate",
  "空欄=商品の原価": "Leave blank to use the product cost",

  // ── 資金繰り ────────────────────────────────────────────────
  資金繰りカレンダーの取得に失敗しました:
    "Could not load the cash-flow calendar",
  "発注が必要な仕入を入荷予定日で投影し、今後いくら現金が要るかを週次で見える化します":
    "Projects the purchases you need to make against their expected delivery dates, so you can see how much cash you need week by week.",
  今後の発注に必要な現金はありません: "No cash is required for upcoming orders",
  必要現金: "Cash required",
  支払い週: "Payment week",
  品目数: "Items",
  明細数: "Line items",

  // ── キット（BOM）─────────────────────────────────────────────
  キット: "Kit",
  "キット（組立品）": "Kit (assembly)",
  キット一覧の取得に失敗しました: "Could not load kits",
  キット1個あたりの数量: "Quantity per kit",
  "原材料と数量を登録してShopify商品へ反映すると、セット商品の販売可能数が原材料の在庫から自動計算されます":
    "Register the components and quantities with the Shopify product, and Shopify automatically calculates how many bundles can be sold from component inventory.",
  保存してShopify商品に反映: "Save and update Shopify product",
  セット構成を保存して在庫へ反映する:
    "Save bundle and update calculated availability",
  "「{name}」へ次のセット構成を保存し、Shopifyの在庫計算へ反映します。":
    'Save the following bundle configuration to "{name}" and apply it to Shopify inventory calculations.',
  "・{name} × {quantity}": "• {name} × {quantity}",
  "在庫数を直接増減・上書きする操作ではありません。販売時にShopifyが原材料在庫を減らします。よろしいですか？":
    "This does not directly increase, decrease, or overwrite inventory quantities. Shopify will deduct component inventory when the bundle is sold. Continue?",
  Shopify商品へ反映しました: "Shopify product updated",
  "Shopifyが原材料の在庫からセット商品の販売可能数を自動計算します。原材料が売れたときもセット商品の在庫へ追随します。":
    "Shopify now calculates bundle availability from component inventory and keeps it in sync when a component sells.",
  "保存時にShopifyの商品をセット商品として登録します。商品在庫の数量を直接上書きする操作ではありません。初回だけShopifyの商品編集権限の許可が必要です。":
    "Saving registers this product as a Shopify bundle. It does not directly overwrite inventory quantities. The first save asks for permission to edit products.",
  "この操作は在庫数を直接増減・上書きしません。Shopifyが原材料在庫からセット商品の販売可能数を計算できるよう、セット構成だけを反映します。":
    "This does not directly increase, decrease, or overwrite inventory quantities. It only applies the bundle configuration so Shopify can calculate sellable bundle availability from component inventory.",
  "Shopify商品へ反映する権限が許可されませんでした。権限を許可してから、もう一度保存してください。":
    "Permission to update the Shopify product was not granted. Grant it, then save again.",
  "Shopifyの権限確認を開けませんでした。Shopify管理画面からUreyukiBoxを開き直してください。":
    "Could not open Shopify's permission prompt. Reopen UreyukiBox from Shopify admin.",
  "{name}（Shopifyに存在しません）": "{name} (not found in Shopify)",
  このキット商品は現在のShopifyに存在しません:
    "This kit product is not in the current Shopify catalog",
  "Shopifyで商品を作成して同期した後、現在の商品をキットとして選び直してください。":
    "Create the product in Shopify and sync it, then select the current product as the kit.",
  Shopifyに存在しない原材料があります:
    "Some components are not in the current Shopify catalog",
  "該当行を現在の商品へ選び直すか削除してください。":
    "Choose a current product for each affected row, or remove the row.",
  Shopifyに存在しないキット: "Kit not found in Shopify",
  原材料の選び直しが必要: "Components need to be reselected",
  Shopifyに存在しない旧商品を含むセットがあります:
    "Some bundles contain old products that are no longer in Shopify",
  "旧商品IDをShopifyへ送信しません。Shopifyで商品を作成して同期した後、現在の商品でセット構成を登録し直してください。":
    "Old product IDs are never sent to Shopify. Create and sync the products in Shopify, then register the bundle again with current products.",
  Shopifyで商品を作成: "Create product in Shopify",
  置換中の商品へ戻る: "Return to the replacement product",
  UreyukiBoxから削除: "Remove from UreyukiBox",
  "「{name}」のUreyukiBox内の旧セット設定（原材料 {n} 件）だけを削除します。Shopifyの商品と作成済みの下書き商品は削除されず、そのまま残ります。この操作は取り消せません。よろしいですか？":
    "Remove only the old UreyukiBox bundle setting for “{name}” ({n} components). The Shopify product and any draft product already created will be kept. This cannot be undone. Continue?",
  "Shopifyへの反映処理を開始済みです。同じ置換処理を完了してください。":
    "The Shopify update has already started. Complete the same replacement workflow.",
  旧セット設定をUreyukiBoxから削除しました:
    "Old bundle setting removed from UreyukiBox",
  "Shopifyの商品と作成済みの下書き商品は削除せず、そのまま残しています。":
    "The Shopify product and any draft product already created were kept.",
  "旧セット設定を削除できませんでした。再試行してください。":
    "Could not remove the old bundle setting. Try again.",
  Shopifyの商品編集を閉じました: "Shopify product editing was closed",
  "Shopifyで保存済みの場合は保存確認から商品同期へ進めます。保存していない場合は、同じ下書き商品の編集を再開してください。":
    "If you saved the product in Shopify, confirm the save to continue to product sync. Otherwise, resume editing the same draft product.",
  Shopify商品の作成結果を確認しています:
    "Checking the Shopify product creation result",
  "重複商品を作らず、同じ処理の結果だけを再確認します。少し待ってから再試行してください。":
    "Only the result of the same operation will be checked, without creating a duplicate. Wait briefly, then retry.",
  "Shopifyの商品編集画面を開けませんでした。Shopify管理画面からUreyukiBoxを開き直してください。":
    "Could not open Shopify's product editor. Reopen UreyukiBox from Shopify admin.",
  "Shopifyの商品編集を完了できませんでした。旧セットは変更していません。":
    "Shopify product editing could not be completed. The old bundle was not changed.",
  Shopifyの商品編集を再開できます: "You can resume Shopify product editing",
  Shopify商品の作成結果を安全に再確認します:
    "Safely recheck the Shopify product creation result",
  商品編集を再開: "Resume product editing",
  Shopifyでの保存を確認して商品同期へ進む:
    "Confirm Shopify save and continue to product sync",
  "同じShopify商品をUreyukiBoxの商品一覧へ読み込みます。旧キット、在庫数量、セット構成はまだ変更しません。次の画面で「セット構成を保存して在庫へ反映する」を押す必要があります。よろしいですか？":
    "Load the same Shopify product into the UreyukiBox catalog? This does not yet change the old kit, inventory quantities, or bundle components. You must use Save bundle and update calculated availability on the next screen to finish.",
  作成結果を再確認: "Recheck creation result",
  "商品名とSKUを入れた同じ下書きを開きます。閉じても別の商品は作りません。":
    "This opens the same draft with its title and SKU. Closing it will not create another product.",
  "商品のステータスは下書きのまま保存してください。アクティブにする必要はなく、UreyukiBoxも販売状態へ変更しません。":
    "Save the product while keeping its status as Draft. You do not need to activate it, and UreyukiBox will not change its sales status.",
  "同じ処理を再確認します。通信が途切れても別の商品は作りません。":
    "This rechecks the same operation. A connection interruption will not create another product.",
  "同じ保存先だけを同期し、セット構成の保存を再開します。商品を作り直しません。":
    "Only the same target will be synced before bundle saving resumes. The product will not be recreated.",
  作成したキット商品: "Created kit product",
  "サーバーが同じストアで作成・確認した商品だけを保存先に固定しています。旧商品IDはShopifyへ送りません。":
    "The target is fixed to the product created and verified by the server in this store. The old product ID is never sent to Shopify.",
  商品同期を開始できません: "Could not start product sync",
  "現在の商品一覧を確認できませんでした。":
    "The current product catalog could not be verified.",
  "保存済みの新しい商品を確認できません。商品編集の再開からやり直してください。":
    "The saved new product could not be verified. Resume product editing and try again.",
  "商品編集は完了しましたが、保存先の商品を同期一覧で確認できませんでした。セット構成は変更していません。もう一度同期してください。":
    "Product editing finished, but the target could not be verified in the synced catalog. The bundle was not changed. Sync again.",
  "以前の商品登録結果を同期一覧で確認できませんでした。商品を新しく作らず、もう一度同期してください。":
    "The earlier product-creation result could not be verified in the synced catalog. Sync again without creating another product.",
  以前の商品登録結果を確認しました:
    "The earlier product-creation result was found",
  保存先の商品を確認しました: "The target product was verified",
  "同期後に確認できた候補から、以前に登録した商品を選んでください。新しい商品は作成していません。":
    "Select the product you created earlier from the candidates found after sync. No new product was created.",
  "サーバーで確定済みの同じ商品だけを保存先に使います。商品を作り直しません。":
    "Only the same server-confirmed product will be used as the target. The product will not be recreated.",
  新しい商品を保存しました: "The new product was saved",
  "セット構成を確認し、「セット構成を保存して在庫へ反映する」を押してください。押すまで旧セットと警告は残ります。":
    "Review the bundle configuration, then select Save bundle and update calculated availability. The old bundle and its warning remain until you do.",
  "現在のShopifyにない原材料を選び直し、「セット構成を保存して在庫へ反映する」を押してください。押すまで旧セットと警告は残ります。":
    "Reselect components no longer present in Shopify, then select Save bundle and update calculated availability. The old bundle and its warning remain until you do.",
  安全な再開情報を確認できません: "Could not verify safe recovery data",
  "旧セットは変更していません。画面を読み込み直してください。":
    "The old bundle was not changed. Reload the page.",
  "Shopifyの商品登録を開始できませんでした。旧セットは変更していません。":
    "Shopify product creation could not be started. The old bundle was not changed.",
  以前の商品登録処理は商品同期だけで再開してください:
    "Resume the earlier product-creation flow by syncing only",
  "重複商品を防ぐため、以前の処理には下書き商品の作成を実行しません。":
    "To prevent a duplicate, no draft product will be created for the earlier flow.",
  "以前の商品登録処理の再開情報がサーバーと一致しません。旧セットは変更していません。":
    "The recovery data for the earlier product-creation flow does not match the server. The old bundle was not changed.",
  "以前の商品登録処理の保存先が一致しません。旧セットは変更していません。":
    "The target for the earlier product-creation flow does not match. The old bundle was not changed.",
  以前の商品登録処理を再開できませんでした:
    "Could not resume the earlier product-creation flow",
  "商品を新しく作らず安全に停止しました。同じ処理を再試行してください。":
    "Stopped safely without creating another product. Retry the same flow.",
  以前の商品登録処理を安全に再確認します:
    "Safely recheck the earlier product-creation flow",
  "重複商品を防ぐため、新しい商品は作らず商品同期だけを実行します。":
    "To prevent a duplicate, no new product will be created; only product sync will run.",
  "商品登録後の商品同期を完了できませんでした。セット構成は変更していません。もう一度お試しください。":
    "The product sync did not complete after product creation. The bundle configuration was not changed. Try again.",
  "旧キット商品が現在のShopify商品として再確認されました。新しい商品へ移さず、一覧を読み込み直して編集してください。":
    "The old kit product is current in Shopify again. Do not move it to the new product; reload the list and edit the current kit.",
  商品登録を続けられませんでした: "Could not continue product creation",
  商品登録後の同期を続けられませんでした:
    "Could not continue the sync after product creation",
  "新しい商品はShopifyに保存済みです。商品を作り直さず、同期だけを再試行してください。":
    "The new product is already saved in Shopify. Do not create it again; retry only the sync.",
  新しい商品はShopifyに保存済みです:
    "The new product is already saved in Shopify",
  商品同期だけを再試行: "Retry product sync only",
  登録したキット商品を選択: "Select the kit product you created",
  "Shopifyから返された名前やIDは使いません。同期後に新しく確認できた商品から、登録した商品を選んでください。":
    "UreyukiBox does not use the name or ID returned by Shopify. Select the product you created from products newly verified after sync.",
  新しいキット商品: "New kit product",
  商品の選択を解除: "Clear product selection",
  "安全な再開情報をブラウザに保存できないため、商品登録を開始しませんでした。ブラウザの保存設定を確認して画面を読み込み直してください。":
    "Product creation was not started because the safe recovery checkpoint could not be stored in this browser. Check browser storage settings and reload the page.",
  安全な再開情報をブラウザへ保存できません:
    "Could not store the safe recovery checkpoint in this browser",
  "重複作成を防ぐため、この画面では新しい商品を作成しません。ブラウザの保存設定を確認してから読み込み直してください。":
    "To prevent duplicate creation, this page will not create another product. Check browser storage settings, then reload the page.",
  保存先の商品を現在のShopifyで確認できません:
    "The target product could not be verified in the current Shopify catalog",
  "旧商品は整理していません。商品を同期してから、この画面を読み込み直してください。":
    "The old product was not cleaned up. Sync products, then reload this page.",
  中断したセット商品の置き換えを再開しました:
    "Resumed the interrupted bundle replacement",
  "新しい商品への保存は完了しています。Shopifyへ再送せず、旧セットの整理だけを再試行できます。":
    "Saving to the new product is complete. Retry only old-bundle cleanup without sending anything to Shopify again.",
  "同じ商品と構成だけを使って、安全に保存を再試行できます。":
    "You can safely retry the save using only the same product and component configuration.",
  同じセット構成で保存を完了してください:
    "Complete the save with the same bundle configuration",
  "結果が不明な保存を別の内容で上書きしません。商品や原材料を変えず、保存済みの同じ内容だけを安全に再試行します。":
    "An uncertain save is never overwritten with different data. Retry only the stored, identical product and component configuration.",
  同じセット構成の保存を再試行: "Retry the same bundle configuration save",
  新しいShopify商品へセット構成を移しました:
    "Bundle configuration moved to the new Shopify product",
  "旧商品のセット構成は整理され、現在の商品だけが残っています。":
    "The old bundle configuration was removed, leaving only the current product.",
  "新しい商品への反映は完了しましたが、旧セットの整理が残っています":
    "The new product was updated, but the old bundle still needs cleanup",
  旧セットの整理を再試行: "Retry old bundle cleanup",
  "新しい商品をもう一度Shopifyへ送る必要はありません。このボタンはUreyukiBox内の旧セットだけを安全に整理します。":
    "You do not need to send the new product to Shopify again. This button only cleans up the old bundle inside UreyukiBox.",
  旧セットをまだ整理できませんでした:
    "The old bundle could not be cleaned up yet",
  旧セットを整理する前提を確認できません:
    "Could not verify the prerequisites for old-bundle cleanup",
  "保存済みの新しいセット構成または原材料が現在のデータと一致しません。旧セットは削除せず、安全に停止しています。":
    "The saved new bundle configuration or its components no longer match the current data. The old bundle was not deleted, and cleanup stopped safely.",
  旧商品の原材料を選び直してください:
    "Reselect components for the old products",
  "旧商品のIDは引き継ぎません。各行で現在のShopify商品を明示的に選んでください。":
    "Old product IDs are not carried over. Explicitly select a current Shopify product on each row.",
  "旧原材料「{name}」の代わりを選択":
    "Select a replacement for old component “{name}”",
  選んだ商品には別のセット構成があります:
    "The selected product already has another bundle configuration",
  "既存のセット構成を上書きしません。上の候補から別の商品を選んでください。":
    "The existing bundle configuration will not be overwritten. Select another product from the candidates above.",
  キット構成の編集: "Edit bundle components",
  現在の商品一覧を確認できないため保存できません:
    "Cannot save because the current product catalog could not be verified",
  "通信状態を確認し、商品一覧を再読み込みしてから保存してください。":
    "Check your connection and reload the product catalog before saving.",
  原材料は30件までです: "You can add up to 30 components",
  "Shopifyのセット商品に登録できる原材料は最大30件です。":
    "A Shopify bundle can contain up to 30 components.",
  設定済みのキットはまだありません: "No kits configured yet",
  原材料: "Component",
  原材料数: "Components",
  組立可能数: "Can assemble",
  構成: "Composition",
  構成の取得に失敗しました: "Could not load the composition",
  同じ原材料が複数行にあります:
    "The same component appears on more than one line",

  // ── Stocky 移行 ─────────────────────────────────────────────
  "Stocky データのコピー（取り込み）": "Copy your Stocky data",
  "Stockyコピー用キーをご確認のうえ、もう一度お試しください。":
    "Check your Stocky copy key and try again.",
  "このキーはコピー処理にのみ使用し、保存しません":
    "This key is used only for the import and is never stored",
  コピーに失敗しました: "The import failed",
  コピーは実行中です: "The import is already running",
  コピー中: "Importing",
  取得するStockyデータ: "Stocky data that will be collected",
  "仕入先、商品と仕入先の紐付け、仕入用税区分":
    "Suppliers, product-to-supplier links, and purchase tax categories",
  "発注書の親レコードと明細、在庫調整の親レコードと明細":
    "Purchase-order parent records and lines, and stock-adjustment parent records and lines",
  "Low Stock・発注点（ROP）・リードタイム、棚卸し履歴と明細、最低発注数量（MOQ）":
    "Low Stock, reorder points (ROP), lead times, stocktake history and lines, and minimum order quantities (MOQ)",
  "Stock on hand、Average cost、Statisticsなどの取得可能なレポート原本。TransfersがStocky外へ遷移する場合や、Orders CSVが画面ダウンロードではなくメール送付のみの場合は、取得不能の証跡を保存し、0件成功として扱いません":
    "Available original reports such as Stock on hand, Average cost, and Statistics. If Transfers redirects outside Stocky, or if Orders CSV is only delivered by email instead of downloaded from the screen, we save evidence that it could not be collected and do not treat it as a successful zero-item result.",
  送信先と保存方法: "Where the data is sent and how it is stored",
  "Chrome拡張がStockyから取得したCSVと画面データは、暗号化されたHTTPS通信でUreyukiBoxへ送信し、現在のShopifyストアの移行データと原本として保存します。":
    "CSV files and screen data collected from Stocky by the Chrome extension are sent to UreyukiBox over encrypted HTTPS and stored as migration data and source originals for the current Shopify store.",
  "含まれる可能性がある個人情報・財務情報・通信内容":
    "Personal information, financial information, and communications that may be included",
  "仕入先／ベンダー担当者・在庫調整実行者（adjusted_by）の氏名、メールアドレス、住所、電話番号、オンライン発注用ユーザー名、アカウント識別子（パスワードは除く）":
    "Supplier/vendor contact and stock-adjustment operator (adjusted_by) names, email addresses, postal addresses, phone numbers, online-ordering usernames, and account identifiers (excluding passwords)",
  "発注書、支払状態・支払条件、仕入用税区分、原価、売上、利益等の購買・財務情報":
    "Purchase orders, payment status and terms, purchasing tax categories, cost, sales, profit, and other purchasing and financial information",
  "発注書コメント、仕入先の非公開メモ、発注書メモテンプレート等の通信・メモ内容":
    "Purchase-order comments, private supplier notes, purchase-order note templates, and other communication or note content",
  "取得元を示す固定ページパス・URL、ページタイトル、取得時刻":
    "Fixed source-page paths or URLs, page titles, and acquisition times used as migration provenance",
  接続用認証情報とアクセスログ: "Connection credentials and access logs",
  "Chrome拡張との接続には、一回限りの接続コードと短命なジョブ専用Bearer認証情報（job capability）を使用します。これらはStockyのパスワード、Cookie、APIキーではありません。":
    "Connection to the Chrome extension uses a one-time pairing code and a short-lived, job-specific Bearer capability. These are not your Stocky password, cookie, or API key.",
  "UreyukiBox APIへの接続時、セキュリティと障害調査のアクセスログに、IPアドレス、日時、固定API経路、応答状態、ブラウザとChrome拡張のバージョン、ショップ・ジョブ・タスク識別子、エラーコードが含まれる場合があります。":
    "When connecting to the UreyukiBox API, security and troubleshooting access logs may include the IP address, date and time, fixed API path, response status, browser and Chrome extension versions, shop, job, and task identifiers, and error code.",
  利用目的と禁止用途: "Permitted purposes and prohibited uses",
  "取得データ、接続用認証情報、アクセスログは、Stockyデータの移行、セキュリティ、不正利用防止、障害調査、サービスの信頼性維持のためだけに使用します。":
    "Collected data, connection credentials, and access logs are used only for Stocky data migration, security, abuse prevention, troubleshooting, and service reliability.",
  "取得データを販売せず、広告、データ仲介、信用評価、融資判断には使用しません。":
    "Collected data is not sold and is not used for advertising, data brokering, creditworthiness, or lending decisions.",
  取得しない情報: "Information that is not collected",
  "Chrome拡張は、Stockyのパスワード、認証Cookie、Stocky APIキーを取得・保存・UreyukiBoxへ送信しません。":
    "The Chrome extension does not collect, store, or send Stocky passwords, authentication cookies, or Stocky API keys to UreyukiBox.",
  同意の取消と安全停止: "Withdrawing consent and fail-safe stopping",
  "開始後の処理停止または保存済みデータの削除を希望する場合は、UreyukiBoxのお問い合わせ画面から依頼してください。":
    "To request that processing be stopped after it starts or that stored data be deleted, contact us through the UreyukiBox Contact screen.",
  "別ストア、Stocky未ログイン、Chrome拡張なし、通信エラー、完全性未確認のいずれかを検出した場合、未取得データを0件や完了として扱わず安全に停止します。再試行時は同じ進行中ジョブを確認して再開し、重複ジョブを作成しません。":
    "If we detect a different store, no Stocky login, no Chrome extension, a communication error, or unverified completeness, the copy stops safely without treating missing data as zero or complete. A retry checks and resumes the same active job instead of creating a duplicate job.",
  "対象ストア: {shop}": "Target store: {shop}",
  "上記の取得対象、接続用認証情報、アクセスログ、利用目的、HTTPS送信、UreyukiBoxへの保存に同意します":
    "I consent to the collection scope, connection credentials, access logs, permitted purposes, HTTPS transmission, and storage in UreyukiBox described above",
  同じコピーを再試行: "Retry the same copy",
  "Chrome拡張の存在と対応契約を確認しています…":
    "Checking that the Chrome extension is present and supports this contract…",
  "同じ進行中ジョブの接続状態を確認しています…":
    "Checking the connection for the same active job…",
  "同じジョブの接続を安全に修復しています…":
    "Safely repairing the connection for the same job…",
  "Chrome拡張で接続権限、Stockyログイン、ストア一致を確認し、コピーを開始しています…":
    "The Chrome extension is verifying connection authorization, the Stocky login, and the store match before starting the copy…",
  "Chrome拡張で接続権限、Stockyログイン、ストア一致を再確認し、同じコピーを再開しています…":
    "The Chrome extension is re-verifying connection authorization, the Stocky login, and the store match before resuming the same copy…",
  "保存結果と完全性を確認しています…":
    "Checking the saved result and its completeness…",
  "取得したStockyデータを正規化しています…":
    "Normalizing the collected Stocky data…",
  "必須カテゴリとレポート原本の完全性を確認しています…":
    "Verifying the completeness of required categories and original reports…",
  "Stockyからデータを取得し、UreyukiBoxへ保存しています…":
    "Collecting data from Stocky and saving it to UreyukiBox…",
  Stockyデータを取得しています: "Collecting Stocky data",
  "保存・正規化・完全性を確認しています":
    "Saving, normalizing, and verifying completeness",
  "停止中です。同じコピーを再試行してください": "Stopped. Retry the same copy",
  コピー工程が完了しました: "The copy process is complete",
  "{stage}（{percent}%）": "{stage} ({percent}%)",
  "保存済みの取得工程数を確認しています…":
    "Checking the number of durably saved acquisition steps…",
  "この進捗率は経過時間の予測ではなく、保存・確認済みの工程だけを示します。":
    "This progress percentage shows only saved and verified steps, not an estimate of elapsed time.",
  "取得工程: {completed}/12": "Acquisition steps: {completed}/12",
  "100%はコピー工程の完了を示します。取得できなかった項目や必須列の不足は、下の完了結果で確認してください。":
    "100% means the copy process is complete. Review the completion result below for anything that could not be collected or had missing required columns.",
  "UreyukiBox Stocky Migration Chrome拡張を確認できませんでした。拡張をインストールまたは有効化し、同じChromeで再試行してください。":
    "The UreyukiBox Stocky Migration Chrome extension could not be detected. Install or enable it, then try again in the same Chrome browser.",
  "Chrome拡張がこのStockyコピー契約に対応していません。拡張を更新してから再試行してください。":
    "The Chrome extension does not support this Stocky copy contract. Update the extension and try again.",
  Chrome拡張をChromeウェブストアからインストールまたは更新:
    "Install or update the Chrome extension from the Chrome Web Store",
  Chrome拡張をインストールまたは更新: "Install or update the Chrome extension",
  "StockyコピーにはUreyukiBox Stocky Migration Chrome拡張が必要です。Chromeウェブストアからいつでもインストールまたは最新版へ更新できます。":
    "Stocky Copy requires the UreyukiBox Stocky Migration Chrome extension. You can install it or update to the latest version from the Chrome Web Store at any time.",
  "検出した拡張バージョン: {version}": "Detected extension version: {version}",
  "必要な拡張バージョン: {version}": "Required extension version: {version}",
  "ChromeウェブストアでUreyukiBox Stocky Migrationをインストールまたは更新してください。":
    "Install or update UreyukiBox Stocky Migration from the Chrome Web Store.",
  "ChromeウェブストアでUreyukiBox Stocky Migrationをインストールまたは更新します。":
    "Install or update UreyukiBox Stocky Migration in the Chrome Web Store.",
  "この画面へ戻り、「導入後に再確認」を押します。":
    "Return to this screen and select Check again after installation.",
  "この画面へ戻り、「同じコピーを再試行」を押します。":
    "Return to this screen and select Retry the same copy.",
  "導入後に「Stockyコピーへ進む」を押します。この段階ではコピージョブはまだ作成されていません。":
    "After installation, select Continue to Stocky copy. No copy job has been created at this entry stage.",
  "進行中の同じコピーと保存済みデータは保持されます。新しいコピーは作成されません。":
    "The same active copy and saved data are preserved. No new copy is created.",
  Chromeウェブストアを開く: "Open Chrome Web Store",
  "Stockyへログインしていません。同じChromeで対象ストアのStockyへログインしてから再試行してください。":
    "You are not signed in to Stocky. Sign in to the target store's Stocky account in the same Chrome browser, then try again.",
  "Stockyで確認したストアが現在のShopifyストアと一致しません。対象ストアを一致させるまでコピーは開始または継続しません。":
    "The store verified in Stocky does not match the current Shopify store. The copy will not start or continue until the target stores match.",
  "別のStockyコピーが進行中です。重複ジョブを作らず、そのコピーが終了するまで安全に停止します。":
    "Another Stocky copy is active. This copy stops safely without creating a duplicate job until the other copy finishes.",
  "進行中ジョブの接続権限を安全に確認できませんでした。重複作成せず停止しました。少し待ってから同じ画面で再試行してください。":
    "The connection authorization for the active job could not be verified safely. The copy stopped without creating a duplicate. Wait briefly, then retry from this same screen.",
  "このコピージョブは終了またはキャンセルされたため停止しました。未取得データを0件や完了として扱っていません。":
    "This copy job ended or was cancelled, so processing stopped. Uncollected data was not treated as zero or complete.",
  "コピーには確認が必要なため安全に停止しました。StockyログインとChrome拡張を確認し、同じ進行中ジョブを再試行してください。":
    "The copy stopped safely because it needs attention. Check the Stocky login and Chrome extension, then retry the same active job.",
  "Chrome拡張とこの画面の通信契約が一致しないため停止しました。Chrome拡張を最新版へ更新して、同じコピーを再試行してください。":
    "The copy stopped because the Chrome extension's communication contract does not match this screen. Update the extension, then retry the same copy.",
  "Chrome拡張で別のStockyコピーが進行中のため停止しました。進行中のコピーを完了してから、同じコピーを再試行してください。":
    "The copy stopped because the Chrome extension is running another Stocky copy. Finish the active copy, then retry this same copy.",
  "進行中ジョブとChrome拡張へ渡した条件が一致しないため停止しました。対象のStockyコピー画面を開き直してください。":
    "The copy stopped because the active job does not match the conditions sent to the Chrome extension. Reopen the target Stocky copy screen.",
  "進行中ジョブの接続権限を確認できないため停止しました。重複ジョブは作成していません。同じコピーを再試行してください。":
    "The copy stopped because the active job's connection authorization could not be verified. No duplicate job was created. Retry the same copy.",
  "Stocky画面の読み取り確認で対応が必要な状態を検出し、不完全なデータを完了扱いにせず停止しました。表示中のバージョンが必要な拡張バージョンと一致している場合は、更新を繰り返さずお問い合わせください。":
    "The copy found a Stocky screen-reading condition that needs attention and stopped without treating incomplete data as complete. If the displayed version matches the required extension version, do not keep updating it; contact support.",
  "Stocky画面の取得に失敗したため停止しました。Stockyの画面を表示できることを確認し、同じコピーを再試行してください。":
    "The copy stopped because the Stocky screen could not be retrieved. Confirm that the Stocky screen opens, then retry the same copy.",
  "Chrome拡張が未対応のStocky取得経路を検出したため停止しました。Stockyログインや拡張の導入状態が原因ではありません。Chrome拡張を最新版へ更新して、同じコピーを再試行してください。":
    "The Chrome extension stopped after detecting an unsupported Stocky collection route. This is not caused by the Stocky login or extension installation state. Update the extension, then retry the same copy.",
  "Stockyの原本1件が保存上限を超えたため、途中までを完了扱いにせず停止しました。お問い合わせ画面からご連絡ください。":
    "One Stocky source exceeded the storage limit, so the copy stopped without treating a partial result as complete. Contact us from the support screen.",
  "保存データのサイズまたはハッシュの完全性を確認できないため、完了扱いにせず停止しました。同じコピーを再試行してください。":
    "The saved data's size or hash integrity could not be verified, so the copy stopped without being marked complete. Retry the same copy.",
  "取得中にStockyの元データが変わったため、異なる時点のデータを混ぜず停止しました。同じコピーを再試行してください。":
    "The Stocky source changed during collection, so the copy stopped without mixing data from different points in time. Retry the same copy.",
  "UreyukiBoxへの保存通信または保存応答を確認できないため停止しました。保存済みデータは削除されません。同じコピーを再試行してください。":
    "The copy stopped because the save request or response from UreyukiBox could not be verified. Saved data was not deleted. Retry the same copy.",
  "Stockyの取得処理を完了できなかったため停止しました。保存済みデータを保ったまま、同じコピーを再試行してください。":
    "The Stocky collection task could not be completed, so the copy stopped. Retry the same copy while retaining saved data.",
  "Chrome拡張内で予期しないエラーが発生したため停止しました。同じコピーを再試行し、続く場合はお問い合わせ画面からご連絡ください。":
    "The copy stopped because an unexpected error occurred in the Chrome extension. Retry the same copy, and contact us from the support screen if it continues.",
  "Chrome拡張のエラーコード: {code}": "Chrome extension error code: {code}",
  "完了結果の整合性を確認できなかったため、完了として表示せず安全に停止しました。":
    "The completion result could not be verified for integrity, so the copy stopped safely without being shown as complete.",
  "操作中にShopifyストアが変わったため、別ストアへ送信せず安全に停止しました。Shopify管理画面から対象ストアを開き直してください。":
    "The Shopify store changed during the operation, so the copy stopped safely without sending data to another store. Reopen the target store from Shopify admin.",
  "接続またはサーバー状態を確認できなかったため、コピーを完了扱いにせず安全に停止しました。再試行すると同じ進行中ジョブを確認して再開します。":
    "The connection or server state could not be verified, so the copy stopped safely without being marked complete. A retry checks and resumes the same active job.",
  Stockyコピーを開始する前に確認してください:
    "Review before starting Stocky copy",
  "Stocky APIコピーの新規受付は終了しています":
    "New Stocky API copy jobs are closed",
  "期限前に作成済みの同じStockyコピーと完了結果だけを確認・再開できます。この画面から新しいAPIコピーは作成しません。新しいコピーには終了後の読み取り専用コピーまたは手動CSVコピーをご利用ください。":
    "You can only review or resume the same Stocky copy and completed result created before the cutoff. This screen will not create a new API copy. Use the post-shutdown read-only copy or manual CSV copy for any new copy.",
  "この画面では、現在のShopifyストアに対して1件のStockyコピージョブだけを作成または再開します。Chrome拡張が同じジョブを先に接続した後、Stockyコピー用キーを使うAPIコピーと画面取得を同じジョブで続けます。":
    "This screen creates or resumes exactly one Stocky copy job for the current Shopify store. After the Chrome extension connects to that same job, API copy using the Stocky copy key and screen collection continue in the same job.",
  "この画面では、現在のShopifyストアに対して1件のStockyコピージョブだけを作成または再開します。Chrome拡張が接続権限、Stockyログイン、ストア一致を確認した後、同じジョブで画面取得を続けます。":
    "This screen creates or resumes exactly one Stocky copy job for the current Shopify store. After the Chrome extension verifies connection authorization, Stocky sign-in, and the store match, screen collection continues in that same job.",
  "完了後は、どちらのStockyコピーでも同じ12項目と取得元ごとの処理件数を確認できます。手動CSVコピーを独立した経路として使う場合は、別タブの「手動CSVコピー」を使ってください。":
    "After completion, both Stocky copy methods show the same 12 result categories and processed counts by source. To use Manual CSV copy as an independent path, open the separate Manual CSV copy tab.",
  "Stockyコピー用キーはAPIコピーにだけ使い、入力欄から直ちに消去します。新しいAPI再試行が必要な場合だけ、もう一度入力してください。":
    "The Stocky copy key is used only for API copy and is cleared from the input immediately. Enter it again only when a new API attempt is required.",
  "チェックを外している間は同意していない状態です。開始前はいつでもチェックを外して同意を取り消せます。コピー画面でのデータを読まないChrome拡張導入確認（PING）を除き、未同意状態からStockyログイン確認、Stockyデータの取得、ジョブの確認・作成、新しいコピーを開始するメッセージを送信しません。":
    "While this box is unchecked, you have not consented. Before starting, you can withdraw consent at any time by unchecking it. Except for the data-blind Chrome extension installation check (PING) on the copy entry screen, no request is sent while consent is absent to check the Stocky sign-in, collect Stocky data, inspect or create a job, or start a new copy.",
  "このストアでは現在のStockyコピーを利用できないため、Chrome拡張へ接続せず安全に停止しました。":
    "Stocky copy is not currently available for this store, so it stopped safely without connecting to the Chrome extension.",
  "Stocky APIコピーの新規受付は2026年8月31日で終了しました。期限前に作成済みの同じジョブと完了結果は引き続き確認・再開できます。新しいコピーには終了後の読み取り専用コピーまたは手動CSVコピーをご利用ください。":
    "New Stocky API copy jobs closed on August 31, 2026. You can still review or resume the same job and its completed result if it was created before the cutoff. Use the post-shutdown read-only copy or manual CSV copy for any new copy.",
  "この画面のコピー種別が現在のジョブまたはChrome拡張の状態と一致しないため、別プロファイルへ送信せず安全に停止しました。コピー画面から開き直してください。":
    "The copy type on this screen does not match the active job or Chrome extension state. It stopped safely without sending anything to another profile. Reopen it from the Stocky copy screen.",
  "進行中ジョブ: {activeLabel} / {activeVersion}。現在の画面: {requestedLabel} / {requestedVersion}。ジョブのコピー種別と契約は途中で変更せず、正しい画面で同じジョブを再開します。":
    "Active job: {activeLabel} / {activeVersion}. Current screen: {requestedLabel} / {requestedVersion}. The job's copy type and contract will not be changed mid-run; reopen the correct screen to resume the same job.",
  "進行中の〜8/31コピーを開く": "Open the active through-August-31 copy",
  進行中の終了後読み取り専用コピーを開く:
    "Open the active post-shutdown read-only copy",
  "Stockyコピー用キーが必要です。新しく入力してから同じコピーを再試行してください。":
    "A Stocky copy key is required. Enter it again, then retry the same copy.",
  "Stocky APIコピーを完了できませんでした。同じジョブを再利用しますが、再試行にはStockyコピー用キーの再入力が必要です。":
    "Stocky API copy could not be completed. The same job will be reused, but you must enter the Stocky copy key again before retrying.",
  "このストアで利用できるStockyコピーと進行中ジョブを安全に確認しています…":
    "Safely checking Stocky copy availability and any active job for this store…",
  "重複しないStockyコピージョブを準備しています…":
    "Preparing a non-duplicating Stocky copy job…",
  "同じジョブのAPIコピーへStockyコピー用キーを安全に送信しています…":
    "Securely submitting the Stocky copy key to the API stage of the same job…",
  "Stocky APIコピーを実行しています…": "Running Stocky API copy…",
  "バックグラウンド実行を確認しました。このページを離れて別のページやタブを開いても大丈夫です。Chromeは閉じず、Stockyにログインしたままにしてください。":
    "Background execution is confirmed. You can leave this page and open another page or tab. Keep Chrome open and stay signed in to Stocky.",
  "コピーの開始処理が完了するまで、この画面を開いたままお待ちください。":
    "Keep this page open until the copy startup process is complete.",
  "この画面のコピー種別を確認できませんでした。コピー画面から対象タブを開き直してください。":
    "The copy type for this screen could not be verified. Reopen the required tab from the Stocky copy screen.",
  Stockyコピーを安全に停止しました: "Stocky copy stopped safely",
  同意してStockyコピーを開始: "Agree and start Stocky copy",
  同意して期限前のコピーを確認: "Agree and check a pre-cutoff copy",
  キーを入力して同じコピーを再試行: "Enter the key and retry the same copy",

  自動コピーは同じ12項目を保存します:
    "Both automatic copy methods save the same 12 categories",
  終了後の読み取り専用期間について: "About the post-shutdown read-only period",
  "Stockyコピー用キーを使うAPI取得とChrome拡張による画面取得を、1件の同じコピージョブで続けて実行します。CSVや画面表を追加で貼り付ける必要はありません。":
    "API collection using the Stocky copy key and screen collection by the Chrome extension run as one continuous copy job. No additional CSV upload or table paste is required.",
  "Chrome拡張がログイン済みのStockyから必要なデータを取得し、1件のコピージョブとしてUreyukiBoxへ保存します。Stockyコピー用キーは不要です。":
    "The Chrome extension collects the required data from signed-in Stocky and saves it to UreyukiBox as one copy job. No Stocky copy key is required.",
  "仕入先・仕入用税区分・商品と仕入先・発注書と明細・在庫調整と明細・Low Stock・リードタイム・MOQ・棚卸し・商品・需要予測設定・レポート原本を、取得できた範囲と取得不能の証跡を含めて確認できます。":
    "You can review suppliers, purchase tax categories, product-supplier links, purchase orders and lines, stock adjustments and lines, Low Stock, lead times, MOQ, stocktakes, products, forecasting settings, and report originals, including collected coverage and evidence of anything Stocky could not provide.",
  "ストアを確認しています…": "Checking the store…",
  Shopifyストアを確認できませんでした:
    "The Shopify store could not be verified",
  "別ストアへ接続しないため、現在のストアを確認できるまでコピーを開始しません。":
    "To prevent connecting to another store, copy will not start until the current store is verified.",
  "Stockyコピーの利用可否を確認しています…":
    "Checking Stocky copy availability…",
  Stockyコピーは現在利用できません: "Stocky copy is not currently available",
  "サーバーの公開設定またはストアの確認を完了できなかったため、Chrome拡張へ接続せず安全に停止しています。":
    "The server publication setting or store verification could not be completed, so the flow is stopped safely without connecting to the Chrome extension.",
  "Chrome拡張を確認しています…": "Checking the Chrome extension…",
  Chrome拡張の更新が必要です: "The Chrome extension must be updated",
  導入後に再確認: "Check again after installing",
  "データ取得を始めず安全に停止しています。デスクトップ版Google Chromeへ拡張をインストールして有効にし、この画面で再確認してください。":
    "No data collection has started. Install and enable the extension in desktop Google Chrome, then check again on this screen.",
  "このコピー種別に対応する最新版へ更新するまで、データ取得を始めず安全に停止しています。":
    "No data collection will start until the extension is updated to a version that supports this copy type.",
  Chrome拡張を確認できました: "Chrome extension detected",
  "この確認は拡張の導入有無と対応契約だけを見るPINGです。Stockyログインとストア一致は、次の画面で取得内容へ同意した後に確認します。":
    "This check is a data-blind PING that verifies only extension installation and contract support. Stocky sign-in and the store match are checked after you consent to the collection details on the next screen.",
  Stockyをインストールまたは開く: "Install or open Stocky",
  やり直す: "Try again",

  // ── 通知・設定 ──────────────────────────────────────────────
  "在庫低下・在庫切れアラートを送信する宛先を設定します。メール・Slack・LINE は併用でき、オンにしたチャネルすべてに送信されます。":
    "Choose where low-stock and out-of-stock alerts are sent. Email, Slack and LINE can be used together, and alerts go to every channel you switch on.",
  メールで通知する: "Send alerts by email",
  "Slack で通知する": "Send alerts to Slack",
  "LINE で通知する": "Send alerts to LINE",
  "オフにしてもアドレスは残ります（再開はここをオンに戻すだけ）。":
    "Switching this off keeps the address — turn it back on to resume.",
  "オフにしても連携は残ります（再開はここをオンに戻すだけ）。":
    "Switching this off keeps everyone linked — turn it back on to resume.",
  "通知のオン/オフを保存できませんでした:":
    "Could not save the notification switch:",
  // ── LINE の受信者（店の人が複数連携できる・上限は max_recipients＝20人/店） ──
  "{count}人が受信中": "{count} receiving",
  受信中のメンバー: "Who receives these alerts",
  "心当たりのない相手が混ざっていたら解除してください。連携コードを知っている人は誰でもここに並びます。":
    "Remove anyone you do not recognise. Anyone who knows the link code can appear in this list.",
  "（名前を取得できませんでした）": "(name unavailable)",
  連携日時: "Linked",
  解除: "Remove",
  メンバーを追加する: "Add someone",
  "店舗のスタッフを最大{max}人まで追加できます。追加したい人それぞれが、次の2ステップを行ってください（あと{count}人）。":
    "Up to {max} of your staff can receive these alerts. Each person does the two steps below ({count} slots left).",
  コピーしました: "Copied",
  "コードが外部に漏れたときは再発行してください。いま受信中のメンバーはそのまま残り、古いコードは使えなくなります。":
    "Reissue the code if it leaks. Everyone currently receiving alerts stays linked, and the old code stops working.",
  "コードを再発行できませんでした:": "Could not reissue the code:",
  連携コードを再発行: "Reissue link code",
  全員の連携を解除: "Unlink everyone",
  通知設定の取得に失敗しました: "Could not load notification settings",
  通知メールアドレス: "Notification email address",
  "メール通知はStarterプラン以上で利用できます。発注点を割った商品を欠品前にお知らせします。":
    "Email alerts are available on Starter and above. They warn you before products below their reorder points run out.",
  "Slack ワークスペースで Incoming Webhook を作成し、URL を貼り付けてください":
    "Create an incoming webhook in your Slack workspace and paste the URL here",
  設定の保存に失敗しました: "Could not save your settings",
  "会社名・屋号": "Company or trading name",
  住所: "Address",
  郵便番号: "Postal code",
  電話番号: "Phone number",
  "適格請求書 登録番号": "Invoice registration number",
  "適格請求書登録番号（任意）": "Invoice registration number (optional)",
  "登録番号は「T」に続けて数字13桁で入力してください":
    "Enter the registration number as “T” followed by 13 digits",
  "自社が適格請求書発行事業者の場合のみ。「T」+ 数字13桁。":
    "Only if your business is a registered invoice issuer. “T” plus 13 digits.",
  "任意。未登録（免税事業者等）の場合は空欄のままにしてください。あとから登録できます。":
    "Optional. Leave blank if you are not registered (for example a tax-exempt business). You can add it later.",
  "任意。発注書に表示されます。形式の自動チェックは行いません。":
    "Optional. Shown on purchase orders. The format is not validated automatically.",
  // ── 仕入税率の編集画面（TaxRateSettings・国別税モデルの要石） ──
  仕入税率: "Purchase tax rates",
  "発注書の税区分に使う税率です（名前と率はこの店専用の設定）":
    "Tax rates used for the tax categories on purchase orders (names and rates belong to this store)",
  "カナダのストアには税率のプリセットがありません。州により GST に PST・HST・QST などが加わり税率が異なるためです。適用される州の税率を、ここで行として追加できます。":
    "Canadian stores start with no preset tax rates, because the rate differs by province — PST, HST or QST is added on top of GST. You can add the rates that apply in your province as rows here.",
  税率の取得に失敗しました: "Failed to load tax rates",
  "国のプリセットは初期値にすぎません。税率の変更・法改正への反映はこの画面で行ってください（自動では変わりません）。変更はこれから作る発注書から反映され、確定済みの発注書の金額は変わりません。":
    "Country presets are only starting values. When rates change, update them here — the app never changes them automatically. Changes apply to purchase orders you create from now on; confirmed purchase orders keep their amounts.",
  "税率が1件も無いため、発注書に税のセクションは表示されません。仕入税を載せる場合は、ここで追加してください。":
    "There are no tax rates, so purchase orders show no tax section. Add one here if your purchases are taxed.",
  名前: "Name",
  税率: "Tax rate",
  税率を追加: "Add a tax rate",
  "例: 軽減 8%": "e.g. Reduced 8%",
  "0% の税率も作れます（免税・リバースチャージなど）。名前は発注書（画面・PDF・CSV）にそのまま表示されます。":
    "You can also create 0% rates (exempt purchases, reverse charge and so on). The name appears on purchase orders exactly as written — on screen, PDF and CSV.",
  "税率は 0〜100 のパーセント（小数第2位まで）で入力してください":
    "Enter the tax rate as a percentage from 0 to 100, with up to 2 decimal places",
  "税率「{name}」を削除します。この税率を使っている商品は、次の発注書から既定の税率で計算されます。":
    "This deletes the tax rate “{name}”. Products using it fall back to the default rate from the next purchase order onward.",
  登録番号は20文字以内で入力してください:
    "Enter a registration number of 20 characters or fewer",
  株式会社〇〇商店: "Example Trading Co.",
  "東京都渋谷区神宮前1-2-3": "1-2-3 Jingumae, Shibuya-ku, Tokyo",
  山田珈琲焙煎所: "Yamada Coffee Roasters",
  "例: PO-2026-001": "e.g. PO-2026-001",

  // ── 税・金額 ────────────────────────────────────────────────
  税区分: "Tax category",
  "標準 10%": "Standard 10%",
  "軽減 8%": "Reduced 8%",
  "控除可能 消費税": "Deductible consumption tax",
  "控除不可 消費税": "Non-deductible consumption tax",
  "経過措置 控除割合": "Transitional deduction rate",
  小計: "Subtotal",
  "他の拠点も含めるには、拠点数の多いプランへの変更が必要です。":
    "To include your other locations, move to a plan with more locations.",
  季節性の商品別設定: "Seasonality by product",
  "どの商品がどの季節イベントで伸びるかを決めます。確定するまで季節係数は適用されません。":
    "Choose which products actually spike for each seasonal event. No seasonal multiplier is applied until you confirm.",
  提案を作成できませんでした: "Could not create suggestions",
  "時間をおいて再度お試しください。提案を作れない間も、未確認の商品に季節係数は適用されません。":
    "Please try again later. While suggestions are unavailable, no seasonal multiplier is applied to unreviewed products.",
  提案ができました: "Suggestions are ready",
  "新しい候補も自動では適用されません。「確認が必要」のイベントを確認してください。":
    'New suggestions are not applied automatically. Review the events marked "Needs review".',
  "このストアの所在国には固定カレンダーの季節補正がありません。設定する項目もありません。":
    "We have no seasonal calendar for your store's country, so there is nothing to configure here.",
  季節性の設定を取得できませんでした:
    "Could not load your seasonality settings",
  うち税: "of which tax",
  店の既定: "Store default",
  "合計（税込）": "Total (tax included)",
  合計: "Total",
  金額: "Amount",
  単価: "Unit price",

  // ── アンケート・流入元 ──────────────────────────────────────
  "UreyukiBox をどこで知りましたか？": "How did you hear about UreyukiBox?",
  "プランを選ぶ前に、UreyukiBox をどこで知りましたか？":
    "Before you choose a plan — how did you hear about UreyukiBox?",
  "どこで知りましたか？": "How did you hear about us?",
  流入元: "Source",
  紹介元: "Referred by",
  "紹介元のお名前・会社名": "Name or company that referred you",
  "ブログ・記事・ニュース・SNS名":
    "Blog, article, news outlet or social account",
  コードをコピー: "Copy code",
  送信: "Send",
  送信して続ける: "Send and continue",
  経路: "Channel",

  // ── 共通の語・状態・操作 ────────────────────────────────────
  再試行: "Retry",
  保存: "Save",
  保存に失敗しました: "Could not save",
  "{name} の構成（原材料 {n} 件）をすべて削除します。":
    "This will delete the entire bill of materials for {name} ({n} components).",
  "この操作は取り消せません。よろしいですか？":
    "This cannot be undone. Continue?",
  追加: "Add",
  追加する商品: "Product to add",
  適用: "Apply",
  適用中: "Applying",
  取消: "Cancel",
  除外: "Exclude",
  開始: "Start",
  読み込み中: "Loading",
  今すぐ同期: "Sync now",
  未同期: "Not synced",
  未登録: "Not registered",
  未設定: "Not set",
  選択してください: "Please choose",
  状態: "Status",
  理由: "Reason",
  内容: "Details",
  メモ: "Note",
  "メモ（任意）": "Note (optional)",
  備考: "Remarks",
  数量: "Quantity",
  日時: "Date and time",
  拠点: "Location",
  拠点を選択してください: "Please choose a location",
  "{name}（プランの拠点数上限を超えています）":
    "{name} (over your plan's location limit)",
  在庫: "Stock",
  商品: "Product",
  商品名: "Product name",
  "商品名 / SKU": "Product / SKU",
  商品名なし: "No product name",
  "{name}（{sku}） ・在庫{qty}": "{name} ({sku}) · Stock {qty}",
  "{poNumber}（{supplier}）": "{poNumber} ({supplier})",
  "{name} ・在庫{qty}": "{name} · Stock {qty}",
  商品を検索: "Search products",
  商品を選択: "Select a product",
  商品を選択してください: "Please select a product",
  商品名またはSKUで検索: "Search by product name or SKU",
  商品の取得に失敗しました: "Could not load products",
  商品がまだ同期されていません: "Your products have not been synced yet",
  商品数がプランの上限を超えているため表示できません:
    "Cannot display this because your product count exceeds your plan limit",
  このページの商品をすべて選択: "Select every product on this page",
  この商品: "This product",
  SKUなし: "No SKU",
  "(名称未設定)": "(untitled)",
  "（名称不明）": "(unknown)",
  "（商品名なし）": "(no product name)",
  アラートの取得に失敗しました: "Could not load alerts",
  セットアップ状況の取得に失敗しました: "Could not load your setup status",
  プラン情報の取得に失敗しました: "Could not load plan information",

  // ── JSX テキスト由来（2026-07-31 バッチ）─────────────────────
  "1つの原材料は1行にまとめてください。":
    "Keep each component on a single line.",
  "ABC分析（売れ筋ランク）": "ABC analysis (best-seller ranks)",
  LINEで友だち追加: "Add as a LINE friend",
  LINE通知: "LINE alerts",
  "LINE通知はProプラン以上の特典です。プランをアップグレードすると、在庫アラートを LINE の1日ダイジェストで受け取れます。":
    "LINE alerts are available on Pro and above. Upgrade your plan to receive inventory alerts as a once-daily LINE digest.",
  "Shopify の商品情報にバーコード（JAN）を設定してから印刷し直してください。":
    "Set a barcode on the product in Shopify, then print again.",
  "Shopify 純正の注文書は SKU またはバーコード（JAN）で商品を照合します。該当商品は Shopify の商品情報に SKU かバーコードを設定してから再ダウンロードするか、純正の注文書で手動追加してください。":
    "Shopify’s native purchase orders match products by SKU or barcode. Either set a SKU or barcode on those products in Shopify and download again, or add them by hand in the native purchase order.",
  "Stockyから必須データとレポート原本を取得し、正規化と完全性確認を進めています…":
    "Retrieving required data and report originals from Stocky, then normalizing and verifying completeness…",
  "仕入先・仕入用税区分・商品と仕入先の紐付け":
    "Suppliers, purchase tax categories, and product-supplier links",
  "発注書と明細・在庫調整と明細":
    "Purchase orders and lines; stock adjustments and lines",
  もう一度確認: "Check again",
  "UreyukiBox Stocky Migration Chrome拡張が必要です":
    "UreyukiBox Stocky Migration Chrome extension required",
  "在庫同期を実行すると、ここに商品が並び、リードタイムを設定できます。":
    "Run an inventory sync and your products appear here, ready for lead times.",
  "Stockyコピー用キー（API key）": "Stocky copy key (API key)",
  "{name}の季節補正を{state}": "{name} seasonality {state}",
  "。先に在庫同期を実行してから再取り込みしてください。":
    ". Run an inventory sync first, then import again.",
  "「Shopifyの在庫に反映する」がオンのとき、入荷登録した数量だけを Shopify の在庫へ加算します（それ以外の場面で在庫を自動で書き換えることはありません）。入荷実績はリードタイムの統計（安全在庫の精度）にも使われます。":
    "When “Write back to Shopify inventory” is on, only the quantities you record as received are added to your Shopify inventory — nothing else is ever written automatically. Receipts also feed the lead-time statistics that make your safety stock more accurate.",
  "「注文書を作成」で内容を確認し「注文済みとしてマーク」を押すと、ここに並び入荷を記録できるようになります。":
    "Open Create purchase order, check the contents and press Mark as ordered, and the order appears here so you can record its receipt.",
  "「注文済みとしてマーク」を押すと発注が確定し、入荷待ちの数量は次回の発注提案から自動で差し引かれます。入荷の記録は「発注済み・入荷」ページで行います。 Shopify 純正の注文書で入荷・受領まで管理している場合は、「その他の操作」の「Shopify注文書用CSV」で純正へ渡せます（同じ発注を両方に登録すると二重に差し引かれるため、どちらか一方で管理してください）。":
    "Pressing Mark as ordered confirms the order, and the quantity awaiting delivery is deducted from the next reorder suggestion automatically. Record receipts on the Ordered & receiving page. If you track receipts in Shopify’s native purchase orders instead, hand them over with Shopify purchase order CSV under More actions — but record each order in one place only, or it is deducted twice.",
  "このサプライヤーは適格請求書発行事業者として未登録のため、支払う消費税":
    "This supplier is not a registered invoice issuer, so the consumption tax you pay",
  "このストアには拠点が複数あります。どの拠点に入荷したかを選べないと別の拠点の在庫を増やしてしまうため、反映先を選べる Business プランでのみ利用できます。入荷の記録（発注提案からの差し引き）は現在のプランでもそのまま使えます。":
    "This store has more than one location. Without being able to choose which location received the goods, stock could be added to the wrong one — so this is available on the Business plan, where you can pick the destination. Recording receipts (and deducting them from reorder suggestions) works on your current plan.",
  "この商品にはまだ仕入先の条件がありません。下から仕入先を追加してください。":
    "This product has no supplier terms yet. Add a supplier below.",
  "この商品には仕入先の条件がありません。":
    "This product has no supplier terms.",
  "現在のプランでは、保存済みの条件の閲覧と削除のみ利用できます。既定の仕入先の条件は発注書に引き続き適用されます。":
    "On your current plan you can view and delete saved terms only. The preferred supplier's terms still apply to purchase orders.",
  "この履歴は UreyukiBox から行った移動のみです。Shopify 管理画面で直接動かした在庫はここには並びません（在庫数そのものには同期で反映されます）。":
    "This history covers only transfers made in UreyukiBox. Stock moved directly in the Shopify admin does not appear here, although the resulting quantities do arrive via sync.",
  "この履歴は UreyukiBox から行った調整のみです。Shopify 管理画面で直接編集した変動はここには並びません（在庫数そのものには同期で反映されます）。":
    "This history covers only adjustments made in UreyukiBox. Changes made directly in the Shopify admin do not appear here, although the resulting quantities do arrive via sync.",
  "この期間より先（リードタイムが長い分）に":
    "further out than this window (because of the longer lead time)",
  "直近{window}日のうち{days}日は欠品あり（日販はその分低く出ています）":
    "Out of stock on {days} of the last {window} days (daily demand reads low because of it)",
  "なぜこの数字？": "Why this number?",
  "この説明はAIが作成しています。数値は計算結果をそのまま使っています。":
    "This explanation is written by AI. The numbers come straight from the calculation.",
  "この説明は定型文です。": "This is a standard explanation.",
  たまにしか売れない商品として計算:
    "Treat as an intermittently selling product",
  "の列と取り込み先を対応付けてください（推測済みの列は確認だけで進めます）。":
    "Map the CSV columns to their destinations below. Columns we already guessed just need a quick check.",
  "の商品は、在庫と入荷予定（発注済みぶん）が発注点を満たしています。在庫が発注点を割るとここに発注書ドラフトが自動で作られます。今すぐ発注したい場合は、下の「商品を発注書に追加」から任意の商品を追加できます。":
    "products have enough stock and incoming quantity to stay above their reorder points. When stock falls below a reorder point, a draft purchase order appears here automatically. To order something right now, add any product using Add a product to this order below.",
  "の支払いが見込まれます。": "is expected to be due.",
  まとめて割り当て: "Assign in bulk",
  "アップグレードすると解放されます。データの同期は継続しているため、アップグレード後はそのまま最新の内容が表示されます。":
    "Upgrading unlocks it. Your data keeps syncing in the meantime, so everything is up to date the moment you upgrade.",
  カウント中の棚卸し: "Stocktake in progress",
  "この棚卸しを保存したときの商品を確認できません。現在の商品を確認してから、もう一度お試しください。":
    "We cannot verify the product saved with this stocktake. Check the current product, then try again.",
  "保存された棚卸し商品と元の商品情報が一致しません。安全のため在庫へ反映しません。":
    "The saved stocktake item does not match its original product record. Inventory will not be updated.",
  "現在のShopify商品で在庫追跡が無効です。Shopifyで在庫追跡を有効にしてから、もう一度お試しください。":
    "Inventory tracking is off for the current Shopify product. Turn it on in Shopify, then try again.",
  "現在のShopifyで同じ商品だと確認された商品がありません。保存商品で現在の商品を確認してから、もう一度お試しください。":
    "No current Shopify product has been confirmed as the same product. Confirm the current product from Saved products, then try again.",
  "棚卸しを保存したときの商品と、現在の商品を確認した記録が一致しません。安全のため在庫へ反映しません。":
    "The product saved in the stocktake does not match the record that confirms the current product. Inventory will not be updated.",
  "以前に同じ商品だと確認したShopify商品が現在は見つかりません。現在の商品を確認し直してください。":
    "The Shopify product previously confirmed as the same product can no longer be found. Confirm the current product again.",
  "同じ商品だと確認した後にShopify商品が更新されています。現在の商品を確認し直してください。":
    "The Shopify product changed after it was confirmed as the same product. Confirm the current product again.",
  "現在のShopify商品の在庫情報を確認できません。Shopifyの商品設定を確認してください。":
    "We cannot verify inventory information for the current Shopify product. Check the product settings in Shopify.",
  "複数の棚卸し商品が同じShopify商品を指しています。どの実数を使うか確認してください。":
    "Multiple stocktake items point to the same Shopify product. Check which counted quantity should be used.",
  "現在のShopify商品を確認できないため、安全に在庫へ反映できません。":
    "We cannot verify the current Shopify product, so inventory cannot be updated safely.",
  "安全に反映できない理由を確認できません。画面を更新しても解決しない場合は、表示された商品を添えてお問い合わせください。":
    "We cannot verify why this item cannot be updated safely. If refreshing does not fix it, contact us and include the product shown here.",
  "{completed} / {total} 件（内部処理 {chunks} / {allChunks}）":
    "{completed} of {total} items (internal batch {chunks} of {allChunks})",
  この棚卸しはまだ在庫へ反映できません:
    "This stocktake cannot be written to inventory yet",
  "実数 {count}": "Counted quantity: {count}",
  反映内容を再確認: "Check the update again",
  Shopifyへの在庫反映結果を照合してください:
    "Review the Shopify inventory update result",
  "Shopifyへ送信した結果を自動で確定できませんでした。同じ在庫変更を新しく送らず、照合画面でShopifyの実在庫を確認してください。":
    "We could not determine the result sent to Shopify automatically. Do not send the same inventory change again; check Shopify's actual inventory on the review screen.",
  "処理済み: {progress}": "Processed: {progress}",
  在庫反映の照合を開く: "Open inventory update review",
  状態を更新: "Refresh status",
  同じ在庫反映を安全に再開できます:
    "The same inventory update can be resumed safely",
  "Shopifyへの送信は完了し、最終同期を待っています":
    "The Shopify write is complete and awaiting final sync",
  Shopifyへ反映する内容を確認してください:
    "Check what will be written to Shopify",
  "途中まで完了した同じ内容だけを再開します。別の商品や数量を新しく送ることはありません。":
    "Only the same partially completed update will resume. No different product or quantity will be sent as a new change.",
  "Shopifyの実在庫を読み直し、UreyukiBoxの履歴と在庫表示を最終確定します。新しい在庫変更は送りません。":
    "We will read Shopify's actual inventory again and finalize the UreyukiBox history and inventory display. No new inventory change will be sent.",
  "進捗: {progress}": "Progress: {progress}",
  "この操作1回で対象をすべて反映します。UreyukiBoxが内部で分割して続行するため、操作を分けたり繰り返したりする必要はありません。":
    "This single action applies every listed item. UreyukiBox splits and resumes the work internally, so you do not need to divide or repeat the action.",
  "この棚卸しの商品は、現在のShopify商品「{target}」と同じ商品だと確認できています。入力した実数「{count}」を、このShopify商品の在庫へ反映しますか？":
    "This stocktake item has been confirmed as the same product as the current Shopify product “{target}”. Write the counted quantity “{count}” to this Shopify product's inventory?",
  棚卸しを保存したときの商品: "Product saved in the stocktake",
  反映する実数: "Counted quantity to write",
  同じ内容で再開する: "Resume the same update",
  最終同期を再開する: "Resume final sync",
  確認した内容を在庫へ反映する: "Write the confirmed quantities to inventory",
  "確認後に商品または実数が変わりました。更新した内容を確認してから、もう一度実行してください。":
    "A product or counted quantity changed after you reviewed it. Check the updated details, then run it again.",
  "読めない実数を修正してから保存してください。未保存の入力がある間は、在庫へ反映する内容を確認できません。":
    "Fix the unreadable counted quantities and save them first. You cannot review an inventory update while entries remain unsaved.",
  "未保存の実数があります。「入力した実数を保存」後に、在庫へ反映する内容を確認できます。":
    "You have unsaved counted quantities. Save them before reviewing the inventory update.",
  "CSVから保存した実数を読み直しています。最新の表が表示されるまで、在庫への反映は開始できません。":
    "Reloading the counted quantities saved from the CSV. The inventory update cannot start until the latest table is shown.",
  在庫へ反映する内容を確認: "Review inventory update",
  キットを選択: "Choose a kit",
  コピーを開始する: "Start copy",
  "コードをコピーして LINE のトークに貼り付け・送信すると、自動で連携が完了します（このページを再読み込みするとステータスが更新されます）。":
    "Copy the code, paste it into your LINE chat and send it, and the link completes automatically. Reload this page to see the updated status.",
  "サプライヤー 登録番号 未登録": "Supplier registration number not provided",
  サプライヤー情報: "Supplier details",
  "サーバーへの接続に失敗しました。通信環境を確認し、「再試行」を押してください。解消しない場合は時間をおいて再度お試しください。":
    "Could not reach the server. Check your connection and press Retry. If the problem persists, wait a moment and try again.",
  "システム在庫は開始した時点の数で固定されています。カウント中に商品が売れても、適用時には入力した実数がそのまま在庫数になります（増減の足し算ではありません）。":
    "System quantities were frozen when the stocktake started. Even if something sells while you are counting, applying the stocktake sets stock to exactly the counted quantity — it is not added on top.",
  ダッシュボードへ: "Go to dashboard",
  "デッドストック（滞留在庫）": "Dead stock (slow-moving)",
  デフォルトに戻す: "Reset to default",
  データをコピー中: "Import in progress",
  "バーの画像は出さずコード文字列だけを印刷しています（スキャンはできません）。":
    "Only the code text is printed, without the bar image, so these cannot be scanned.",
  "プランが有効になりました。": "Your plan is now active.",
  "プランを反映しています…": "Applying your plan…",
  "プラン以上へのアップグレードが必要です（アップグレード後はそのままお使いいただけます）。":
    "plan or above is required. Once you upgrade, everything works as it is.",
  プレビュー中: "previewing",
  メールアドレスを編集: "Edit email address",
  一括適用: "Apply to all",
  "一部の商品はShopifyに仕入原価が未設定のため、販売価格を目安に評価額を計算しています（「目安」表示）。 Shopify商品の原価（原価管理）を設定すると正確な評価額になります。":
    "Some products have no cost set in Shopify, so their valuation is estimated from the selling price and marked “estimate”. Set the product cost in Shopify to get an exact valuation.",
  "一部の商品はShopifyに仕入原価が未設定のため、販売価格を目安に金額を計算しています（「目安」表示）。仕入単価欄に手入力するか、Shopify商品の原価（原価管理）を設定すると正確な発注金額になります。":
    "Some products have no cost set in Shopify, so amounts are estimated from the selling price and marked “estimate”. Enter a unit cost by hand, or set the product cost in Shopify, for exact order totals.",
  "一部の商品は原価が未設定のため、販売価格を目安に滞留資金を計算しています（「目安」表示）。":
    "Some products have no cost set, so the capital tied up is estimated from the selling price and marked “estimate”.",
  "上のセレクタでキット（組立品）を選び、原材料と数量を設定してください。":
    "Choose a kit in the selector above, then set its components and quantities.",
  "主拠点の商品を一覧にして、いまのシステム在庫を記録します。数え終えて「在庫へ反映する内容を確認」で対象商品と実数を確認し、確定すると Shopify の在庫が実数に置き換わります — Shopify 管理画面で数量を直接編集するのと同じ効果です。同じ棚卸しを両方で行うと二重に反映されるため、どちらか一方で行ってください。":
    "Lists the products at your primary location and records their current system quantities. When you have finished counting, review the target products and counted quantities, then confirm to replace Shopify inventory with those quantities — the same effect as editing quantities in Shopify admin. Run the same stocktake in one place only, or it is applied twice.",
  "今後の改善のため、1問だけご回答ください。":
    "Just one question, to help us improve.",
  "仕入先の条件を取得できませんでした。": "Could not load the supplier terms.",
  仕入先別の条件: "Terms by supplier",
  仕入単価をまとめて保存: "Save all unit costs",
  "件あります。どれかを選んでください。": "found. Please pick one.",
  "保存に失敗しました。時間をおいて、もう一度お試しください。":
    "Could not save. Please wait a moment and try again.",
  入力した実数を保存: "Save counted quantities",
  入力した数量を入荷登録: "Receive the entered quantity",
  入庫を記録する: "Record the receipt",
  "入荷待ちの数量は発注提案から自動で差し引かれます。商品が届いたら「発注済み・入荷」ページで入荷を記録してください。":
    "Quantities awaiting delivery are deducted from reorder suggestions automatically. When the goods arrive, record the receipt on the Ordered & receiving page.",
  再計算: "Recalculate",
  出庫する: "Ship",
  "出庫には Business プランが必要です。下書きの取り消しは現在のプランでも行えます。":
    "Shipping requires the Business plan. You can still cancel this draft on your current plan.",
  "出庫済みです。届いた数量を入力して入庫を記録してください（分けて届いた分だけでも記録できます）。":
    "Already shipped. Enter the quantity that arrived and record the receipt — partial deliveries are fine.",
  "別のタブ、または前回の操作で開始されたコピーが進行中です。このページを閉じても処理は続きます。完了するとこの画面は最初のページに戻ります（取り込んだ内容はダッシュボードでご確認ください）。":
    "Another tab, or an earlier action, already has an import running. It continues even if you close this page. When it finishes this screen returns to the first step — check what was imported on the dashboard.",
  削除: "Delete",
  原材料を追加: "Add a component",
  取り消す: "Undo",
  "取り込んだ数値は「発注点」そのものです。実際にアラートが鳴るラインは、ここにサービスレベル（統計的な安全在庫）と季節係数を加えた値になるため、Stocky と同じ数字を入れても少し大きくなります。内訳は発注点設定の各行で確認できます。":
    "The values you import are the reorder points themselves. The line that actually triggers an alert adds a service level (statistical safety stock) and a seasonal factor on top, so entering the same numbers as Stocky gives a slightly higher figure. You can see the breakdown on each row of the reorder points screen.",
  受領する: "Receive",
  "商品は今回の発注書にだけ一時追加されます。商品の仕入先設定や、次回以降の発注書には影響しません。":
    "products are added to this purchase order only. Your supplier assignments and future purchase orders are unaffected.",
  商品を発注書に追加: "Add a product to this order",

  // 注文書＝貼り付けでまとめて追加（PasteAddProducts）
  貼り付けでまとめて追加: "Add multiple by pasting",
  "表計算ソフトや仕入先の注文書から、SKU・バーコード（JAN）を含む表をそのまま貼り付けると、複数の商品を一度に追加できます。列の順番は問いません。":
    "Paste a table containing SKUs or barcodes (JAN) straight from a spreadsheet or a supplier order form to add multiple products at once. Column order does not matter.",
  "貼り付けから商品コードを読み取れませんでした。SKU または バーコード（JAN）を含めて貼り付けてください。":
    "Could not read any product codes from the paste. Include SKUs or barcodes (JAN) and paste again.",
  "貼り付けが大きすぎます（{count} 値）。一度に突合できるのは {max} 値までです。何回かに分けて貼り付けてください。":
    "The paste is too large ({count} values). Up to {max} values can be matched at once. Paste it in smaller batches.",
  "貼り付けた内容に、この店の商品と一致する SKU・バーコードが見つかりませんでした。Shopify の商品に SKU か バーコードが登録されているか、同期が済んでいるかを確認してください。":
    "None of the pasted values matched a product in this store. Check that your Shopify products have SKUs or barcodes and that the sync has finished.",
  突合できませんでした: "Could not match the paste",
  "{count} 行を商品と突合しました": "Matched {count} rows to products",
  "数量と追加する行をここで直せます。よければ「まとめて追加」を押してください。":
    "You can fix quantities and choose which rows to add here. When it looks right, press “Add products”.",
  "追加できる行がありません。SKU・バーコードの列を含めて貼り付け直してください。":
    "No rows can be added. Paste again with a column of SKUs or barcodes.",
  数量の列: "Quantity column",
  "数量の読み違い（単価などを数量と読んだ場合）は、ここで列を選び直すと全行に反映されます。":
    "If a quantity was misread (for example a unit price read as a quantity), pick a different column here and every row updates.",
  "数量の列なし（すべて 1）": "No quantity column (all quantities 1)",
  "{n} 列目（{header}）": "Column {n} ({header})",
  "{n} 列目": "Column {n}",
  "{code} を追加する": "Add {code}",
  "数量は 1 以上の整数で入力してください": "Enter a whole number of 1 or more",
  すでに明細にあります: "Already on this order",
  "突合できなかった行が {count} 行あります":
    "{count} rows could not be matched",
  "一致する商品が見つからなかった行: {codes}":
    "Rows with no matching product: {codes}",
  "同期がまだか、Shopify の商品に SKU・バーコードが登録されていない可能性があります。":
    "The sync may not have finished yet, or those products may not have a SKU or barcode in Shopify.",
  貼り付け内容を確認: "Check pasted rows",
  "{count} 商品をまとめて追加": "Add {count} products",

  "在庫が発注点を割った商品があると、ここに注文書ドラフトが自動で作られます。まだ発注点を設定していない場合は、先に仕入先ごとのリードタイムと安全在庫日数を入力してください。":
    "When stock falls below a reorder point, a draft purchase order appears here automatically. If you have not set reorder points yet, enter a lead time and safety-stock days for each supplier first.",
  在庫へ反映する: "Write back to inventory",
  "在庫数が1以上の商品が同期されると、ここに評価額の内訳が表示されます。":
    "Once products with stock of one or more are synced, the valuation breakdown appears here.",
  在庫評価額: "Inventory valuation",
  失敗: "Failed",
  寝ている仕入資金の合計: "Total purchasing capital sitting idle",
  対応済みにする: "Mark as handled",
  戻る: "Back",
  手動: "Manual",
  "仕入先別の条件の単価を使用中（変更は「仕入先別の条件」から）":
    "Using the per-supplier price (change it under Supplier terms)",
  "押してください（まだ何も保存していません）。":
    "to continue — nothing has been saved yet.",
  "拠点から拠点へ品物を動かした記録を残します。出庫すると移動元の在庫が減り、入庫すると移動先の在庫が増えます— Shopify 管理画面で両方の拠点の数量を手で直すのと同じ効果です。 Shopify の「転送（Transfer）」は作成しません（作成した転送は途中で取り消せないため）。同じ移動を Shopify の転送でも行うと二重に計上されるため、どちらか一方で管理してください。":
    "Records goods you moved from one location to another. Shipping reduces stock at the source, and receiving increases it at the destination — the same effect as editing both locations’ quantities by hand in the Shopify admin. We do not create a Shopify Transfer, because a transfer in progress cannot be cancelled. Record the same move in one place only, or it is counted twice.",
  既定: "Default",
  既定にする: "Make default",
  明細に追加: "Add to the order",
  "時間をおいて再度お試しください。": "Please wait a moment and try again.",
  未契約: "Not subscribed",
  "未対応アラート（": "Open alerts (",
  "棚卸しの履歴はまだありません。": "No stocktakes yet.",
  棚卸しを中止: "Cancel stocktake",
  棚卸しを開始する: "Start a stocktake",
  棚卸し履歴: "Stocktake history",
  "次に開いたときは元の設定に戻ります。この画面での選択はそのまま有効なので、入荷登録はこのまま進められます。":
    "Next time you open this screen the original setting returns. Your choice here still applies, so you can carry on recording the receipt.",
  次へ: "Next",
  注文書の詳細: "Purchase order details",
  "現在、未対応のアラートはありません。": "There are no open alerts right now.",
  "現在のプランでは発注数量の自動提案は含まれません。各商品の発注数量を入力すると、金額・消費税・合計が自動で計算され、そのまま発注書（PDF/CSV）に出力できます。販売速度から発注数量を自動で提案する「自動注文書」は、スタータープラン以上でご利用いただけます。":
    "Your current plan does not include suggested order quantities. Enter a quantity for each product and the amount, consumption tax and total are calculated for you, ready to export as a purchase order (PDF or CSV). Automatic purchase orders, which suggest quantities from your sales velocity, are available on the Starter plan and above.",
  "現在のプランでは直近7日間の集計です。30日履歴はProで解放されます":
    "Your current plan aggregates the last 7 days. The 30-day history unlocks on Pro",
  発注をキャンセル: "Cancel the order",
  発注元情報を保存しました: "Company profile saved",
  "発注書に出るのは「既定」にした仕入先だけです。他の仕入先の条件は比較用に保存されるだけで、発注数量にも金額にも影響しません。":
    "Only the supplier you mark as Default appears on the purchase order. Terms for other suppliers are kept for comparison and affect neither quantities nor amounts.",
  発注書メール: "Purchase order email",
  "発注書（PDF・CSV）のヘッダに「発注元」として表示される自社情報です。未設定の場合、発注書に発注元が表示されません。登録番号は自社が適格請求書発行事業者の場合のみ入力してください。":
    "Your company details, shown as the buyer in the header of purchase orders (PDF and CSV). If you leave this blank, no buyer appears on the purchase order. Enter a registration number only if your business is a registered invoice issuer.",
  "発注書（PDF・CSV）のヘッダに「発注元」として表示される自社情報です。未設定の場合、発注書に発注元が表示されません。":
    "Your company details, shown as the buyer in the header of purchase orders (PDF and CSV). If you leave this blank, no buyer appears on the purchase order.",
  "発注点が未設定の商品は、発注が必要かどうかをアプリが判断できないため数量を空欄のまま並べています（在庫が足りているという意味ではありません）。数量は手入力できます。発注点を設定すると、その商品は自動提案の対象になります。":
    "Products with no reorder point are listed with an empty quantity, because the app cannot tell whether they need ordering — it does not mean stock is sufficient. You can enter quantities by hand. Once a reorder point is set, the product is included in automatic suggestions.",
  "発注点を割った商品が無いか、仕入先の割当・原価が未設定の可能性があります。":
    "Either nothing has fallen below its reorder point, or suppliers and costs have not been assigned yet.",
  目安: "estimate",
  "直近の販売実績がある商品が同期されると、ここにABCランクが表示されます。":
    "Once products with recent sales are synced, their ABC ranks appear here.",
  "承認済みの{count}商品だけ、直近の販売速度を{multiplier}倍して発注点と推奨数量を計算しています。未承認の商品は1.00倍のままです。":
    "Only the {count} approved products use {multiplier} times recent sales velocity for reorder points and recommended quantities. Unapproved products remain at 1.00x.",
  "破損・ロスなどの在庫の増減を理由つきで記録します。登録すると Shopify の在庫（主拠点）に即時反映されます — Shopify 管理画面で数量を直接編集するのと同じ効果です。同じ調整を両方で行うと二重に増減するため、どちらか一方で行ってください。":
    "Records stock increases and decreases — damage, loss and so on — with a reason. Saving one applies it to your Shopify inventory at the primary location immediately, the same effect as editing the quantity in the Shopify admin. Record the same adjustment in one place only, or it is applied twice.",
  移動の履歴: "Transfer history",
  "移動の履歴はまだありません。": "No transfers yet.",
  移動をつくる: "Create a transfer",
  移動を作成: "Create transfer",
  "商品を検索（SKU・商品名・Shopify バリアントID）":
    "Search products (SKU, product name or Shopify variant ID)",
  在庫調整する商品候補: "Products to adjust",
  商品検索に失敗しました: "Could not search products",
  指定された商品を選択できませんでした:
    "The requested product could not be selected",
  指定された商品を確認できませんでした:
    "Could not verify the requested product",
  "URLの商品IDが正しくありません。在庫調整する商品を検索して選んでください。":
    "The product ID in the URL is invalid. Search for and select the product to adjust.",
  "指定された商品は現在のShopify商品に見つからないか、このストアの商品ではありません。商品を検索して選んでください。":
    "The requested product is not in the current Shopify catalogue or does not belong to this store. Search for and select a product.",
  "全商品をサーバーから検索します。候補は最大100件です。見つからない場合は SKU またはバリアントIDを入力してください。":
    "Searches the full catalogue on the server and shows up to 100 matches. If you cannot find a product, enter its SKU or variant ID.",
  編集: "Edit",
  発注メール: "Purchase-order email",
  "発注メール（要確認）": "Purchase-order email (review required)",
  発注メールを確認して送る: "Review and send purchase-order email",
  仕入先メールを登録: "Add supplier email",
  "発注書のメール送信は Starter プラン以上で利用できます。":
    "Purchase-order email is available on Starter and higher plans.",
  "仕入先のメールアドレスを登録すると、発注内容を確認して送信できます。":
    "Add the supplier email address to review and send the purchase order.",
  "宛先・件名・本文を確認してから送信します。":
    "Review the recipient, subject, and full message before sending.",
  設定を保存しました: "Settings saved",
  設定済みのキット一覧: "Configured kits",
  "該当する未割当の商品はありません。":
    "There are no matching unassigned products.",
  読み取り: "Read",
  調整を記録する: "Record an adjustment",
  調整履歴: "Adjustment history",
  "調整履歴はまだありません。": "No adjustments yet.",
  費用サマリー: "Cost summary",
  "送信に失敗しました。お手数ですがもう一度お試しください。":
    "Could not send. Sorry — please try again.",
  "通常 1〜10 分ほどで完了します。このページを閉じても処理は続くので、開き直して確認できます。":
    "Usually finishes in 1–10 minutes. It continues even if you close this page, so you can come back and check.",
  "通常期（1.00倍）をプレビュー中": "Previewing the normal season (×1.00)",
  連携を解除: "Unlink",
  適格請求書発行事業者: "Registered invoice issuer",
  選択を解除: "Clear selection",
  "）を基準に判定しています。": ") as the basis.",

  // ── ラベル定数・選択肢・プランゲート ────────────────────────
  下書き: "Draft",
  入荷待ち: "Awaiting delivery",
  一部入荷: "Partially received",
  入荷済み: "Received",
  キャンセル: "Cancel",
  移動中: "In transit",
  受領済み: "Received",
  取り消し: "Cancelled",
  カウント中: "Counting",
  適用済み: "Applied",
  中止: "Cancelled",
  破損: "Damaged",
  "ロス（紛失・盗難）": "Shrinkage (lost or stolen)",
  記録の訂正: "Record correction",
  "検品・品質保留": "Inspection or quality hold",
  その他: "Other",
  在庫低下: "Low stock",
  在庫切れ: "Out of stock",
  "Shopify App Store で見つけた": "Found it on the Shopify App Store",
  "Google などの検索": "A search engine such as Google",
  "ブログ・記事・ニュース・SNS": "A blog, article, news story or social post",
  "知人・取引先・パートナーの紹介":
    "Referred by a contact, supplier or partner",
  発注書: "Purchase orders",
  この機能は上位プランの特典です: "This feature is part of a higher plan",
  "この機能は{plan}プラン以上でご利用いただけます。":
    "This feature is available on the {plan} plan and above.",
  "{name}は{plan}プラン以上の機能です":
    "{name} is available on the {plan} plan and above",
  "{name}のプレビュー": "{name} preview",
  データ件数を確認しています: "Checking how much data is available",
  データ件数を確認できませんでした: "Could not check the available data",
  "{name}: {count}件": "{name}: {count}",
  "{name}を表示するためのプランを見る": "View plans to show {name}",
  表示する: "Show",
  プラン情報を確認中: "Checking plan access",
  プラン情報を取得できませんでした: "Could not load plan information",
  "0以上の整数で入力": "Enter a whole number of 0 or more",
  "標準（90%）": "Standard (90%)",
  "厚め（95%）": "Thicker (95%)",
  "かなり厚め（99%）": "Much thicker (99%)",
  安全在庫の厚さ: "Safety stock buffer",
  "売れ行きのばらつきに、どれだけ備えるか。厚くすると発注量が増えます。":
    "How much to buffer against variability in sales. A thicker buffer increases order quantities.",
  売れ行きの実績が少ない商品の見積もり:
    "Estimating products with little sales history",
  "少なめに見る — 在庫を抱えたくない":
    "Estimate low — avoid tying up cash in stock",
  "実績どおり — 直近の売れ行きをそのまま使う":
    "As observed — use recent sales as-is",
  "多めに見る — 欠品を出したくない": "Estimate high — avoid running out",
  "実績が数日しかない商品ほど、この設定の影響が大きくなります。30日分たまると、どの設定でもほぼ同じ数字になります。":
    "This setting matters most for products with only a few days of history. Once 30 days accumulate, every option lands on almost the same number.",
  現在の設定を取得できませんでした: "Could not load the current setting",
  "現在値を確認できないため、誤って上書きしないよう変更操作を停止しています。":
    "Changes are disabled until the current value can be confirmed, preventing an accidental overwrite.",
  設定の保存結果を確認できませんでした:
    "Could not confirm whether the setting was saved",
  "通信状況によっては保存済みの可能性があります。最新の設定を再取得しています。表示を確認してから、必要な場合だけ選び直してください。":
    "The setting may already have been saved despite the connection problem. We are loading the latest setting. Check it before selecting again.",
  設定の適用結果を確認できませんでした:
    "Could not confirm whether the settings were applied",
  "通信状況によっては適用済みの可能性があります。最新の状態を再取得しています。チェック状態を確認してから、必要な場合だけもう一度お試しください。":
    "The settings may already have been applied despite the connection problem. We are loading the latest status. Check the selections before trying again.",

  // ── 差し込み付きの文・帳票（2026-07-31 バッチ2）───────────────
  " — 在庫 {n}": " — stock {n}",
  " − 発注済み（未入荷） {n}": " − on order (not yet received) {n}",
  "PC・別の端末の方: QRコードを LINE で読み取り":
    "On a computer or another device: scan the QR code with LINE",
  Shopifyの在庫に反映する: "Write back to Shopify inventory",
  "Shopifyの在庫に反映する（{location}）":
    "Write back to Shopify inventory ({location})",
  Shopify注文書: "Shopify-purchase-order",
  "{amount}（目安）": "{amount} (est.)",
  "{count}行はSKU・バーコード未設定のため、Shopify注文書用CSVに含まれていません":
    "{count} lines have no SKU or barcode, so they are not included in the Shopify purchase order CSV",
  "{date}の週〜": "week of {date} onwards",
  "{date}以降は控除割合が{rate}%へ引き下げられます。":
    "From {date} the deduction rate drops to {rate}%.",
  "{item} の入荷数": "Received quantity for {item}",
  "{item} の受領数（残り {n}）":
    "Quantity received for {item} ({n} outstanding)",
  "{item} の在庫を {sign} 調整します。": "Adjust stock for {item} by {sign}.",
  "{item} の実数": "Counted quantity for {item}",
  "{location} の在庫から移動数量を差し引きます。Shopify の在庫に即時反映されます。よろしいですか？":
    "This deducts the transfer quantity from stock at {location}. It is applied to your Shopify inventory immediately. Continue?",
  "{n} 件": "{n}",
  "{n} 行を検出しました。CSV": "Found {n} rows. CSV",
  "{n}件の商品はJAN（13桁・8桁）として読めないコードのため、":
    "{n} products have a code that cannot be read as a barcode (13 or 8 digits),",
  "{n}件の商品はバーコード未設定のため、ラベルを出していません。":
    "{n} products have no barcode, so no label was produced for them.",
  "{n}件を選択中": "{n} selected",
  "{n}日前": "{n} days ago",
  "{n}週間": "{n} weeks",
  "{plan} プランへアップグレード": "Upgrade to {plan}",
  "{plan}（トライアル中）": "{plan} (trial)",
  "{poNumber} の入荷をスキャン": "Scan receipts for {poNumber}",
  "{poNumber} をキャンセルしますか？（発注提案の差し引きから外れます）":
    "Cancel {poNumber}? It will no longer be deducted from reorder suggestions.",

  // --- 確定後の発注書編集（PlacedOrderEditPanel） ---------------------------
  // ⚠️ 追加前に既存キーを grep する（重複キーは tsc の TS1117 だけが捕まえる）。
  内容を修正: "Edit order",
  修正を終了: "Done editing",
  入荷予定日: "Expected arrival",
  "追加する商品（バーコード／SKU）": "Add a product (barcode / SKU)",
  "読み取った商品を数量1で明細に追加します。数量は追加後に発注数の欄で直してください。":
    "The scanned product is added as a line with quantity 1. Adjust the quantity in the ordered-qty field afterwards.",
  "{item} は既にこの発注書にあります。数量欄で増やしてください。":
    "{item} is already on this purchase order. Increase its quantity instead.",
  "{item} をこの発注書から削除しますか？":
    "Remove {item} from this purchase order?",
  "{item} の発注数": "Ordered quantity for {item}",
  "{item} の仕入単価": "Unit cost for {item}",
  個: " units",
  "入荷済み {received}": "Received {received}",
  行を削除: "Remove line",
  "発注数は1以上の整数で入力してください。":
    "Enter the ordered quantity as a whole number of 1 or more.",
  "発注数を入荷済みの数量より小さくできません（{item}: 入荷済み {received}）。":
    "The ordered quantity cannot be lower than what has already been received ({item}: received {received}).",
  "仕入単価は0以上の数値で入力してください。":
    "Enter the unit cost as a number of 0 or more.",
  "発注数は入荷済みの数量より下げられません（入荷済みちょうどまで下げると発注書は入荷済みとして締まります）。行を削除できるのは未入荷の明細だけで、最後の1行は削除できません。単価の変更は諸掛の配賦（実原価）にもそのまま反映されます。":
    "The ordered quantity cannot go below what has already been received (cutting it to exactly the received quantity closes the purchase order as received). Only lines with no receipts can be removed, and the last remaining line cannot be removed. Unit-cost changes also flow through to the landed-cost allocation.",
  "{supplier}　御中": "To {supplier}",
  "{target} − 在庫 {available} − 入荷予定 {incoming}{ordered} = {qty}":
    "{target} − stock {available} − incoming {incoming}{ordered} = {qty}",
  "{target} − 在庫 {available} − 入荷予定 {incoming}{ordered} = {raw}{moq}{pack} = {qty}":
    "{target} − stock {available} − incoming {incoming}{ordered} = {raw}{moq}{pack} = {qty}",
  " → 最低発注数量（MOQ）{n} に引き上げ":
    " → raised to the minimum order quantity (MOQ) of {n}",
  " → 入数 {n} の倍数に切り上げ":
    " → rounded up to a multiple of the pack size {n}",
  "{year}年{month}月{day}日": "{year}-{month}-{day}",
  "「{term}」に一致する商品はありません。": "No products match “{term}”.",
  "うち統計的安全在庫 +{n}": "of which statistical safety stock +{n}",
  "この内容で発注を確定します（{items}品目・合計{qty}点）。":
    "This confirms the order ({items} items, {qty} units in total).",
  "この商品は既に全量入荷済みです（{item}）。":
    "This product has already been received in full ({item}).",
  "この棚卸しの対象に含まれていない商品です（{item}）。棚卸しを中止して開始し直すと最新の商品が対象になります。":
    "This product is not part of this stocktake ({item}). Cancel the stocktake and start again to pick up the latest products.",
  "この発注書に含まれていない商品です（{item}）。":
    "This product is not on this purchase order ({item}).",
  "ほか {count} 件。検索語を増やすと絞り込めます。":
    "And {count} more. Add more search terms to narrow it down.",
  "ステップ1: 公式アカウント（Bot）を友だち追加":
    "Step 1: add the official account (bot) as a friend",
  "ステップ2: トークで連携コードを送信":
    "Step 2: send the link code in the chat",
  "スマホの方: ボタンをタップすると LINE が開きます":
    "On a phone: tapping the button opens LINE",
  "データの同期は継続しています。機能のご利用には {plan}":
    "Your data keeps syncing. To use this feature you need",
  バーコードラベル: "Barcode labels",
  "バーコードラベル {n}枚を印刷用に開きました":
    "Opened {n} barcode labels for printing",
  "バーコードラベルを印刷（{n}枚）": "Print barcode labels ({n})",
  入荷数: "Qty received",
  "入荷した数量を入力すると押せます。":
    "Enter the quantities you received to enable this button.",
  "{count} 件の入荷数が数値として読めません（整数で入力してください）。このままだと残りの明細だけが登録されます。":
    "{count} received quantities could not be read as numbers (enter whole numbers). As is, only the remaining lines will be recorded.",
  "{count} 件の実数が数値として読めないため保存できません。整数で入力してください（例: 1200）。":
    "{count} counted quantities could not be read as numbers, so they cannot be saved. Enter whole numbers (for example, 1200).",
  残りを全部入荷: "Receive all remaining",
  "適用期間: {start}〜{end}": "Applies {start} to {end}",
  AI候補は自動適用されません: "AI suggestions are not applied automatically",
  "ストアの国: {country}": "Store country: {country}",
  "「確認が必要」のイベントで、実際に売上が伸びる商品を1件以上選んでください。商品を選ぶと適用操作が表示されます。":
    'For events marked "Needs review", select at least one product that genuinely sees higher sales. The apply action appears after you make a selection.',
  "適用した商品だけが、表示された期間中の発注数量で季節係数の対象になります。":
    "Only the products you apply are adjusted in purchase-order quantities, and only during the dates shown.",
  確認が必要: "Needs review",
  "確認が必要なイベントはありません。": "There are no events to review.",
  "適用済み・季節補正なし・対象商品なしを閉じる":
    "Hide applied, no-adjustment, and no-product events",
  "適用済み・季節補正なし・対象商品なし（{n}件）":
    "Applied, no-adjustment, or no-product events ({n})",
  対象商品なし: "No products",
  "確認が必要（1候補）": "Needs review (1 candidate)",
  "確認が必要（{n}候補）": "Needs review ({n} candidates)",
  "適用済み（1商品）": "Applied (1 product)",
  "適用済み（{n}商品）": "Applied ({n} products)",
  季節補正なし: "No seasonal adjustment",
  "すべてのチェックを外しています。保存済みの季節補正を解除する場合は、明示的に確定してください。":
    "All products are unchecked. Explicitly confirm below if you want to remove the saved seasonal adjustment.",
  このイベントの適用を解除: "Remove this event adjustment",
  "このイベントの季節補正を解除しますか？": "Remove this event adjustment?",
  季節補正を解除する: "Remove seasonal adjustment",
  解除しない: "Keep adjustment",
  "{name}で現在適用中の{n}商品から季節係数を外します。候補商品は削除されません。":
    "This removes the seasonal multiplier from the {n} products currently applied for {name}. Candidate products are not deleted.",
  変更を保存: "Save changes",
  選んだ1商品に適用: "Apply to 1 selected product",
  "選んだ{n}商品に適用": "Apply to {n} selected products",
  どの商品にも結び付けられなかった数量があります:
    "Some quantities could not be matched to any product",
  "Stocky 側で SKU が未設定の商品があると、その行は数量だけになり取り込めません。Shopify に SKU を設定してから貼り直してください。":
    "Products with no SKU in Stocky come through as a quantity with nothing to match. Set a SKU on the product in Shopify, then paste again.",
  "発注点を手動で固定しているため、係数は発注数量に影響しません":
    "Reorder point is set manually, so the multiplier will not affect the order quantity",
  発行済み注文書あり: "In a placed purchase order",
  "すでに発行済みの注文書に入っています。いま適用しても、その注文書の数量は変わりません（次に作る注文書から効きます）。":
    "Already included in a placed purchase order. Applying the multiplier now will not change that order's quantities (it applies from the next purchase order).",
  "選んだ商品のうち、数量が変わらないものがあります":
    "Some of the products you picked will not change quantity",
  "{names} は発注点を手動で固定しているため、係数を適用しても発注数量は変わりません。":
    "{names} have a manually fixed reorder point, so applying the multiplier will not change their order quantity.",
  "「発注点設定」でこれらの商品の発注点を空にすると、販売速度から自動計算されるようになり、季節係数が数量に反映されます。":
    "Clear the reorder point for those products on the Reorder points page and it will be calculated from sales velocity instead — then the seasonal multiplier reaches the quantity.",
  提案を作り直す: "Rebuild suggestions",
  発注書を検索: "Search purchase orders",
  明細の並び順: "Sort line items",
  "既定（並べ替えない）": "Default (no sorting)",
  SKU順: "By SKU",
  商品名順: "By product name",
  数量の多い順: "By quantity (highest first)",
  "仕入先の請求書と同じ並びにすると、行を1つずつ突き合わせられます。印刷するラベルの順番も揃います。":
    "Match your supplier’s invoice order to reconcile line by line. Printed labels follow the same order.",
  "仕入先の請求書と同じ並びにすると、行を1つずつ突き合わせられます。発注書のPDF・CSV・バーコードラベルもこの並びで出ます。":
    "Match your supplier’s invoice order to reconcile line by line. The purchase order PDF, CSV and barcode labels all follow this order.",
  "PO番号・仕入先名・商品名・SKU": "PO number, supplier, product or SKU",
  "入力した語を含む発注書を、この画面に出ていない古いものからも探します。":
    "Searches every purchase order, including older ones that are not shown on this page.",
  "「{q}」に一致する発注書はありませんでした":
    "No purchase orders matched “{q}”",
  検索を消す: "Clear search",
  "PO番号・仕入先名・商品名・SKU の一部で探せます。まだ「注文済みとしてマーク」していない発注書はこの画面に並びません。":
    "You can search by part of a PO number, supplier, product name or SKU. Purchase orders you have not yet marked as ordered do not appear on this page.",
  "この発注書の商品はバーコード（JAN）が未設定のため、ラベルを印刷できません。":
    "The products on this purchase order have no barcode, so no labels can be printed.",
  "バーコードラベルは、入荷数を入力していればその数量、未入力なら残数（入荷済みの発注書は発注数）ぶんを印刷します。":
    "Barcode labels are printed for the quantity you entered, or for the outstanding quantity when nothing is entered (the ordered quantity once a purchase order is fully received).",
  バーコード画像なし: "No barcode image",
  "プランの反映に失敗しました:": "Could not apply your plan:",
  "Shopify でプランの有効化をまだ確認できません。少し待ってから再試行してください。":
    "Shopify has not confirmed that your plan is active yet. Wait a moment, then retry.",
  "一致する商品が見つからない SKU: {n} 件（この行は取り込まれません）。":
    "SKUs with no matching product: {n} — those rows are not imported.",
  "一致する商品が見つからなかった SKU（{n} 件）: {skus}":
    "SKUs with no matching product ({n}): {skus}",
  "今後{weeks}週間で必要な仕入現金の合計":
    "Total purchasing cash needed over the next {weeks} weeks",
  "仕入先 登録番号 未登録": "Supplier registration number not provided",
  "仕入先が未割当の商品が{n}件あります":
    "{n} products have no supplier assigned",
  "仕入先別の条件 — {title}": "Terms by supplier — {title}",
  "保存に失敗しました:": "Could not save:",
  "原価（手入力 > Shopify原価 > 販売価格の順）× 在庫数の合計です。":
    "The sum of cost (manual entry > Shopify cost > selling price, in that order) × quantity on hand.",
  "同じコードの商品が {n}": "{n} products share this code",
  "同期に失敗しました:": "Sync failed:",
  "同期が完了しました。売れ筋とアラートを再計算しました。":
    "Sync complete. Best sellers and alerts have been recalculated.",
  "商品数（{count}件）がプランの上限（{limit}件）を超えています":
    "Your product count ({count}) exceeds your plan limit ({limit})",
  "在庫（全拠点合算） {stock} ・ 日販 {demand}":
    "stock (all locations) {stock} · {demand}/day",
  "売れ筋 TOP {n}（直近 {days} 日）": "Top {n} sellers (last {days} days)",
  "発注点は新規 {created} 件・上書き {updated} 件、仕入先別リードタイムは新規 {leadCreated} 件・上書き {leadUpdated} 件です。よろしければ「上書きして取り込む」を":
    "Reorder points: {created} new and {updated} overwritten; supplier lead times: {leadCreated} new and {leadUpdated} overwritten. If that looks right, press Overwrite and import",
  "既存の設定 {n} 件を上書きします": "Overwrites {n} existing settings",
  "同じ SKU の商品が複数あるため取り込まない行: {n} 件。":
    "Rows skipped because the SKU matches multiple products: {n}.",
  "一致する仕入先が見つからない Vendor: {n} 件（この行は取り込まれません）。":
    "Vendors with no matching supplier: {n}; those rows are not imported.",
  "同じ名前の仕入先が複数ある Vendor: {n} 件（この行は取り込まれません）。":
    "Vendors matching multiple suppliers: {n}; those rows are not imported.",
  "一致した商品 {matched} 件": "Matching products: {matched}",
  "同じ SKU の商品が複数あり特定できなかった行（{n} 件）: {values}":
    "Rows not resolved because the SKU matches multiple products ({n}): {values}",
  "一致する仕入先が見つからなかった Vendor（{n} 件）: {values}":
    "Vendors with no matching supplier ({n}): {values}",
  "同じ名前の仕入先が複数あり特定できなかった Vendor（{n} 件）: {values}":
    "Vendors not resolved because multiple suppliers share the name ({n}): {values}",
  "読み飛ばした行（{n} 件）:": "Rows skipped ({n}):",
  "現在 {current} 個（しきい値 {threshold}）":
    "currently {current} units (threshold {threshold})",
  "現在の在庫であと {n} 個組み立てられます。":
    "You can assemble {n} more from the stock you hold.",
  "発注元 未設定": "No buyer set",
  "発注書 {poNumber} を注文済みにしました":
    "Purchase order {poNumber} is marked as ordered",
  "発注点 {n}": "reorder point {n}",
  "発注点 {point}": "reorder point {point}",
  "登録済み {n} 件": "{n} registered",
  登録番号: "Registration number",
  "直近 {days} 日間に売れた商品はありません（同期は完了しています）。":
    "No products sold in the last {days} days (the sync has finished).",
  "まだ同期していません。上の「今すぐ同期」を押すと、商品と注文を読み込みます。":
    "Nothing has been synced yet. Press “Sync now” above to pull in your products and orders.",
  "直近{days}日で、在庫が残ったまま一度も売れていない商品は見つかりませんでした。":
    "No products were left in stock without a single sale in the last {days} days.",
  "直近{days}日まったく売れていないのに在庫が残っている商品です（寝ている仕入資金の大きい順）。":
    "Products that have not sold at all in the last {days} days but are still in stock, ordered by the capital tied up.",
  "補充目標 {n}": "replenish-to {n}",
  "補充目標 {target} = 発注点 {point} + {uplift}（補充日数ぶんの想定販売数）":
    "replenish-to {target} = reorder point {point} + {uplift} (expected sales over the cover period)",
  "補充目標 {target} まで発注": "order up to {target}",
  "要発注（発注点 {point}）": "Reorder needed (reorder point {point})",
  "解除に失敗しました:": "Could not unlink:",
  "読み取り: {code}": "Scanned: {code}",
  "読み飛ばす行: {n} 件。": "Rows to skip: {n}.",
  販売実績なし: "No sales history",
  "（目安）": "(est.)",
  "／ 在庫合計: {n} 個": "/ total stock: {n} units",
  "※「目安」表示の仕入単価は、Shopifyに仕入原価が未設定のため販売価格を暫定使用しています。":
    "* Unit costs marked “est.” fall back to the selling price, because no cost is set on the product in Shopify.",
  "※仕入先は適格請求書発行事業者として未登録のため、仕入税額控除は経過措置の対象です。支払消費税 {total} のうち控除対象は{rate}%（{deductible}）、控除対象外 {nonDeductible}（発行日 {date} 基準）。":
    "* This supplier is not a registered invoice issuer, so input tax credit falls under Japan’s transitional rules. Of the {total} consumption tax paid, {rate}% ({deductible}) is deductible and {nonDeductible} is not, based on the issue date {date}.",
  "このサプライヤーは適格請求書発行事業者として未登録のため、支払う消費税 {total} のうち仕入税額控除できるのは {rate}%（{deductible}）までで、残り {nonDeductible} は控除の対象外です。":
    "This supplier is not a registered invoice issuer, so of the {total} consumption tax you pay, only {rate}% ({deductible}) is deductible as input tax — the remaining {nonDeductible} is not.",
  "控除割合は注文書の発行日（{date}）を基準に判定しています。":
    "The deduction rate is determined by the purchase order’s issue date ({date}).",
  "最終同期: {at}": "Last synced: {at}",
  "売上数量（直近 {days} 日）": "Units sold (last {days} days)",
  "発注 {ordered} ／ 入荷済み {received} ／ 残 {remaining}":
    "Ordered {ordered} · received {received} · outstanding {remaining}",
  "・": "• ",

  // --- 諸掛と実原価（parity v2 Step 7・LandedCostPanel） -------------------
  // ⚠️ 追加する前に必ず既存キーを grep する。重複キーは vitest では黙って後勝ちに
  //    なり、`npx tsc --noEmit` の TS1117 だけが捕まえる（2回踏んだ罠）。
  // ⚠️ 下の5語は `t(KIND_LABEL[...])` のように **変数経由**で引くので
  //    i18nCoverage の検査に出てこない。消すと英語UIで日本語が残る。
  "送料・輸送費": "Shipping / freight",
  "関税・輸入諸税": "Duties / import taxes",
  "その他（検品料・手数料など）": "Other (inspection, handling, etc.)",
  金額比で配分: "Allocate by value",
  数量比で配分: "Allocate by quantity",
  諸掛: "Additional costs",
  諸掛と実原価: "Additional costs & landed cost",
  諸掛と実原価を隠す: "Hide additional costs & landed cost",
  "{poNumber} の諸掛と実原価": "Additional costs & landed cost for {poNumber}",
  諸掛を配分できません: "These additional costs cannot be allocated",
  "下の一覧から配分方法を変えるか、その諸掛を削除してください。":
    "Change the allocation method below, or delete that cost.",
  種類: "Type",
  配分方法: "Allocation",
  諸掛を追加: "Add cost",
  "諸掛はまだ登録されていません。送料や関税を足すと、明細ごとの実原価を計算します。":
    "No additional costs yet. Add shipping or duties to see the landed cost per line.",
  商品計: "Goods",
  諸掛計: "Additional costs",
  実原価合計: "Landed total",
  計算できません: "Cannot be calculated",
  発注数量: "Ordered qty",
  明細金額: "Line amount",
  配分された諸掛: "Allocated cost",
  "実原価（1個あたり）": "Landed unit cost",
  "実原価は表示のみで、商品の原価には書き込みません。配分は発注数量で行うため、分納しても1個あたりの実原価は変わりません。":
    "Landed cost is shown for reference only — it is never written back to the product cost. Allocation uses the ordered quantity, so partial receipts do not change the landed unit cost.",
  // 容積比（CBM）按分。"容積比で配分（CBM）" は ALLOCATION_LABEL 経由＝変数参照
  // なので coverage 検査に出ないが、消すと英語UIで日本語が残る（上の5語と同じ）。
  "容積比で配分（CBM）": "Allocate by volume (CBM)",
  "容積（m³/個）": "Volume (m³/unit)",
  "{item} の容積（m³）": "Volume for {item} (m³)",
  "容積は1個あたりの m³（CBM）で入力します。容積比で配分するには、すべての明細に容積が必要です（空欄の明細があると配分できません）。":
    "Enter volume as m³ (CBM) per unit. Allocating by volume requires a volume on every line — allocation stops if any line is blank.",
  "容積は0以上の数値で入力してください（m³）。":
    "Enter the volume as a number of 0 or more (m³).",
  未入力: "Not set",

  // --- レポート（parity v2 Step 8・Reports.tsx） ---------------------------
  // ⚠️ 追加前に既存キーを grep する（重複キーは tsc の TS1117 だけが捕まえる）。
  レポート: "Reports",
  "利益と仕入先の実績を期間で見ます。":
    "Profit and supplier performance over a date range.",
  開始日: "From",
  終了日: "To",
  この期間で表示: "Show this range",
  "終了日は開始日以降を指定してください。":
    "The end date must not be before the start date.",
  利益レポート: "Profit report",
  "利益（商品別）": "Profit by product",
  原価: "Cost",
  粗利: "Gross profit",
  粗利率: "Margin",
  "{n} 件の明細を集計から除外しました":
    "{n} line items were excluded from these totals",
  原価を設定する: "Set product costs",
  "原価が未設定の商品: {n} 件。原価を入れないと粗利が実態より小さく出るため、集計に混ぜていません。":
    "Products with no cost set: {n}. Without a cost the gross profit would look smaller than it really is, so they are left out of the totals.",
  "売上がまだ同期されていない明細: {n} 件。次回の同期で集計に入ります。":
    "Line items whose revenue has not synced yet: {n}. They will be included after the next sync.",
  この期間に集計できる売上がありません: "No sales to report for this range",
  "この期間の明細はすべて集計対象外でした（原価未設定・売上が未同期）。原価を設定すると粗利が出ます。":
    "Every line item in this range was left out of the totals (no cost set, or revenue not synced yet). Set a cost to see gross profit.",
  "この期間に売れた商品がありません。期間を広げるか、同期が終わっているかをご確認ください。":
    "Nothing sold in this range. Try a wider range, or check that the sync has finished.",
  "上位 {shown} 件を表示しています（対象 {total} 件）。上の合計は表に出ていない商品も含めた全体の金額です。":
    "Showing the top {shown} of {total} products. The totals above cover every product, including the ones not listed here.",
  "原価は現在の設定値を使っています（Shopify は注文明細に仕入原価を持たないため）。原価を後から変えると過去の粗利も変わります。":
    "Costs come from the product’s current setting, because Shopify does not store a unit cost on the order line. Changing a cost later also changes past gross profit.",
  仕入先レポート: "Supplier report",
  この期間に確定した発注がありません:
    "No purchase orders were placed in this range",
  "このレポートは「注文済みとしてマーク」した発注書だけを集計します。下書きのままの発注書は含まれません。":
    "This report only counts purchase orders you marked as placed. Drafts are not included.",
  注文書を開く: "Open purchase orders",
  発注件数: "Orders",
  発注金額: "Ordered value",
  平均リードタイム: "Avg. lead time",
  納期どおり: "On time",
  "（仕入先の指定なし）": "(no supplier set)",
  "{n} 日": "{n} days",
  "平均リードタイムは発注確定から最初の入荷までの日数です。入荷の実績が無い仕入先は「—」と表示します（0日とは書きません）。":
    "Average lead time counts the days from placing the order to the first receipt. Suppliers with no receipts yet show “—” rather than 0 days.",
  // ダッシュボードのアラート → 発注（在庫低下の次の一手を画面から切らさない）
  "{name} に発注する": "Order from {name}",
  "「{name}」に今必要な発注はありません":
    "Nothing to order from “{name}” right now",
  指定された仕入先: "the selected supplier",
  "在庫と入荷予定が発注点を満たしているため、この仕入先の発注書ドラフトは作られていません（アラートを見たあとに入荷・売れ行きが変わった可能性があります）。今すぐ発注する場合は、下の「商品を発注書に追加」から追加できます。":
    "Stock plus incoming units already meet the reorder point, so no draft was created for this supplier (receipts or sales may have moved since the alert). To order anyway, use “Add products to this purchase order” below.",

  // 発注点設定の絞り込み・表示上限
  "発注点が未設定の商品だけ（{n}件）":
    "Only products with no reorder point ({n})",
  "{shown} / {total} 件を表示中": "Showing {shown} of {total}",
  "発注点が未設定の商品は、発注が必要かどうかをアプリが判断できず、注文書の自動提案にも並びません。リードタイムと在庫日数の目標を入れると自動計算が始まります。":
    "For products with no reorder point the app cannot tell whether an order is needed, so they never appear in the automatic suggestions. Enter a lead time and a days-of-supply target to start the calculation.",
  条件に一致する商品はありません: "No products match these filters",
  絞り込みを解除: "Clear filters",
  "発注点が未設定の商品はありません（すべての商品で自動計算が有効です）。":
    "Every product has a reorder point (the automatic calculation is active for all of them).",
  "商品名・SKU の一部で探せます。":
    "Search by part of the product name or SKU.",
  "{n} 件（一括適用はこの仕入先の全商品に効きます）":
    "{n} products (bulk apply affects every product of this supplier)",
  "さらに表示（残り {n} 件）": "Show more ({n} left)",

  // セット商品の在庫＝未同期と「まだ作っていない」を区別する
  "在庫同期を実行すると、上のセレクタでキット（組立品）を選べるようになります。":
    "Run an inventory sync and the selector above will let you pick a kit.",

  // バーコード読み取りの案内（使える場所が画面から見つからなかった）
  "バーコードリーダーは使えますか（どの画面で使えますか）":
    "Can I use a barcode scanner (and on which screens)?",
  "使えます。USB／Bluetooth のバーコードリーダーは「コードを打ち込んで Enter」を送るキーボードとして動くため、専用の設定やアプリは要りません。読み取り欄のある画面を開き、欄にカーソルがある状態で読み取ってください。1件ごとに欄は自動で空になり、次の読み取りを受け付けます。":
    "Yes. A USB or Bluetooth scanner acts as a keyboard that types the code and presses Enter, so no special setup or app is needed. Open a screen that has a scan field, put the cursor in it, and scan. The field clears itself after each scan and is ready for the next one.",
  "入荷（発注済み・入荷）＝届いた商品を読み取って入荷数を積み上げる":
    "Receiving (Ordered & receiving) — scan what arrived to build up the received quantity",
  "棚卸し＝カウント中の実数を読み取りで入力する":
    "Stocktakes — scan to enter the counted quantity",
  "在庫調整＝対象の商品を読み取りで選ぶ":
    "Stock adjustments — scan to pick the product to adjust",
  "読み取りは Shopify の商品に登録されたバーコード（JAN）と SKU の両方で照合します。どちらも未設定の商品は読み取れないため、その場合は手入力してください。棚に貼る値札は「発注点設定」画面の「バーコードラベル（印刷）」から出せます。":
    "Scans are matched against both the barcode and the SKU on your Shopify product. Products with neither cannot be scanned, so enter those by hand. Shelf labels can be printed from “Barcode labels (print)” on the Reorder points screen.",
  "スマートフォンのカメラでの読み取りには対応していません。":
    "Scanning with a phone camera is not supported.",
  入荷を開く: "Open receiving",
  棚卸しを開く: "Open stocktakes",
  在庫調整を開く: "Open stock adjustments",

  // UNKNOWN 在庫書き込みの明示照合（migration 0076）
  在庫反映の確認: "Inventory write review",
  注文書の入荷: "Purchase order receipt",
  "拠点間移動（出庫）": "Transfer shipment",
  "拠点間移動（入庫）": "Transfer receipt",
  棚卸しの確定: "Stocktake application",
  在庫操作の確認状況を取得できませんでした:
    "Could not check inventory write review status",
  "Shopify への在庫反映結果が不明な操作が無いか確認できていません。通信環境を確認して再試行してください。":
    "The app could not check whether any inventory writes have an unknown Shopify outcome. Check your connection and try again.",
  在庫操作の確認が必要です: "Inventory writes need review",
  確認する: "Review",
  この在庫操作を確認: "Review this inventory write",
  "{count} 件の在庫操作を確認": "Review {count} inventory writes",
  在庫同期を再開: "Resume inventory sync",
  "Shopify への反映結果を確認できなかった在庫操作が {count} 件あります。二重反映を防ぐため、次の在庫操作を行う前に確認してください。":
    "{count} inventory writes have an unconfirmed Shopify outcome. Review them before the next inventory operation to prevent a duplicate adjustment.",
  要確認: "Needs review",
  実在庫の同期待ち: "Inventory sync pending",
  "照合済み・同期完了": "Reviewed and synced",
  未適用で確定: "Confirmed not applied",
  "注文書（保存IDなし）": "Purchase order (no saved ID)",
  "注文書 #{id}": "Purchase order #{id}",
  "拠点間移動（保存IDなし）": "Transfer (no saved ID)",
  "拠点間移動 #{id}": "Transfer #{id}",
  "棚卸し（保存IDなし）": "Stocktake (no saved ID)",
  "棚卸し #{id}": "Stocktake #{id}",
  "在庫調整 #{id}": "Stock adjustment #{id}",
  "対象拠点（保存ID）: {id}": "Location (saved ID): {id}",
  "出庫元拠点（保存ID）: {id}": "Source location (saved ID): {id}",
  "入庫先拠点（保存ID）: {id}": "Destination location (saved ID): {id}",
  "理由: {reason}": "Reason: {reason}",
  "操作メモ: {note}": "Operation note: {note}",
  "Shopifyバリアント {variantId}: 在庫を {delta} 調整":
    "Shopify variant {variantId}: adjust inventory by {delta}",
  "発注明細 {itemId}: {quantity} 個を入荷":
    "Purchase order line {itemId}: receive {quantity}",
  "Shopifyバリアント {variantId}: {quantity} 個を出庫":
    "Shopify variant {variantId}: ship {quantity}",
  "Shopifyバリアント {variantId}: {quantity} 個を入庫":
    "Shopify variant {variantId}: receive {quantity}",
  "Shopifyバリアント {variantId}: 実数 {quantity} 個":
    "Shopify variant {variantId}: counted quantity {quantity}",
  "在庫アイテム {itemId} / Shopify拠点 {locationId}":
    "Inventory item {itemId} / Shopify location {locationId}",
  "{target}: 増減 {delta}": "{target}: change {delta}",
  "（送信時の変更元 {quantity}）": " (compare-and-set value {quantity})",
  "{target}: 実数 {quantity}{before}":
    "{target}: set quantity {quantity}{before}",
  "保存済み在庫項目 {index}: {target}":
    "Saved inventory item {index}: {target}",
  "Shopifyで確認した日時: {value}": "Checked in Shopify at: {value}",
  "確認した画面: {value}": "Screen checked: {value}",
  "確認した根拠: {value}": "Evidence checked: {value}",
  "セッションを確認できませんでした（401）。Shopify 管理画面からアプリを再読み込みしてください。":
    "Your session could not be verified (401). Reload the app from Shopify admin.",
  "このストアで確認できる在庫操作が見つかりませんでした（404）。一覧を再読み込みしてください。":
    "This inventory write was not found for the current store (404). Reload the unresolved list.",
  "在庫操作の状態が変わったか、同じストアに別の未解決操作が残っているため確定できませんでした（409）: {detail}。未解決の一覧を確認し、他の操作を先に判断してください。":
    "The inventory write changed state or another unresolved write remains for this store, so this request could not be completed (409): {detail}. Return to the unresolved list and review the other writes first.",
  "送信中だった処理と照合判断が重ならないよう、安全待機中です（409）: {detail}。初回送信から24時間経過後に状態を再取得し、Shopify の実在庫をもう一度確認してから判断してください。":
    "This write is in a safety wait so an in-flight request cannot overlap your decision (409): {detail}. After 24 hours from the first attempt, refresh this page and verify the authoritative inventory in Shopify again before deciding.",
  "入力内容を確認してください（422）: {detail}":
    "Check the required fields (422): {detail}",
  "照合結果は保存されましたが、Shopify の実在庫同期に失敗しました（502）。この操作は「実在庫の同期待ち」のまま保護されています。同じ画面から同期を再開してください。":
    "The review decision was saved, but the authoritative Shopify inventory sync failed (502). This operation remains protected as Inventory sync pending. Resume the sync from this screen.",
  "サーバー処理に失敗しました（{status}）: {detail}。状態を再取得してから同じ判断を再試行できます。":
    "The server request failed ({status}): {detail}. Refresh the state, then retry the same decision.",
  "在庫操作の照合に失敗しました（{status}）: {detail}":
    "Inventory write review failed ({status}): {detail}",
  "Shopify への書き込み結果を確認できなかった操作を、安全に照合します":
    "Safely review inventory writes whose Shopify outcome could not be confirmed",
  二重反映を防ぐための確認です:
    "This review prevents duplicate inventory changes",
  "ここにある操作は、通信切断などにより Shopify へ反映されたかをアプリが断定できません。確認が終わるまで同じ在庫操作は再実行しないでください。":
    "A connection loss or similar failure left the Shopify outcome of these operations uncertain. Do not repeat the same inventory operation until this review is complete.",
  在庫操作の一覧を取得できませんでした: "Could not load inventory writes",
  確認が必要な在庫操作はありません: "No inventory writes need review",
  "Shopify への反映結果が不明な操作や、実在庫の同期待ちの操作はありません。":
    "There are no writes with an unknown Shopify outcome and no authoritative inventory syncs pending.",
  同期を再開: "Resume sync",
  内容を確認: "Review details",
  拠点間移動を開く: "Open inventory transfers",
  "Shopify 実在庫の同期を再試行しますか？":
    "Retry the authoritative Shopify inventory sync?",
  "「Shopify に反映済み」で確定しますか？":
    "Confirm that Shopify already applied this operation?",
  "「Shopify に反映されていない」で確定しますか？":
    "Confirm that Shopify did not apply this operation?",
  未解決の一覧へ: "Back to unresolved writes",
  発注書メールの未解決一覧へ: "Back to unresolved emails",
  実在庫の同期を再試行する: "Retry authoritative inventory sync",
  適用済みと確定する: "Confirm as applied",
  未適用と確定する: "Confirm as not applied",
  戻って確認する: "Go back and check",
  "業務記録はすでに一度だけ確定済みです。再試行では業務記録を増やさず、Shopify から対象商品の実在庫を読み直してアプリの在庫表示を置き換えます。":
    "The business record has already been finalized exactly once. This retry does not create another record; it reads the affected inventory from Shopify and replaces the app's inventory view.",
  "Shopify にこの操作がすでに反映されている前提で、保存済みの業務記録を一度だけ確定します。その後、Shopify の実在庫を読み直します。":
    "This finalizes the saved business record exactly once on the basis that Shopify already applied the operation, then reads the authoritative inventory from Shopify.",
  "Shopify にこの操作が反映されていないと確定し、新しい在庫操作を許可します。実際には反映済みだった場合、あとで再実行すると二重に在庫が動きます。":
    "This confirms that Shopify did not apply the operation and allows a new inventory write. If Shopify did apply it, repeating the write later will change inventory twice.",
  "このボタンを押す前に、Shopify 管理画面の実在庫と在庫調整履歴をもう一度確認してください。":
    "Before pressing this button, check the current inventory and adjustment history in Shopify admin once more.",
  在庫操作の詳細を取得できませんでした:
    "Could not load inventory write details",
  "Shopify 管理画面の実在庫を直接確認してください":
    "Check the authoritative inventory directly in Shopify admin",
  "この画面に保存された内容だけで判断しないでください。Shopify 管理画面の「商品 ＞ 在庫」と在庫調整履歴を開き、対象拠点・対象商品・数量・時刻が一致するか確認してください。":
    "Do not decide from the saved details on this screen alone. In Shopify admin, open Products > Inventory and the inventory adjustment history, then verify the location, product, quantity, and time.",
  照合処理を完了できませんでした: "Could not complete the review",
  状態を再取得: "Refresh status",
  "照合済みですが、Shopify 実在庫の同期が未完了です":
    "Reviewed, but the authoritative Shopify inventory sync is incomplete",
  "Shopify 実在庫の同期を再試行": "Retry authoritative Shopify inventory sync",
  "業務記録は一度だけ確定済みです。新しい在庫操作は保護のため停止中です。同期を再試行すると、業務記録を増やさず Shopify の現在庫だけを読み直します。":
    "The business record has been finalized exactly once. New inventory writes remain blocked for safety. Retrying reads the current Shopify inventory without adding another business record.",
  操作時に保存した内容: "Saved operation request",
  "保存内容を項目別に表示できません。判断せずサポートへ連絡してください。":
    "The saved request cannot be shown as structured fields. Do not make a decision; contact support.",
  "Shopify へ送信するため保存した内容": "Saved Shopify inventory write",
  "Shopify 送信用の保存内容を表示できません。判断せずサポートへ連絡してください。":
    "The saved Shopify write cannot be displayed. Do not make a decision; contact support.",
  保存済みの照合記録: "Saved review audit",
  "判断: Shopify に反映済み": "Decision: applied in Shopify",
  "判断: Shopify に反映されていない": "Decision: not applied in Shopify",
  "確定日時: {value}": "Confirmed at: {value}",
  "確認メモ: {value}": "Review note: {value}",
  "照合記録はありません。": "No review audit is saved.",
  "Shopify で確認した結果を入力": "Record what you verified in Shopify",
  "どちらかを選んだだけでは確定しません。必須項目を入力して「確認画面へ」を押したあと、内容をもう一度確認します。":
    "Selecting an option does not confirm it. Complete every required field, choose Review decision, then check the decision once more.",
  "Shopify に反映済み": "Applied in Shopify",
  "保存済みの業務記録を確定し、Shopify の実在庫を読み直します。":
    "Finalize the saved business record and read the authoritative inventory from Shopify.",
  "Shopify に反映されていない": "Not applied in Shopify",
  "この操作を未適用として閉じ、新しい在庫操作を許可します。":
    "Close this operation as not applied and allow a new inventory write.",
  "確認メモ（必須）": "Review note (required)",
  "例: Shopify の在庫履歴と現在庫を照合しました":
    "Example: I checked the Shopify inventory history and current quantity",
  "Shopify で確認した日時（必須）": "Time checked in Shopify (required)",
  "Shopify 管理画面を確認した日時を、タイムゾーンを含めて入力してください。":
    "Enter when you checked Shopify admin, including the time zone.",
  "確認した画面・根拠（必須）": "Screen and evidence checked (required)",
  "例: 商品 ＞ 在庫 ＞ 調整履歴で -3 の記録を確認":
    "Example: Confirmed the -3 record under Products > Inventory > Adjustment history",
  確認画面へ: "Review decision",
  この在庫操作の照合は完了しています: "This inventory write review is complete",
  "Shopify の実在庫同期まで完了しました。この操作を再確定する必要はありません。":
    "The authoritative Shopify inventory sync is complete. This operation does not need to be confirmed again.",
  "Shopify には反映されていない判断で完了しました。必要なら元の在庫画面から新しい操作として実行してください。":
    "This review completed with the decision that Shopify did not apply the operation. If needed, run it as a new operation from its original inventory screen.",
  "在庫操作IDが正しくありません。未解決の一覧から開き直してください。":
    "The inventory operation ID is invalid. Open it again from the unresolved list.",
  // 棚卸しの進捗・残作業（潰し切る作業なのに残りが画面に無かった）
  "実数を入力した商品 {done} / {total} 件（残り {remaining} 件）":
    "Counted {done} of {total} products ({remaining} left)",
  未入力だけ表示: "Show only uncounted",
  この棚卸しには商品が1件もありません: "This stocktake has no products",
  "商品が同期される前に開始された可能性があります。中止して、同期が終わってから開始し直してください。":
    "It may have been started before your products synced. Cancel it and start again once the sync has finished.",
  すべての商品に実数を入力しました: "Every product has a counted quantity",
  "「入力した実数を保存」を押してから「在庫へ反映する内容を確認」で対象商品と実数を確認します。":
    "Press “Save counted quantities”, then “Review inventory update” to check the target products and quantities.",

  // Durable purchase-order email delivery and explicit reconciliation (0078)
  発注書メールの送信確認: "Purchase-order email delivery review",
  "送信結果が確定していないメールを、二重送信せずに確認します":
    "Review unresolved email outcomes without sending duplicate purchase orders",
  新しいメールとして送り直さないでください: "Do not resend this as a new email",
  "一覧に残るメールは、送信受付後の通信切断などで結果を確定できていません。安全な再開は保存済みの同じ本文・同じ操作キーだけで行います。":
    "These emails have an unresolved outcome, such as a connection loss after acceptance. A safe resume uses only the saved body and the same operation key.",
  照合済みの記録も表示: "Show reviewed records",
  発注書メールの一覧を取得できませんでした:
    "Could not load purchase-order email deliveries",
  確認が必要な発注書メールはありません: "No purchase-order emails need review",
  "送信結果が不明な発注書メールはありません。":
    "There are no purchase-order emails with an unresolved outcome.",
  "仕入先ID {id} への発注書メール": "Purchase-order email to supplier ID {id}",
  "要求した配送先: {requested} / 保存した配送先: {resolved}":
    "Requested destination: {requested} / Saved destination: {resolved}",
  送信済みで確定: "Confirmed sent",
  未送信で確定: "Confirmed not sent",
  要照合: "Needs review",
  送信処理中: "Delivery in progress",
  送信受付済み: "Accepted for delivery",
  送信結果が不明: "Delivery outcome unknown",
  受付期限切れ: "Acceptance expired",
  送信済み: "Sent",
  送信失敗: "Delivery failed",
  "送信依頼時の主拠点（自動選択）":
    "Primary location at request time (automatic)",
  "拠点ID {id} を明示指定": "Location ID {id} explicitly selected",
  保存された拠点なし: "No saved location",
  "拠点ID {id}": "Location ID {id}",
  "このストアで確認できる発注書メールが見つかりませんでした（404）。一覧から開き直してください。":
    "This purchase-order email was not found for this store (404). Open it again from the list.",
  "発注書メールの状態が変わったか、24時間の安全待機中です（409）: {detail}":
    "The email state changed or the 24-hour safety wait is still active (409): {detail}",
  "発注書メールの照合に失敗しました（{status}）: {detail}":
    "Purchase-order email review failed ({status}): {detail}",
  "発注書メールの照合に失敗しました。": "Purchase-order email review failed.",
  "「送信済み」で確定しますか？": "Confirm this email as sent?",
  "「未送信」で確定しますか？": "Confirm this email as not sent?",
  送信済みと確定する: "Confirm as sent",
  未送信と確定する: "Confirm as not sent",
  "仕入先から受信確認を得た場合、または UreyukiBox サポートから送信済みと回答された場合だけ確定してください。":
    "Confirm only if the supplier confirms receipt or UreyukiBox support confirms that it was sent.",
  "このメールが実際には届いていた場合、新しく送り直すと二重送信になります。仕入先の受信状況または UreyukiBox サポートの回答をもう一度確認してください。":
    "Sending a new email would create a duplicate if this one was delivered. Recheck the supplier's confirmation or the answer from UreyukiBox support.",
  "確定後はこの送信を同じ操作キーで再開できません。":
    "After confirmation, this delivery cannot be resumed with the same operation key.",
  発注書メールの詳細を取得できませんでした:
    "Could not load purchase-order email details",
  仕入先にメールの受信を確認してください:
    "Ask the supplier whether they received the email",
  確認できない場合は問い合わせる: "Contact support if you cannot verify it",
  "下に保存された宛先・件名を伝え、電話など別の連絡手段で仕入先に届いたか確認してください。結果を確認できない場合は推測せず、お問い合わせください。":
    "Tell the supplier the saved recipient and subject shown below, then use another channel such as phone to ask whether it arrived. If you cannot verify the outcome, do not guess; contact support.",
  "まだ24時間の安全待機中です。新しいメールとして送り直さず、送信開始から24時間後に状態を再取得してください。":
    "The 24-hour safety wait is still active. Do not send a new email; refresh the status 24 hours after delivery started.",
  保存済みの同じ送信だけを再開できます:
    "Only the same saved delivery can be resumed",
  同じ送信を再開: "Resume the same delivery",
  "保存済み本文を読み取れません。再送せずサポートへ連絡してください。":
    "The saved body cannot be read. Do not resend; contact support.",
  "宛先・件名・本文・操作キーを変えずに注文書画面へ戻します。新しいメールは作りません。":
    "Return to the purchase-order screen with the same recipient, subject, body, and operation key. No new email is created.",
  "保存済みの宛先・本文・操作キーをこのタブに安全に保存できなかったため、送信は再開していません。ブラウザのストレージ設定を確認してください。":
    "The saved recipient, body, and operation key could not be stored safely in this tab, so delivery was not resumed. Check your browser storage settings.",
  保存済みの送信を再開できませんでした: "Could not resume the saved delivery",
  "送信受付: {value}": "Accepted: {value}",
  配送先の保存内容: "Saved destination details",
  "要求した配送先: {value}": "Requested destination: {value}",
  "送信本文に確定した配送先: {value}":
    "Destination frozen in the email: {value}",
  "保存済み本文を表示できません。判断せずサポートへ連絡してください。":
    "The saved body cannot be displayed. Do not make a decision; contact support.",
  送信受付時に保存した全文: "Full body saved at acceptance",
  宛先: "To",
  差出人: "From",
  件名: "Subject",
  最後に記録した送信エラー: "Last recorded delivery error",
  "メール配信サービスから送信結果を確認できなかったため、この画面で確認が必要です。":
    "The email service did not return a conclusive outcome, so this delivery needs review here.",
  確認した送信結果と証跡を入力: "Record the verified outcome and evidence",
  "選択しただけでは確定しません。必須項目を入力して確認画面を開き、もう一度内容を確認します。":
    "Selecting an outcome does not confirm it. Complete every required field, open the confirmation, and review it once more.",
  "仕入先の受信確認、または UreyukiBox サポートの送信済み回答があります。":
    "The supplier confirmed receipt, or UreyukiBox support confirmed that it was sent.",
  未送信: "Not sent",
  "仕入先の未受信確認、または UreyukiBox サポートの未送信回答があります。":
    "The supplier confirmed non-receipt, or UreyukiBox support confirmed that it was not sent.",
  "例: 仕入先へ電話し、受信済みと確認しました":
    "Example: Called the supplier and confirmed receipt",
  "確認した日時（必須）": "Time checked (required)",
  "確認方法・根拠（必須）": "Verification method and evidence (required)",
  "例: 仕入先担当者○○様へ電話で確認":
    "Example: Confirmed by phone with the supplier contact",
  この発注書メールの照合は完了しています:
    "This purchase-order email review is complete",
  "送信済みとして確定しました。このメールを再送する必要はありません。":
    "Confirmed as sent. This email does not need to be resent.",
  "未送信として確定しました。必要なら注文書画面で最新内容を確認し、新しいメールとして送信できます。":
    "Confirmed as not sent. If needed, review the latest content on the purchase-order screen and send it as a new email.",
  "発注書メールIDが正しくありません。未解決の一覧から開き直してください。":
    "The purchase-order email ID is invalid. Open it again from the unresolved list.",
  発注書メールの送信状況を取得できませんでした:
    "Could not load purchase-order email delivery status",
  保存済みの送信を確認: "Review saved delivery",
  "このタブに結果未確定の発注書メールが保存されています。新しいメールを作らず、保存済みの送信を確認してください。":
    "This tab contains a purchase-order email with an unresolved outcome. Review the saved delivery without creating a new email.",
  "送信結果が不明な発注書メールが無いか確認できていません。通信環境を確認して再試行してください。":
    "The app could not check whether any purchase-order email outcome is unresolved. Check the connection and try again.",
  発注書メールの送信確認が必要です:
    "Purchase-order email delivery review required",
  このメールの送信結果を確認: "Review this email outcome",
  "{count} 件の送信結果を確認": "Review {count} email outcomes",
  "送信結果を確定できていない発注書メールが {count} 件あります。ボタンを押して保存内容を開き、仕入先に受信を確認してください。確認できない場合は画面からお問い合わせできます。":
    "{count} purchase-order email outcome(s) remain unresolved. Open the saved details, ask the supplier whether they received it, and use the contact action if you cannot verify the result.",
  後で確認: "Review later",
  未確定の送信を再確認: "Review unresolved delivery",
  送信状況の確認が必要: "Delivery review required",
  この仕入先のメールは送信状況の確認が必要です:
    "This supplier email requires delivery review",
  この仕入先には結果未確定のメールがあります:
    "This supplier has an email with an unresolved outcome",
  "新しいメールとして送信したり、同じ操作を再試行したりせず、保存された送信記録を照合してください。":
    "Do not send a new email or retry this operation. Review the saved delivery record.",
  "新しい本文や操作キーでは送信できません。保存済みの宛先・件名・本文・操作キーだけを再確認して再開できます。":
    "A new body or operation key cannot be used. Only the saved recipient, subject, body, and operation key can be reviewed and resumed.",
  同じ内容を再確認: "Review the same content",
  "送信内容と操作キーをこのタブに安全に保存できなかったため、メールは送信していません。ブラウザのストレージ設定を確認して再試行してください。":
    "The email content and operation key could not be stored safely in this tab, so no email was sent. Check your browser storage settings and try again.",
  "前回送信の確認情報をこのタブに安全に保存できなかったため、別注文のメールは送信できません。ブラウザのストレージ設定を確認してください。":
    "The previous-delivery confirmation could not be stored safely in this tab, so the separate order cannot be emailed. Check your browser storage settings.",
  別の新規注文として送信する前に確認:
    "Review before sending as a separate new order",
  送信する発注書メールを確認: "Review purchase-order email",
  別の新規注文として送信: "Send as a separate new order",
  同じ操作キーで別注文送信を再開:
    "Resume the separate-order delivery with the same operation key",
  この内容で送信: "Send this exact email",
  この仕入先には最近送信済みの発注書があります:
    "A purchase order was recently emailed to this supplier",
  "以下の前回メールと今回のメールが別の注文であることを確認してください。同じ注文なら送信しないでください。":
    "Confirm that the previous email below and this email are separate orders. Do not send if they are the same order.",
  前回送信したメールの全文を確認できません:
    "The full previously sent email cannot be verified",
  "前回の宛先・件名・本文・送信時刻を表示できるまで、別注文として送信できません。":
    "This cannot be sent as a separate order until the previous recipient, subject, full body, and delivery time can be displayed.",
  前回送信済みのメール: "Previously sent email",
  "送信日時: {value}": "Sent at: {value}",
  表示中の本文だけを送信します: "Only the displayed body will be sent",
  "本文（商品・数量・金額を含む全文）":
    "Full body (including items, quantities, and totals)",
  "この画面だけの未保存の数量変更・商品追加・除外・発注書情報は、このメールには含まれません。送信受付後に在庫・税率・宛先設定を変更しても、受付済みの本文は変わりません。":
    "Unsaved quantity changes, added or excluded items, and purchase-order details on this screen are not included. Once accepted, later inventory, tax, or recipient changes do not alter the saved body.",
  メール設定または送信を完了できませんでした:
    "Could not complete email setup or delivery",
  送信状況を確認: "Review delivery status",
  "通信環境を確認して、もう一度お試しください。":
    "Check your connection and try again.",
  前回の送信済みメールを再表示しました:
    "The previously sent email was displayed again",
  別注文メールの送信結果は未確定です:
    "The separate-order email outcome is unresolved",
  "新しいメールは送信していません。同じ注文なら操作は完了です。別の新規注文を送る場合だけ、前回と今回の全文を確認してください。":
    "No new email was sent. If this is the same order, no further action is needed. Only for a separate new order, compare the full previous and current emails.",
  "新しい操作キーでは送信しないでください。保存済みの本文・操作キー・別注文確認情報を変えずに、同じ送信だけを再確認して再開できます。":
    "Do not send with a new operation key. You may only review and resume the same delivery without changing its saved body, operation key, or separate-order confirmation.",
  別の新規注文として送信する: "Send a separate new order",
  同じ別注文メールを再確認: "Review the same separate-order email",
  "{email} に発注書を送信しました。": "Purchase order emailed to {email}.",
  "サーバーで作成した宛先・件名・全文を確認したあとに送信します。この画面だけの数量変更・商品追加・除外は含まれません。":
    "Review the server-generated recipient, subject, and full body before sending. Unsaved quantity changes, added items, and exclusions on this screen are not included.",

  // Stocky 移行と API / CSV の担当範囲。
  "Stocky終了後の在庫管理をUreyukiBoxで継続できます。まずは在庫データを同期し、仕入先と発注点を設定してください。Stockyを使っていた場合は、公式エクスポートや保存済みCSV・画面貼り付けで対応データを取り込めます。":
    "Keep managing inventory with UreyukiBox after Stocky retires. Start by syncing inventory, then set up suppliers and reorder points. If you used Stocky, import supported data from the official export, saved CSV files, and copied screen tables.",
  CSV取り込み: "CSV import",
  "〜8/31": "Through Aug 31",
  "9/1〜": "Sep 1 onward",
  "9/1以降のStocky公式エクスポート":
    "Stocky official export from Sep 1 onward",
  "Stockyから届いたフォルダ内のmanifest.jsonと8つのCSVを、名前を変えずにまとめて選択してください。ZIPのままでは取り込めません。":
    "Select manifest.json and all eight CSV files from the folder Stocky sent you, without renaming them. A ZIP file cannot be imported directly.",
  "最初に内容と件数だけを確認します。確認画面の次の操作を行うまで保存しません。現在のShopify在庫数は変更しません。":
    "First, only the contents and counts are checked. Nothing is saved until you confirm on the next screen. Current Shopify inventory quantities are not changed.",
  公式エクスポートの9ファイル: "Nine official export files",
  Stocky公式エクスポートの9ファイル: "Nine Stocky official export files",
  "{selected} / 9ファイルを選択": "{selected} of 9 files selected",
  必要なファイルの選択状況: "Required file selection status",
  選択済み: "Selected",
  重複: "Duplicate",
  未選択: "Missing",
  "9ファイルを確認してください": "Check the nine files",
  "不足: {files}": "Missing: {files}",
  "重複: {files}": "Duplicate: {files}",
  "対象外: {files}": "Unexpected: {files}",
  ファイル内容を確認する: "Check file contents",
  確認して取り込む: "Confirm and import",
  "まだ保存していません。内容と件数を確認してください":
    "Nothing has been saved yet. Check the contents and counts",
  "サーバーの確認結果を検証できませんでした。もう一度確認してください。":
    "The server result could not be verified. Check the files again.",
  "9ファイル・元データ{rows}行を確認しました。":
    "Checked nine files containing {rows} source rows.",
  元データ行数: "Source rows",
  保存方法: "Storage mode",
  業務データと原本: "Operational data and exact source",
  一部の業務データと原本: "Some operational data and exact source",
  UreyukiBoxで保存する件数: "Records to save in UreyukiBox",
  "拠点別Min/Max履歴": "Min/max history by location",
  "運用データ: 新規{created}件・更新{updated}件・変更なし{unchanged}件。商品未一致{variants}件・仕入先未一致{suppliers}件。":
    "Operational data: {created} new, {updated} updated, {unchanged} unchanged. {variants} unmatched products and {suppliers} unmatched suppliers.",
  件数: "Count",
  確認が必要な項目があります: "Some items need review",
  "Shopify variant {id}: 発注点がないためMOQ/リードタイムを運用設定へ反映しません。":
    "Shopify variant {id}: MOQ and lead time were not applied to operational settings because no reorder point exists.",
  "product_variants.csvの商品・価格・在庫数は履歴として保存し、Shopify商品は変更しません。":
    "Products, prices, and inventory quantities in product_variants.csv are saved as history; Shopify products are not changed.",
  "min_max_available.csvは拠点別履歴として保存し、現在の在庫数や発注点は変更しません。":
    "min_max_available.csv is saved as location-level history; current inventory quantities and reorder points are not changed.",
  "stock_take_items.csvは棚卸履歴として保存し、現在の在庫数へ反映しません。":
    "stock_take_items.csv is saved as stocktake history and is not applied to current inventory quantities.",
  "stock_transfer_items.csvは移動履歴として保存し、現在の在庫移動を作成しません。":
    "stock_transfer_items.csv is saved as transfer history and does not create current inventory transfers.",
  公式エクスポートを取り込みました: "Official export imported",
  "9ファイルの原本を保存しました。現在のShopify在庫数は変更していません。":
    "Saved the exact source for all nine files. Current Shopify inventory quantities were not changed.",
  "同じ取込の保存済み結果を再表示しました。重複して追加していません。":
    "Displayed the saved result for this same import. No duplicate records were added.",
  商品と仕入先の仕入条件: "Product and supplier purchasing terms",
  "商品・バリエーション": "Products and variants",
  発注明細: "Purchase order lines",
  在庫移動: "Stock transfers",
  在庫移動明細: "Stock transfer lines",
  在庫調整明細: "Stock adjustment lines",
  データで見る: "View in Data",
  "StockyデータのCSV・貼り付け移行": "Stocky data migration from CSV and pasted tables",
  "保存済みLow Stock CSVを取り込みます": "Import a saved Low Stock CSV",
  "終了前に保存した low_stock_csv.csv を選んでください。使う列は SKU・Vendor・ROP（発注点）・Lead Time の4つです。":
    "Select a low_stock_csv.csv file saved before shutdown. The importer uses the SKU, Vendor, ROP, and Lead Time columns.",
  "確認状態が一致しません。CSV と列の対応付けを確認して、もう一度お試しください。":
    "The confirmation state no longer matches. Check the CSV and column mapping, then try again.",

  形式: "Format",

  // Stocky 移行の基本用語。
  Stockyコピー: "Stocky copy",
  一般の在庫調整: "General stock adjustments",
  仕入用税区分: "Purchase tax categories",
  "Low Stock CSV": "Low Stock CSV",

  // 手作り / 別システム CSV による仕入先フォールバック。
  仕入先情報: "supplier details",
  "有効 {valid} 行・新規 {created} 件・更新 {updated} 件・変更なし {unchanged} 件・除外 {skipped} 行":
    "Valid rows: {valid} / new: {created} / updated: {updated} / unchanged: {unchanged} / skipped: {skipped}",
  "仕入先を新規 {created} 件・更新 {updated} 件取り込みました（変更なし {unchanged} 件）":
    "Imported suppliers: {created} new, {updated} updated ({unchanged} unchanged)",
  "同じ正規化名の仕入先が複数あるため特定できない仕入先名: {count} 件（該当行は取り込まれません）。":
    "Supplier names that match multiple normalized suppliers: {count} (those rows will not be imported).",
  "同じ正規化名の仕入先が複数あり特定できなかった仕入先名（{count} 件）: {values}":
    "Supplier names not resolved because multiple normalized suppliers matched ({count}): {values}",
  "優先仕入先（is_preferred）の列": "Preferred supplier (is_preferred) column",
  "入数（1ケースの個数）の列": "Pack size (units per case) column",
  仕入条件または優先設定: "purchasing terms or preferred-supplier settings",
  "有効 {valid} 行・除外 {skipped} 行 / 対象商品 {linked} 件 / リンク: 新規 {linksCreated} 件・更新 {linksUpdated} 件・変更なし {linksUnchanged} 件 / 仕入先自動作成 {suppliersCreated} 件 / 条件書込 {conditionsWritten} 件（既存値上書き {overwritten} セル） / 優先仕入先: 新規 {preferredCreated} 件・変更または修復 {preferredChanged} 件・保持 {preferredPreserved} 件":
    "Valid rows: {valid}; skipped: {skipped} / products: {linked} / links: {linksCreated} new, {linksUpdated} updated, {linksUnchanged} unchanged / suppliers auto-created: {suppliersCreated} / terms written: {conditionsWritten} ({overwritten} existing cells overwritten) / preferred suppliers: {preferredCreated} new, {preferredChanged} changed or repaired, {preferredPreserved} preserved",
  "商品と仕入先のリンクを新規 {created} 件・更新 {updated} 件取り込みました":
    "Imported product-supplier links: {created} new, {updated} updated",
  "同じ正規化 SKU の商品が複数あるため特定できない SKU: {count} 件（該当行は取り込まれません）。":
    "SKUs that match multiple products after normalization: {count} (those rows will not be imported).",
  "同じ正規化 SKU の商品が複数あり特定できなかった SKU（{count} 件）: {values}":
    "SKUs not resolved because multiple normalized products matched ({count}): {values}",
  // Stocky purchase tax type manual fallback.
  "この手動税区分画面に対応するサーバー機能がまだ反映されていません。古い取込先へは切り替えず、安全のため停止しました。時間をおいて画面を再読み込みしてください。":
    "The server feature for this manual tax type screen is not available yet. The import stopped safely instead of falling back to an older endpoint. Wait a while, then reload the page.",
  "確認結果を検証できませんでした。まだ取り込まず、もう一度確認してください。":
    "The preflight result could not be verified. Nothing has been imported; run the preflight again.",
  "取り込み結果を検証できませんでした。画面を再読み込みして確認してください。":
    "The import result could not be verified. Reload the page and check again.",
  "Purpose 列を用意して、仕入用の行だけに purchase と入力する方法を推奨します。Purpose 列を省く場合は、表の全行が仕入用であることを確認するチェックが必要です。":
    "We recommend adding a Purpose column and entering purchase only on purchase-tax rows. If you omit Purpose, you must attest that every row in the table is for purchases.",
  取り込み前にご確認ください: "Before importing",
  "既存の商品への税率割り当てと、すでに登録済みの仕入税率は上書きしません。仕入税率がまだ1件もない場合は、安全に既定を決められる表に限って新しい仕入税率を作成し、今後の発注書で使用します。":
    "Existing product tax assignments and existing purchase tax rates are never overwritten. If there are no purchase tax rates yet, a table with an unambiguous default may create new rates for future purchase orders.",
  "ID なしの行は名前を識別子にします。名前を変えると別の参照データとして追加され、同じ名前の税率を再取り込みしても既存の仕入税率には同期しません。Tax ID を指定した行は、作成後にこの手動機能から内容を変更できません。":
    "A row without an ID uses its name as its identity. Renaming it creates a separate reference row, and re-importing a same-name rate does not synchronize an existing purchase tax rate. A row created with a Tax ID cannot later be changed through this manual importer.",
  "ID なしの税区分が複数ある場合は既定を安全に決められないため、仕入税率を自動作成しません。取り込み後に仕入税率の設定画面で必要な税率と既定を確認してください。":
    "When more than one tax type has no ID, no purchase tax rate is created automatically because a safe default cannot be determined. After importing, review the required rates and default on the purchase tax rate settings page.",
  "Tax name と Rate % は必須です。Rate % は 0〜100 のパーセントで、小数点には . を使います。Tax ID は全行で分かる場合だけ入力し、Purpose は purchase と入力してください。":
    "Tax name and Rate % are required. Enter Rate % as a percentage from 0 through 100 and use a period as the decimal separator. Enter Tax ID only if it is known for every row, and enter purchase for Purpose.",
  "ヘッダーは Tax name, Rate %, Tax ID, Purpose です。Tax ID が無い場合は列の対応付けで「（使わない）」を選んでください。":
    "The headers are Tax name, Rate %, Tax ID and Purpose. If you do not have Tax IDs, choose “Not used” for that mapping.",
  入力方法: "Input method",
  "CSV ファイルを選ぶ": "Choose a CSV file",
  ヘッダー付きの表を貼り付ける: "Paste a table with headers",
  仕入税区分のCSVファイル: "Purchase tax type CSV file",
  ヘッダー付き表の貼り付け欄: "Table with headers",
  "1行目のヘッダーを含めて、表計算ソフトからそのまま貼り付けられます。":
    "Include the first header row and paste directly from your spreadsheet.",
  列を確認する: "Review columns",
  "{count} 行を検出しました": "Found {count} rows",
  "下のサンプルを確認し、各項目に対応する列を選んでください。推測された列も確定前に確認してください。":
    "Review the sample below and map a column to each field. Verify every guessed mapping before confirming.",
  "Tax name（税区分名）の列・必須": "Tax name column (required)",
  "Rate %（0〜100%）の列・必須": "Rate % column (0–100%, required)",
  "値は 0〜100 のパーセントです。5.5% は 5.5 または 5.5% と入力し、5,5 は使用できません。":
    "Values are percentages from 0 through 100. Enter 5.5% as 5.5 or 5.5%; do not use 5,5.",
  "Tax ID の列・任意": "Tax ID column (optional)",
  "正の Stocky tax type ID が全行で分かる場合だけ選びます。ID が無い場合は税区分名から安全な手動IDを作ります。":
    "Select this only when a positive Stocky tax type ID is known for every row. Without an ID, a collision-checked manual ID is derived from the tax type name.",
  "Purpose の列・任意（ある場合は必ず選択）":
    "Purpose column (optional; required when present)",
  "選んだ場合、値が purchase の行だけを取り込めます。sales や空欄が1行でもあれば全体を停止します。":
    "When mapped, only rows whose value is purchase can be imported. A single sales or blank value stops the entire import.",
  同じ列を複数の項目に選べません:
    "A column cannot be selected for more than one field",
  "項目ごとに別の列を選んでください。":
    "Select a different column for each field.",
  "Purpose と思われる列が複数あります":
    "More than one column looks like Purpose",
  "用途を一意に判定できません。該当する列名を1つだけに整理してから、表を確認し直してください。":
    "The purpose is ambiguous. Rename the columns so only one looks like Purpose, then review the table again.",
  "Purpose と思われる列を対応付けてください":
    "Map the column that looks like Purpose",
  "列「{column}」を Purpose として選ぶか、表からその列を削除してから確認し直してください。チェックだけではこの列を無視できません。":
    "Select “{column}” as Purpose, or remove that column and review the table again. The attestation cannot be used to ignore this column.",
  "この表の全行が仕入用の税区分で、販売用の行を含まないことを確認しました":
    "I confirm that every row in this table is a purchase tax type and that it contains no sales rows",
  "Purpose 列を使わない場合だけ必要です。確認できない場合は取り込まないでください。":
    "This is required only when no Purpose column is used. Do not import if you cannot confirm it.",
  "必須列を選び、Purpose 列を対応付けるか仕入用だけの表であることを確認すると、事前確認へ進めます。":
    "Select the required columns, then either map Purpose or attest that the table contains purchase rows only to continue to preflight.",
  取り込み内容を事前確認: "Run import preflight",
  "事前確認が完了しました（まだ保存していません）":
    "Preflight complete (nothing saved yet)",
  無効な行があるため確定できません: "Cannot confirm while invalid rows remain",
  "全 {total} 行・有効 {valid} 行・新規 {created} 件・更新 {updated} 件・変更なし {unchanged} 件・無効 {invalid} 行":
    "Total: {total} / valid: {valid} / new: {created} / updated: {updated} / unchanged: {unchanged} / invalid: {invalid}",
  "確定すると仕入税率を {count} 件新規作成し、今後の発注書の税計算で使用します。商品への割り当ては変更しません。":
    "Confirming will create {count} purchase tax rates for tax calculations on future purchase orders. Product assignments will not change.",
  "確定時に仕入税率を新規作成する予定はありません。既存の仕入税率と商品への割り当ては変更しません。":
    "Confirming will not create any purchase tax rates. Existing purchase tax rates and product assignments will not change.",
  "{row} 行目: {reason}": "Row {row}: {reason}",
  "無効な行の一覧は先頭 {shown} 件まで表示しています。":
    "Only the first {shown} invalid rows are shown.",
  この内容で仕入税区分を取り込む: "Import these purchase tax types",
  仕入税区分の取り込みが完了しました: "Purchase tax type import complete",
  "新規 {created} 件・更新 {updated} 件・変更なし {unchanged} 件":
    "New: {created} / updated: {updated} / unchanged: {unchanged}",
  "仕入税率を {count} 件作成しました。商品への割り当ては変更していません。":
    "Created {count} purchase tax rates. Product assignments were not changed.",
  "既存の仕入税率と商品への割り当ては変更していません。":
    "Existing purchase tax rates and product assignments were not changed.",
  // Stocktake source-provenance views.
  "Stockyから取り込み済み（Stocky側の適用状態は不明）":
    "Imported from Stocky (application status in Stocky is unknown)",
  商品を数えた後の在庫数として保存:
    "Saved as inventory after counting the products",
  "商品を数える前の Stocky 在庫数として保存":
    "Saved as the inventory shown in Stocky before counting",
  "分からない（CSV の値をそのまま保存）": "Not sure (CSV values saved as-is)",
  "指定なし（手動列対応）": "Not specified (manual column mapping)",
  棚卸し履歴の詳細取得に失敗しました:
    "Could not retrieve stocktake history details",
  "棚卸し履歴の詳細を読み込んでいます…": "Loading stocktake history details…",
  棚卸し履歴の詳細: "Stocktake history details",
  Stockyから取り込み済み: "Imported from Stocky",
  実際の反映先を正確に表示できません:
    "The exact destination of the inventory update is unavailable",
  "保存した反映記録がないか、内容を安全に確認できません。現在の商品情報から推測せず、反映先を表示していません。":
    "The saved application record is missing or cannot be verified safely. No destination is inferred from current product data.",
  Shopifyへ実際に反映した内容: "What was actually applied to Shopify",
  "反映時に保存した記録です。現在の商品情報から推測した内容ではありません。":
    "This record was saved when the inventory update was applied. It is not inferred from current product data.",
  保存した棚卸し商品: "Saved stocktake product",
  実際に反映したShopify商品: "Shopify product actually updated",
  反映した実数: "Applied count",
  "このShopify商品を同じ商品として確認して反映しました":
    "This Shopify product was confirmed as the same product before applying the count",
  "{title} / SKU: {sku} / 保存時のShopify バリアントID: {variantId} / 棚卸し明細ID: {itemId}":
    "{title} / SKU: {sku} / saved Shopify variant ID: {variantId} / stocktake line ID: {itemId}",
  "{title} / SKU: {sku} / Shopify 商品ID: {productId} / バリアントID: {variantId} / 在庫アイテムID: {inventoryItemId} / ロケーションID: {locationId}":
    "{title} / SKU: {sku} / Shopify product ID: {productId} / variant ID: {variantId} / inventory item ID: {inventoryItemId} / location ID: {locationId}",
  "Stocky側で在庫へ適用済みかどうかは、このCSVからは確認できません。UreyukiBoxは現在在庫を変更していません。":
    "This CSV does not show whether Stocky applied the count to inventory. UreyukiBox has not changed current inventory.",
  "元CSV: {file}": "Source CSV: {file}",
  "実施日: {date}": "Date counted: {date}",
  "実施拠点: {name}": "Location counted: {name}",
  "Current Stock の扱い: {meaning}": "Current Stock setting: {meaning}",
  "明細はありません。": "There are no line items.",
  "源泉値 {count} 件": "{count} source values",
  詳細を見る: "View details",
  棚卸し履歴の取得に失敗しました: "Could not retrieve stocktake history",
  棚卸しの操作: "Stocktake actions",
  棚卸しの操作情報の取得に失敗しました:
    "Could not retrieve stocktake action information",
  "UreyukiBoxで実施した棚卸しと、Stockyから取り込んだ履歴です。StockyのCSVは在庫への適用状態を証明しないため、取り込み済みと適用済みを分けて表示します。Shopify管理画面で直接編集した変動はここには並びません。":
    "This list contains stocktakes performed in UreyukiBox and history imported from Stocky. A Stocky CSV does not prove whether inventory was updated, so imported and applied statuses are shown separately. Changes made directly in Shopify admin are not listed here.",
  実施日時: "Date and time",
  "カウント / 源泉値": "Counts / source values",
  UreyukiBoxで適用: "Applied by UreyukiBox",
  元CSV: "Source CSV",
  詳細: "Details",
  "選んだCSVの合計サイズが大きすぎます（上限 25MB）。複数回に分けて取り込んでください。":
    "The selected CSV files are too large in total (25 MB maximum). Split them into multiple imports.",
  "{files} ファイル・元データ {rows} 行を確認しました。記録: 新規 {created} 件・更新 {updated} 件・変更なし {unchanged} 件。原本 {archived} ファイルを保管します。":
    "Reviewed {files} files and {rows} source rows. Records: {created} new, {updated} updated and {unchanged} unchanged. {archived} source files will be archived.",
  "1. 親一覧 purchase_orders.csv（1ファイル）":
    "1. Parent purchase_orders.csv (one file)",
  発注書の親一覧CSV: "Purchase-order parent-list CSV",
  個別の発注書CSV: "Individual purchase-order CSVs",
  在庫調整の履歴CSV: "Stock-adjustment history CSVs",
  確定して取り込む: "Confirm and import",
  件数を確認する: "Review counts",
  "まだ保存していません。件数と原本を確認してください":
    "Nothing has been saved yet. Review the counts and source files.",
  履歴と原本を保存しました: "History and source files saved",
  "CSV行数が運用データの安全上限100,000行を超えています。発注書・在庫調整の業務データへは変換せず、原本を完全保存します（{rows}行すべて未処理）。":
    "This CSV exceeds the 100,000-row operational-data safety limit. It will not be converted into purchase-order or stock-adjustment operational data; the complete source file will be saved ({rows} rows unprocessed).",
  "1件の発注書または在庫調整の明細が安全上限（25,000行または正規化後8MiB）を超えています。この取込全体を発注書・在庫調整の業務データへは変換せず、原本を完全保存します（{rows}行すべて未処理）。":
    "One purchase order or stock-adjustment record exceeds the safety limit of 25,000 line-item rows or 8 MiB after normalization. This entire import will not be converted into purchase-order or stock-adjustment operational data; the complete source files will be saved ({rows} rows unprocessed).",
  "運用データの安全上限により、この取込全体を発注書・在庫調整の業務データへは変換せず、原本を完全保存します（{rows}行すべて未処理）。":
    "An operational-data safety limit prevents this entire import from being converted into purchase-order or stock-adjustment operational data. The complete source files will be saved ({rows} rows unprocessed).",
  "保存後は、この画面の「保存済みデータの取り出し」から全行を汎用形式で分割取得できます。":
    "After saving, use Saved data exports on this page to download every row in generic chunks.",
  業務データへ変換せず原本を保存することを確認しました:
    "I understand; save the source file without converting it into operational data",
  "まだ保存していません。業務データへ変換せず原本を保存する内容を確認してください":
    "Nothing has been saved yet. Review saving the source file without converting it into operational data.",
  "原本を保存しました（運用データの正規化は未完了）":
    "Source file saved (operational-data normalization is incomplete)",
  "運用データへ正規化済み {normalized} 行・未処理 {unprocessed} 行。":
    "{normalized} rows normalized into operational data; {unprocessed} rows remain unprocessed.",
  "運用データへの書き込みは0件です。":
    "No records were written to operational data.",
  "運用データの安全上限により、この取込全体は運用モデルへ1件も変換せず、送信された正確な原本と解析可能な全行を汎用JSONLとして保持します（{rows}行すべて運用変換は未処理）。":
    "Because of the operational-data safety limit, none of this import will be converted into operational models. The exact submitted source and every parseable row will be retained as generic JSONL ({rows} rows remain unprocessed for operational conversion).",
  "保存後は、この画面の「保存済みデータの取り出し」から原本をダウンロードし、解析可能な全行の汎用JSONLを分割取得できます。":
    "After saving, use Saved data exports on this page to download the source and retrieve generic JSONL for every parseable row in chunks.",
  "運用モデルへ書き込まず、正確な原本と解析可能な全行を汎用JSONLとして保全することを確認する":
    "I confirm that nothing will be written to operational models and that the exact source and every parseable row will be retained as generic JSONL",
  "原本と汎用JSONLを保存しました（運用データは未反映）":
    "Source and generic JSONL saved (operational data was not changed)",
  "正確な原本と解析可能な全行を汎用JSONLとして保存しました。この画面の「保存済みデータの取り出し」から原本をダウンロードし、汎用JSONLを分割取得できます。":
    "The exact source and every parseable row were saved as generic JSONL. Use Saved data exports on this page to download the source and retrieve the generic JSONL in chunks.",
  親一覧だけを先に保存できます: "You can save the parent list first",
  "商品明細は「未取得」として保存します。個別の発注書CSVが見つかったときは、同じ親一覧と一緒に後から追加できます。":
    "Line items will be saved as not retrieved. When individual purchase-order CSVs become available, add them later together with the same parent list.",
  "発注書 {records} 件のうち、商品明細を正規化済み {complete} 件・商品明細未取得 {incomplete} 件・明細確認が必要 {conflicts} 件です。未取得の発注書も一覧情報と原本は保存され、個別CSVを後から追加できます。":
    "Of {records} purchase orders, {complete} have normalized line items, {incomplete} have line items not retrieved, and {conflicts} require review. List data and source files are still saved for orders without line items, and individual CSVs can be added later.",
  "親一覧と個別CSVの仕入先が一致しない発注書があります。個別CSVの原本は保存しますが、商品明細は推測で正規化しません。":
    "Some purchase orders have different suppliers in the parent list and individual CSV. The individual source CSV is saved, but its line items are not normalized by inference.",
  保存済み原本を確認する: "Review saved files",
  表データを確認できます: "Table data available",
  原本のみ: "Original file only",
  "{files} ファイルを確認しました。同じ内容または同じ取込IDの原本 {duplicates} 件も一覧で確認できます。":
    "Reviewed {files} files. The list also shows {duplicates} source files with duplicate content or import IDs.",
  "{files} ファイルの原本を保存しました。": "Saved {files} source files.",
  原本をダウンロード: "Download source file",
  "このファイルは {n} 行あります。発注点・リードタイムには1件も書き込まず、正確な原本と解析可能な全行を汎用JSONLとして保持します。":
    "This file contains {n} rows. No reorder points or lead times will be written; the exact source and every parseable row will be retained as generic JSONL.",
  "保存後は「保存済みデータの取り出し」から原本をダウンロードし、汎用JSONLを分割取得できます。まだ何も保存していません。":
    "After saving, use Saved data exports to download the source and retrieve the generic JSONL in chunks. Nothing has been saved yet.",
  "発注点・リードタイムへ書き込まず、正確な原本と解析可能な全行を汎用JSONLとして保全することを確認する":
    "I confirm that no reorder points or lead times will be written and that the exact source and every parseable row will be retained as generic JSONL",
  "原本と汎用JSONLを保存しました（発注点・リードタイムは未反映）":
    "Source and generic JSONL saved (reorder points and lead times were not changed)",
  "{n}行の正確な原本と解析可能な全行を汎用JSONLとして保存しました。発注点・リードタイムには1件も書き込んでいません。保存済みデータの取り出しから汎用JSONLを分割取得できます。":
    "Saved the exact source and every parseable row as generic JSONL for {n} rows. No reorder points or lead times were written. Retrieve the generic JSONL in chunks from Saved data exports.",
  保存したStockyファイルの一覧: "Saved Stocky files",
  外部バックアップを閉じる: "Close external backup",
  "保存済みデータを外部バックアップする（通常は不要）":
    "Back up saved data externally (usually not needed)",
  これはStockyからデータを取得する操作ではありません:
    "This does not retrieve data from Stocky",
  "すでにUreyukiBoxへ保存したデータを、別の場所へバックアップするための機能です。Stocky側で未取得のデータは増えません。通常の移行確認は、上の「保存したStockyファイルの一覧」で行ってください。":
    "This feature backs up data already saved in UreyukiBox to another location. It does not add anything that has not been retrieved from Stocky. For normal migration checks, use Saved Stocky files above.",
  保存済みデータをZIPでダウンロード: "Download saved data as ZIP",
  ZIPに収まらない場合は分割ダウンロードします:
    "Use split downloads when the data does not fit in one ZIP",
  "一括ZIPが上限を超えた場合は、下の各データセットと、一覧にある各ファイルのJSONLを順番に取得し、最後の検証が完了するまで続けてください。取得中に内容が変わった場合は最初からやり直す案内を表示します。ZIPに失敗しても、保存済みデータは削除・変更されません。":
    "If the ZIP exceeds its limit, download each dataset below and the JSONL for each listed file until final verification completes. If data changes during download, you will be prompted to restart. A ZIP failure never deletes or changes saved data.",
  保存済みデータをJSONLで分割ダウンロード:
    "Download saved data as split JSONL files",
  "各ボタンで最大8MBずつダウンロードします。最終ファイルの後に件数と並び順を検証します。表示されたすべてのファイル名がブラウザのダウンロード一覧にあることを確認してください。":
    "Each button downloads up to 8 MB. After the final file, record count and order are verified. Confirm that every displayed filename appears in your browser downloads.",
  "保存済みデータを読み込んでいます…": "Loading saved data…",
  保存済みデータを取得できませんでした: "Could not load saved data",
  データの種類: "Data type",
  ダウンロード状況: "Download status",
  "汎用JSONL: 検証済み・ユーザー確認済み（{count} 行 / {parts} パート）":
    "Generic JSONL: verified and user-confirmed ({count} rows / {parts} parts)",
  "汎用JSONL: 検証済み・ブラウザ保存未確認（{count} 行 / {parts} パート）":
    "Generic JSONL: verified; browser save unconfirmed ({count} rows / {parts} parts)",
  "汎用JSONL: {parts} パート / {count} of {total} 行を受信（保存未確認）":
    "Generic JSONL: {parts} parts / {count} of {total} rows received (save unconfirmed)",
  "汎用JSONL: 未受信": "Generic JSONL: not received",
  汎用JSONLを最初から再取得: "Download generic JSONL again from the beginning",
  ダウンロード一覧で汎用JSONLを確認した:
    "I confirmed the generic JSONL in my download list",
  次の汎用JSONLチャンクを取得: "Download the next generic JSONL chunk",
  最初の汎用JSONLチャンクを取得: "Download the first generic JSONL chunk",
  "現在のショップを確認できません。再読み込みしてください。":
    "The current shop could not be verified. Reload the page.",
  ストア情報を取得できませんでした: "Could not load store information",
  "ストア情報を確認しています…": "Checking store information…",
  現在のショップを確認できません: "The current shop could not be verified",
  現在のショップを確認しています: "Verifying the current shop",
  "再読み込みするか、ショップを開き直してから再試行してください。":
    "Reload the page or reopen the shop, then try again.",
  "ショップを確認できるまで移行入力は開始しません。":
    "Migration input remains unavailable until the shop is verified.",
  もう一度確認する: "Verify again",
  "{dataset}: ブラウザのダウンロード一覧で {files} を確認しました。再開情報をブラウザから削除しました。":
    "{dataset}: confirmed {files} in the browser download list. Resume information was removed from this browser.",
  "{filename}: 前回までを含む {files} をブラウザのダウンロード一覧で確認しました。再開情報をブラウザから削除しました。":
    "{filename}: confirmed {files}, including earlier downloads, in the browser download list. Resume information was removed from this browser.",
  "{filename} の汎用行を最初から再取得できる状態に戻しました。":
    "{filename} is ready to download generic rows again from the beginning.",
  "{filename} は全 {count} 行・{parts} パートのサーバー整合性を再検証しました。前回までを含む {files} をブラウザのダウンロード一覧で確認してください。":
    "{filename}: server integrity was reverified for all {count} rows across {parts} parts. Confirm {files}, including earlier downloads, in the browser download list.",
  "文字コードを安全に解析できないため、原本のみ取得できます":
    "Only the source file can be retrieved because its character encoding cannot be parsed safely",
  "列数が安全上限を超えるため、原本のみ取得できます":
    "Only the source file can be retrieved because its column count exceeds the safety limit",
  "CSV形状が安全上限を超えるため、原本のみ取得できます":
    "Only the source file can be retrieved because its CSV shape exceeds the safety limit",
  "CSV構造を安全に解析できないため、原本のみ取得できます":
    "Only the source file can be retrieved because its CSV structure cannot be parsed safely",
  "全チャンク受信済み・サーバー最終検証中（{count} 件）":
    "All chunks received; final server verification in progress ({count} rows)",
  "全チャンク受信済み・サーバー最終検証待ち（{count} 件）":
    "All chunks received; final server verification pending ({count} rows)",
  最終整合性を検証中: "Verifying final integrity",
  検証を再試行: "Retry verification",
  原本保管容量: "Source archive capacity",
  "{used} / {cap}、{files} / {fileCap} ファイルを使用中（残り {remaining}、{remainingFiles} ファイル）":
    "Using {used} / {cap} and {files} / {fileCap} files ({remaining} and {remainingFiles} files remaining)",
  原本保管容量を超えます: "The source archive capacity would be exceeded",
  "この選択内容は店舗の保管上限（512 MiB・20,000ファイル）を超えるため保存できません。ローカルの原本は削除せず保管し、移行期限が迫っている場合はサポートへ連絡してください。":
    "This selection exceeds the shop's archive limit (512 MiB and 20,000 files), so it cannot be saved. Keep the local originals and contact support if the migration deadline is near.",
  保存する項目: "Item to save",
  "保管する Stocky 原本": "Stocky source files to archive",
  原本を保管する: "Archive source files",
  原本を確認しました: "Source files reviewed",
  原本またはZIPを処理できませんでした:
    "Could not process the source file or ZIP",
  "一括ZIPの上限（64MBまたは原本件数）を超えました。データは削除されていません。下の正規化データセットを検証完了までチャンク取得し、保存済み原本は一覧から個別にダウンロードしてください。":
    "The all-in-one ZIP exceeded its 64 MB or source-count limit. No data was deleted. Download every normalized dataset chunk through successful verification, and download each saved original from the list.",
  "別のデータ書き出しを処理中です。表示された再試行時間を待ってから、同じ操作をもう一度お試しください。":
    "Another export is in progress. Wait for the displayed retry interval, then try the same action again.",
  "一時保存先の空き容量を確保できませんでした。データは削除されていません。表示された再試行時間を待ってから、同じ操作をもう一度お試しください。":
    "The server could not reserve enough temporary storage. No data was deleted. Wait for the displayed retry interval, then try the same action again.",
  "チャンク取得中にデータが変更されました。保存済みファイルは残りますが、完全な一式とは確認できません。最初のチャンクから再取得してください。":
    "Data changed while chunks were being downloaded. Files already saved remain, but they are not a verified complete set. Restart from the first chunk.",
  データセット: "Dataset",
  未取得: "Not downloaded",
  未受信: "Not received",
  "{chunks} チャンク / {count} 件を受信・保存操作開始（保存未確認）":
    "{chunks} chunks / {count} rows received and save dispatched (save unconfirmed)",
  "サーバー整合性検証済み・ブラウザ保存未確認（{count} 件 / root {root} / {files}）":
    "Server consistency verified; browser save unconfirmed ({count} rows / root {root} / {files})",
  "サーバー整合性検証済み・ユーザー確認済み（{count} 件 / root {root} / {files}）":
    "Server consistency verified; files user-confirmed ({count} rows / root {root} / {files})",
  最初のチャンクを取得: "Download first chunk",
  次のチャンクを取得: "Download next chunk",
  検証完了: "Verified",
  ダウンロード一覧でファイル名を確認した:
    "I confirmed the filenames in my download list",
  最初から再取得: "Download again from the beginning",
  "{dataset} はサーバー上の全 {count} 件・順序付きハッシュの整合性を検証しました。ブラウザが保存を完了したかは確認できないため、ダウンロード一覧で {files} を確認してください。":
    "{dataset}: the server verified all {count} rows and the ordered hash. The browser's completed save cannot be detected, so confirm {files} in your download list.",
  "{dataset} を最初から再取得できる状態に戻しました。":
    "{dataset} is ready to download again from the beginning.",
  "保存済み原本を読み込んでいます…": "Loading saved source files…",
  保存済み原本を取得できませんでした: "Could not load saved source files",
  "保存済みの原本はまだありません。": "No source files have been saved yet.",
  ファイル名: "Filename",
  カテゴリ: "Category",
  正規化レベル: "Normalization level",
  "原本カテゴリ: {category}・保存行数: {rows}":
    "Source category: {category} · Saved rows: {rows}",
  "取得条件・文脈": "Acquisition conditions and context",
  CSVヘッダー: "CSV headers",
  バイト単位で保存したCSV: "Byte-exact CSV",
  取得した画面データ: "Captured screen data",
  仕入先注文番号: "Supplier order number",
  支払日: "Paid on",
  調整額: "Adjustments",
  税額: "Tax",
  入荷分小計: "Received subtotal",
  入荷状況: "Received",
  初回入荷日: "First received",
  最終入荷日: "Last received",
  担当者: "Employee",
  Stocky原本の親行: "Stocky source parent rows",
  "同じ発注書・在庫調整に一致したCSVの物理行を、行ごとに分けて表示しています。":
    "Physical CSV rows matching this purchase order or stock adjustment are shown separately.",
  "原本行 {row}": "Source row {row}",
  原本行を開く: "Open source row",
  "保存データID: {id}・取得項目: {task}":
    "Saved data ID: {id} · Capture task: {task}",
  "分類: {collection}・サイズ: {bytes} bytes":
    "Category: {collection} · Size: {bytes} bytes",
  "取得条件: {scope}": "Capture scope: {scope}",
  "内容確認・ダウンロード": "View contents and download",
  "このコピーで保存した全CSV・画面データ":
    "All CSV and captured screen data saved by this copy",
  "CSVは取得した応答をバイト単位で保存しています。画面から取得した表・設定・取得不能の証跡は、CSV原本とは区別して「取得した画面データ」と表示します。":
    "CSV files preserve the fetched response byte for byte. Tables, settings, and unavailable evidence captured from a screen are labeled as captured screen data, separately from CSV originals.",
  行数: "Rows",
  保存日時: "Saved at",
  操作: "Action",
  次の100件を読み込む: "Load the next 100",
  "全 {total} 件中 {loaded} 件を表示": "Showing {loaded} of {total}",
  "Stockyの公式エクスポート、保存済みCSV、画面表から取得できた範囲を保存します。":
    "Save the data available in Stocky's official export, saved CSV files, and onscreen tables.",
  Stockyデータ取込について: "About Stocky data import",
  "Stocky公式エクスポートや終了前に保存したCSV・画面表のうち、下に表示する対応形式だけを取り込めます。ファイルにない項目は推測して補完しません。":
    "You can import only the supported formats shown below from Stocky's official export or CSV files and onscreen tables saved before shutdown. Missing fields are never inferred.",
  "次のCSVまたは保存済み画面表を項目ごとに取り込みます。":
    "Import each of the following CSV files or saved onscreen tables separately.",
  "すでに取り込み済みの場合、この入力は不要です。追加するCSVがある場合だけ、下の各欄で選んでください。":
    "If this data has already been imported, no input is needed. Use the fields below only when you have additional CSV files.",
  Transfersは保存作業不要です: "No Transfers file needs to be saved",
  "Stockyの「Inventory → Transfers」は外部リンクでShopify管理画面の「在庫移管」を開きます。StockyのレポートやCSVは表示されないため、この移行画面でTransfersを探したりアップロードしたりする必要はありません。":
    "Stocky's Inventory → Transfers is an external link that opens Inventory transfers in Shopify admin. It does not show a Stocky report or CSV, so there is no Transfers file to find or upload on this migration page.",
  Stocky公式エクスポートを使う場合: "Using Stocky's official export",
  "Stockyから届いたファイルを確認し、下に示すファイル名・列と一致するものだけを選んでください。エクスポート全体のZIPをそのまま取り込む機能ではありません。":
    "Review the files sent by Stocky and select only those matching the filenames and columns shown below. This importer does not accept the entire export ZIP as-is.",
  "別のタブ、または前回の操作で開始されたコピーが進行中です。このページを閉じても処理は続きます。":
    "A copy started in another tab or a previous action is still running. It will continue if you close this page.",
  "Stock on hand（在庫スナップショット）": "Stock on hand (inventory snapshot)",
  "Low Stock（専用取込で5MiBを超えた原本）":
    "Low Stock (source files over the 5 MiB dedicated-import limit)",
  "手動仕入先マスタ CSV（0件・運用上限超原本）":
    "Manual supplier master CSV (zero rows or over the operational limit)",
  "手動商品・仕入先リンク CSV（0件・運用上限超原本）":
    "Manual product-supplier link CSV (zero rows or over the operational limit)",
  "Average cost（平均原価）": "Average cost",
  "Purchase Orders（親一覧）": "Purchase Orders (parent list)",
  "Purchase Order（個別発注書）": "Purchase Order (individual order)",
  "Adjustments（在庫調整）": "Adjustments",
  "Stocky の棚卸し（Stocktakes）を記録として取り込みます。Stocky側で在庫へ適用済みかどうかはCSVから判定できないため、UreyukiBoxは現在在庫を変更せず、源泉値と記録だけを保存します。Stocky は棚卸し1回につき1つの CSV を書き出すので、複数の棚卸しをまとめて選べます。実施日と拠点は分かる場合だけ指定し、不明のままでも後から設定できます。":
    "Import Stocky stocktakes as records. The CSV cannot show whether Stocky already applied them to inventory, so UreyukiBox does not change current inventory and preserves only the source values and records. Stocky exports one CSV per stocktake, so you can select multiple stocktakes together. Specify the count date and location only when known; you can leave them unknown and set them later.",
  区切り文字: "Delimiter",
  自動判定: "Auto-detect",
  カンマ: "Comma",
  タブ: "Tab",
  不明: "Unknown",
  "実施日・拠点が分からないファイルも「不明」のまま取り込めます。元データから確認できたときに棚卸し履歴で後から設定してください。":
    "Files with an unknown date or location can be imported as Unknown. Set them later from stocktake history when the source data confirms them.",
  "{name} の実施日（分かる場合のみ）":
    "Date counted for {name} (only when known)",
  "空欄は「不明」として保存し、棚卸し履歴から後で設定できます。":
    "A blank value is saved as Unknown and can be set later from stocktake history.",
  "{name} の実施拠点（分かる場合のみ）":
    "Location for {name} (only when known)",
  "棚卸しの実施日・拠点を更新できませんでした。もう一度お試しください。":
    "Could not update the stocktake date and location. Try again.",
  "実施日（不明なら空欄）": "Date counted (leave blank if unknown)",
  実施拠点: "Location counted",
  "選ばない場合は不明のまま保存します。":
    "If no location is selected, it remains saved as Unknown.",
  "実施日・拠点を更新できませんでした": "Could not update the date and location",
  "実施日・拠点を保存": "Save date and location",
  "実施日・拠点を設定・修正": "Set or correct date and location",
  "実施日・拠点の記録履歴": "Recorded date and location history",
  "実施日 {date}、拠点 {location}（記録日時: {changedAt}）":
    "Date counted: {date}; location: {location} (recorded {changedAt})",
  "選ばない場合は不明として保存し、棚卸し履歴から後で選べます。":
    "If no location is selected, it is saved as Unknown and can be selected later from stocktake history.",
  "{poNumber} の発注明細": "Line items for {poNumber}",
  "実原価（明細）": "Landed line cost",
  設定済みのキット: "Configured kits",
  資金繰り計算の対象商品: "Products included in cash-flow calculations",
  発注点: "Reorder point",
  リードタイム: "Lead time",
  在庫評価額の商品: "Inventory valuation items",
  直近30日のABC分析商品: "Items in the last 30 days of ABC analysis",
  "90日間の滞留在庫商品": "Items in 90-day dead stock",
  移動の作成: "Create transfer",
  移動対象の商品: "Transfer candidate items",
  移動数量: "Transfer quantity",
  発注点を設定できる商品: "Items available for reorder-point settings",
  未入荷発注数: "Open ordered quantity",
  "直近30日・利益レポートの集計元明細":
    "Last 30 days · source line items for the profit report",
  "直近30日・仕入先レポートの集計元発注書":
    "Last 30 days · source purchase orders for the supplier report",
  発注日: "Ordered date",
  ステータス: "Status",
  棚卸し対象の商品: "Stocktake candidate items",
  "このCSVの原本 {rows} 行は完全保存済みです。棚卸し履歴や現在在庫には書き込んでいません。":
    "The exact source for this CSV ({rows} rows) is already fully archived. No stocktake-history or current-inventory records were written.",
  "10万物理行を超えたため、原本 {rows} 行を完全保存し、棚卸し履歴や現在在庫には書き込んでいません。保存済みデータの取り出しから全行を汎用形式で分割取得できます。":
    "Because the file exceeds 100,000 physical rows, its exact source ({rows} rows) was fully archived. No stocktake-history or current-inventory records were written. You can retrieve every row in generic-format chunks from Saved data exports.",
  "Stockyの対象データが0件だったため、ヘッダーを含む原本を完全保存しました。棚卸し履歴や現在在庫には書き込んでいません。":
    "Because the Stocky report contained zero records, its exact source including the headers was fully archived. No stocktake-history or current-inventory records were written.",
  "運用DBへ変換せず原本と汎用形式を保持する確認結果の件数が一致しません。再度お試しください。":
    "The row counts in the confirmation to retain the source and generic format without operational conversion do not match. Try again.",
  "{count} 件は棚卸し履歴へ書かず、原本と解析可能な全行の汎用形式を保持します":
    "For {count} file(s), the exact source and every parseable row in generic format will be retained without writing stocktake history",
  "対象0件のStockyレポートはヘッダーを含む原本を完全保存します。10万物理行を超えるファイルも1行も捨てず、原本と解析可能な全行の汎用形式を保持しますが、この操作では棚卸し履歴や現在在庫を変更しません。保存後は「保存済みデータの取り出し」から全行を汎用形式で分割取得できます。下のボタンが、運用DBへ変換せず保持することの明示確認です。":
    "A zero-record Stocky report is fully archived including its headers. For a file over 100,000 physical rows, the exact source and every parseable row in generic format are retained without dropping a row, but this action does not change stocktake history or current inventory. After archiving, every row can be retrieved in generic-format chunks from Saved data exports. The button below explicitly confirms retention without operational conversion.",
  "貼り付けた一覧が大きすぎます。必要な棚卸しを含む範囲だけを貼り付けてください。":
    "The pasted list is too large. Paste only the section containing the stocktakes you need.",
  "見出しを確認できません。No.・Name・Location・Generated at・Status を含めて表をコピーしてください。":
    "The headers could not be verified. Copy the table with No., Name, Location, Generated at, and Status included.",
  "同じ意味の見出しが複数あります。Stocky の棚卸し一覧だけをコピーしてください。":
    "More than one header has the same meaning. Copy only the Stocky stocktake list.",
  "同じ Stocky No. が複数あります。重複行を除いてから貼り付けてください。":
    "The same Stocky No. appears more than once. Remove duplicate rows and paste the list again.",
  "一覧が10,000件を超えています。今回のCSVに対応する範囲だけを貼り付けてください。":
    "The list exceeds 10,000 rows. Paste only the section corresponding to this CSV batch.",
  "見出しは確認できましたが、No. と Generated at がある棚卸し行を読み取れませんでした。":
    "The headers were verified, but no stocktake row with both No. and Generated at could be read.",
  "Stocky の棚卸し一覧を閉じる": "Close the Stocky stocktake list",
  "Stocky の棚卸し一覧から名称・作成日時・状態も取り込む（任意）":
    "Also import names, generated times, and statuses from the Stocky stocktake list (optional)",
  "Stocky の Stocktakes 一覧で、No.・Name・Location・Generated at・Status を含む表を見出しごとコピーし、そのまま貼り付けてください。CSVが1件・一覧が1行なら自動で対応します。複数件では各CSVから正しい一覧行を選びます。":
    "In Stocky's Stocktakes list, copy the table including the No., Name, Location, Generated at, and Status headers, then paste it as-is. One CSV and one list row are matched automatically; for multiple files, select the correct list row for each CSV.",
  "Stocky の棚卸し一覧": "Stocky stocktake list",
  一覧を読み取れませんでした: "Could not read the list",
  "棚卸し一覧を {count} 件読み取りました":
    "Read {count} stocktake(s) from the list",
  "Generated at は Stocky で作成された日時として、タイムゾーンを付けずに正規化して保存します。棚卸しの完了日とは限らないため、実施日には自動設定しません。":
    "Generated at is normalized and saved as the time Stocky created the stocktake, without adding a timezone. Because it is not necessarily the completion date, it is not set as the date counted automatically.",
  "{count} 件の Generated at は形式を確認できないため、原文だけを保存します。":
    "Generated at could not be parsed for {count} row(s), so only the original text will be saved.",
  "表以外の {count} 行は対応対象から外しました。":
    "Excluded {count} non-table row(s) from matching.",
  "Stocky の一覧には独立したコメント欄がありません。Name は「Stockyでの棚卸し名」として別に保存し、下のメモへは自動でコピーしません。":
    "The Stocky list has no separate comment field. Name is saved separately as the Stocky stocktake name and is not copied into the note below.",
  "同じStocky一覧行を複数のCSVへ対応できません。各CSVで別の棚卸しを選んでください。":
    "The same Stocky list row cannot be matched to more than one CSV. Select a different stocktake for each CSV.",
  "{name} に対応する Stocky の棚卸し":
    "Stocky stocktake corresponding to {name}",
  Stocky一覧から選んでください: "Select from the Stocky list",
  一覧情報を対応しない: "Do not link list metadata",
  名前なし: "No name",
  拠点不明: "Unknown location",
  "CSVと一覧が1件ずつだったため自動で対応しました。":
    "Matched automatically because there is one CSV and one list row.",
  "CSVにはStockyの棚卸し番号が無いため、ファイル名や並び順では自動判定しません。":
    "The CSV does not contain the Stocky stocktake number, so filenames and order are not used for automatic matching.",
  棚卸しの一覧情報も一緒に保存します:
    "The stocktake list details will be saved too",
  "No.: {number} / Name: {name} / Location: {location} / Generated at: {generatedAt} / Status: {status}":
    "No.: {number} / Name: {name} / Location: {location} / Generated at: {generatedAt} / Status: {status}",
  "Generated at は作成日時です。Statusが途中の棚卸しでも表示されるため、完了日とは扱いません。Nameもコメントではなく棚卸し名として保存します。":
    "Generated at is the creation time. It is shown even while a stocktake is in progress, so it is not treated as the completion date. Name is saved as the stocktake name, not as a comment.",
  "Stockyの作成日 {date} を、この棚卸しの実施日として使う（自分で確認済み）":
    "Use Stocky's creation date {date} as this stocktake's date counted (I have verified it)",
  "選ばない限り、実施日は不明のままです。作成日時そのものはどちらでも保存されます。":
    "Unless selected, the date counted remains unknown. The creation time itself is saved either way.",
  "このStocky一覧行は別のCSVにも選ばれています。":
    "This Stocky list row is also selected for another CSV.",
  "Stocky一覧の情報をサーバーが保存対象として確認できませんでした。画面を再読み込みしてから、もう一度お試しください。":
    "The server did not acknowledge the Stocky list information for saving. Reload the page and try again.",
  Stocky棚卸し一覧から保存した情報:
    "Information saved from the Stocky stocktake list",
  "Stocky No.: {number}": "Stocky No.: {number}",
  "Stockyでの棚卸し名: {name}": "Stocky stocktake name: {name}",
  "Stocky一覧の拠点: {location}": "Location in the Stocky list: {location}",
  "Stockyでの作成日時: {generatedAt}": "Created in Stocky at: {generatedAt}",
  "作成日時はStockyのGenerated atを正規化した値で、棚卸しの完了日時ではありません。Stockyでの棚卸し名もコメントとは別に保存しています。":
    "The creation time is the normalized Stocky Generated at value, not the stocktake completion time. The Stocky stocktake name is also saved separately from comments.",

  // Unified read-only Stocky data browser.
  データ: "Data",
  Stocky移行: "Stocky migration",
  "Stockyコピー（〜8/31）": "Stocky copy (through Aug 31)",
  "Stockyコピー（終了後の読み取り専用期間）":
    "Stocky copy (post-shutdown read-only period)",
  手動CSVコピー: "Manual CSV copy",
  Stockyコピーへ進む: "Continue to Stocky copy",
  "Stockyコピー（〜8/31）：コピーが完了しました":
    "Stocky copy (through Aug 31): Copy complete",
  "Stockyコピー（終了後の読み取り専用期間）：コピーが完了しました":
    "Stocky copy (post-shutdown read-only period): Copy complete",
  "Stockyコピー（〜8/31）：コピーは完了しました（必須列の不足あり）":
    "Stocky copy (through Aug 31): Copy complete with missing required columns",
  "Stockyコピー（終了後の読み取り専用期間）：コピーは完了しました（必須列の不足あり）":
    "Stocky copy (post-shutdown read-only period): Copy complete with missing required columns",
  "取得できたデータと原本の保存は完了しました。必須列が不足した項目は0件成功にせず、取り込めなかった項目として以下に明示します。":
    "The data that could be read and its originals have been saved. Items with missing required columns are listed below as not imported, never as a successful zero-item result.",
  "必須列が不足した項目: {itemCount}件（不足列 合計{columnCount}列）":
    "Items with missing required columns: {itemCount} ({columnCount} missing columns total)",
  "{collection}: {columns}": "{collection}: {columns}",
  "Stocky側の列を修正した後は「新しいStockyコピーを開始」から再取得できます。今回保存できた件数と不足項目は、この完了結果のまま確認できます。":
    "After correcting the columns in Stocky, select Start a new Stocky copy to capture them again. The saved counts and missing items from this run remain visible in this completion result.",
  "Stocky APIコピーの新規受付は終了しています。今回保存できた件数と不足項目は、この完了結果のまま確認できます。新しいコピーには終了後の読み取り専用コピーまたは手動CSVコピーをご利用ください。":
    "New Stocky API copy jobs are closed. The saved counts and missing items from this run remain visible in this completion result. Use the post-shutdown read-only copy or manual CSV copy for any new copy.",
  "対応するStockyのページ／UIが利用可能で構造が変更されていない間は、Stocky側の列を修正した後に「新しいStockyコピーを開始」から再取得できます。今回保存できた件数と不足項目は、この完了結果のまま確認できます。":
    "While the supported Stocky pages and UI remain available and structurally unchanged, correct the columns in Stocky and select Start a new Stocky copy to capture them again. The saved counts and missing items from this run remain visible in this completion result.",
  "必須列が不足（{count}列）": "Missing required columns ({count})",
  "不足している必須列: {columns}": "Missing required columns: {columns}",
  必須列の不足あり: "Required columns missing",
  新しいStockyコピーを開始: "Start a new Stocky copy",
  データで全結果を見る: "View all results in Data",
  "発注書・明細": "Purchase orders and lines",
  "在庫調整・明細": "Stock adjustments and lines",
  "棚卸し履歴・明細": "Stocktake history and lines",
  レポート原本: "Report originals",
  "必須データの保存・取り込みと完全性確認がすべて終わりました。以下はサーバーに保存された完了結果です。":
    "All required data has been saved or imported and verified for completeness. These are the completion results stored on the server.",
  仕入先マスタと詳細: "Supplier records and details",
  Stockyの仕入用税区分: "Stocky purchase tax categories",
  商品ごとの仕入先と仕入条件: "Suppliers and purchasing terms by product",
  過去の発注書親レコードと全明細:
    "Historical purchase order records and all line items",
  過去の在庫調整親レコードと全明細:
    "Historical stock adjustment records and all line items",
  "Low Stock・発注点": "Low Stock and reorder points",
  "Low Stock CSVと商品ごとの発注点":
    "Low Stock CSV and reorder points by product",
  "仕入先・商品ごとのリードタイム": "Lead times by supplier and product",
  "仕入先・商品ごとの最低発注数量":
    "Minimum order quantities by supplier and product",
  Stockyの全棚卸しと商品明細: "All Stocky stocktakes and item lines",
  "Stockyの商品・バリエーション": "Stocky products and variants",
  "商品一覧、バリエーション、Stocky側の商品設定":
    "Product list, variants, and product settings in Stocky",
  需要予測設定: "Forecasting settings",
  Stocky画面で確認した需要予測の設定:
    "Forecasting settings verified on Stocky screens",
  "Stock on hand・全レポート原本": "Stock on hand and all report originals",
  "Stock on hand・CSVで書き出せるレポート原本":
    "Stock on hand and CSV-exportable report originals",
  "仕入用税区分（API）": "Purchase tax categories (API)",
  "仕入先（API）": "Suppliers (API)",
  "発注書・明細（API）": "Purchase orders and lines (API)",
  "在庫調整・明細（API）": "Stock adjustments and lines (API)",
  "商品と仕入先の紐付け（API）": "Product-to-supplier links (API)",
  "仕入先一覧（表示中・非表示）": "Supplier list (visible and hidden)",
  仕入先詳細: "Supplier details",
  "仕入先の最低発注数量（MOQ）": "Supplier minimum order quantities (MOQ)",
  仕入先のリードタイム: "Supplier lead times",
  仕入先CSV原本: "Supplier CSV original",
  商品一覧: "Product list",
  商品バリエーション: "Product variants",
  バリエーション詳細: "Variant details",
  全商品の発注点: "Reorder points for all products",
  需要予測の既定設定: "Forecasting defaults",
  Vendorリードタイム: "Vendor lead times",
  棚卸し一覧: "Stocktake list",
  棚卸し一覧の画面原本: "Stocktake list screen original",
  全棚卸しCSV: "All stocktake CSV files",
  "Low Stock CSV（ROP・リードタイム）": "Low Stock CSV (ROP and lead time)",
  Urgencyレポート: "Urgency report",
  "Low Stock商品レポート": "Low Stock product report",
  "Low Stockバリエーション表": "Low Stock variant table",
  発注書一覧: "Purchase order list",
  "個別発注書・明細": "Individual purchase orders and lines",
  発注書一覧CSV原本: "Purchase order list CSV original",
  発注書明細CSV原本: "Purchase order line CSV original",
  在庫調整一覧: "Stock adjustment list",
  "個別在庫調整・明細": "Individual stock adjustments and lines",
  在庫調整一覧CSV原本: "Stock adjustment list CSV original",
  仕入用税区分一覧: "Purchase tax category list",
  仕入用税区分詳細: "Purchase tax category details",
  "Stock on hand CSV（画面で選択中の原価）":
    "Stock on hand CSV (cost basis selected on screen)",
  "Stock on hand画面表": "Stock on hand screen table",
  "Current cost・Average cost": "Current cost and average cost",
  "Stock on hand履歴の対象8モード": "The eight Stock on hand history modes",
  "Stock on hand履歴CSV（8モード）": "Stock on hand history CSV (eight modes)",
  "ABC Analysisレポート": "ABC Analysis report",
  "Best Sellersレポート": "Best Sellers report",
  "Purchase Ordersレポート": "Purchase Orders report",
  "Stock Adjustmentsレポート": "Stock Adjustments report",
  Profitレポート: "Profit report",
  Ordersレポート画面表: "Orders report screen table",
  "Orders CSV": "Orders CSV",
  Productsレポート: "Products report",
  "Sale Itemsレポート": "Sale Items report",
  "SKU Variantsレポート": "SKU Variants report",
  Statisticsレポート: "Statistics report",
  Transfersレポート: "Transfers report",
  "保存・取り込み済み": "Saved and imported",
  必須列が不足: "Required columns missing",
  原本保存済み: "Original saved",
  対象一覧を確認済み: "Target list verified",
  "0件を画面で確認済み": "Verified as empty on screen",
  Stocky画面で直接取得できない状態を確認済み:
    "Observed that it cannot be retrieved directly from the Stocky screen",
  StockyのTransfers導線でリダイレクトを確認済み:
    "Redirect observed from the Stocky Transfers entry point",
  "StockyのOrders画面でCSVリンクを確認し、安全のため操作せず証跡を保存済み":
    "CSV link observed on the Stocky Orders screen; not invoked under the side-effect safety policy",
  "売価合計（Stocky集計）": "Total retail value (Stocky)",
  "売価合計（Shopify集計）": "Total retail value (Shopify)",
  "在庫数量（Stocky集計）": "Total items (Stocky)",
  "在庫数量（Shopify集計）": "Total items (Shopify)",
  "原価合計（Stocky集計）": "Total cost (Stocky)",
  "原価合計（Shopify集計）": "Total cost (Shopify)",
  "平均原価合計（Stocky集計）": "Total average cost (Stocky)",
  "平均原価合計（Shopify集計）": "Total average cost (Shopify)",
  "どちらのStockyコピーでも、完了後は同じ12項目と取得元ごとの処理件数を確認できます。項目の合計はAPI・CSV・画面取得の各件数を合算するため、同じ業務データを複数の取得元で確認した場合は重複を含みます。明細は「データで見る」、原本は個別の内容確認・ダウンロードから確認できます。":
    "Both Stocky copy options show the same 12 result categories and processed counts for each source after completion. Category totals add the API, CSV, and screen-capture counts, so they can include the same business record verified from more than one source. Open Data for normalized records, or inspect and download each original separately.",
  データへ戻る: "Back to data",
  データを取得できませんでした: "Could not retrieve data",
  "Stockyから保存・取り込んだ過去のデータと、これからUreyukiBoxで作成するデータを種類別に確認します。":
    "Review historical data saved or imported from Stocky together with data you create in UreyukiBox from now on, organized by type.",
  "保存した仕入用の税区分です。": "Saved purchase tax types.",
  "取り込み後の編集を含む、UreyukiBoxで現在使う仕入税率です。":
    "Purchase tax rates currently used in UreyukiBox, including edits made after import.",
  "保存した仕入先と連絡先です。": "Saved suppliers and contact details.",
  "保存した発注書と明細です。": "Saved purchase orders and line items.",
  "発注書ごとに記録した入荷日時・拠点・数量・メモです。":
    "Recorded receipt time, location, quantity, and notes for each purchase order.",
  "保存した一般の在庫調整です。棚卸し履歴とは別です。":
    "Saved general inventory adjustments. These are separate from stocktake history.",
  "保存した棚卸しと、その商品明細です。":
    "Saved stocktakes and their item details.",
  "棚卸しごとの商品と源泉数量です。":
    "Products and source quantities for each stocktake.",
  "UreyukiBoxで現在使っている発注点、リードタイム、最低発注数量です（取込後の変更を含みます）。":
    "Reorder points, lead times, and minimum order quantities currently used in UreyukiBox, including changes made after import.",
  "UreyukiBoxで現在使っている商品と仕入先、仕入条件の紐付けです（取込後の変更を含みます）。":
    "Product-supplier links and purchasing terms currently used in UreyukiBox, including changes made after import.",
  "保存したCSV・貼り付け原文と解析状況です。":
    "Saved CSV files, pasted source text, and their processing status.",
  商品不整合: "Product mismatches",
  現在の商品としての確認: "Current-product confirmation",
  "現在の商品ID（UreyukiBox）": "Current product ID (UreyukiBox)",
  現在のShopify商品ID: "Current Shopify product ID",
  現在のShopifyバリアントID: "Current Shopify variant ID",
  現在のShopify在庫アイテムID: "Current Shopify inventory item ID",
  現在のSKU: "Current SKU",
  現在の商品名: "Current product name",
  現在のバリエーション名: "Current variant name",
  現在のShopify商品: "Current Shopify product",
  現在の商品としての確認状態を確認してください:
    "Review the current-product confirmation status",
  "保存データと現在のShopify商品が一致しない項目を確認します。":
    "Review saved product references that do not match the current Shopify catalogue.",
  保存データと現在のShopify商品の対応を確認:
    "Review saved product references against current Shopify products",
  元の商品が現在のShopifyにありません:
    "The original product is no longer in Shopify",
  元のバリエーションが現在のShopifyにありません:
    "The original variant is no longer in Shopify",
  保存時の商品と現在のShopify商品が一致しません:
    "The saved product does not match a current Shopify product",
  安全な一致候補あり: "Safe match available",
  確認した現在の商品: "Confirmed current product",
  現在の商品には該当しない: "No corresponding current product",
  以前に確認した商品を再確認してください:
    "Review the previously confirmed product",
  未解決: "Unresolved",
  ストア担当者が現在の商品だと確認しました:
    "A store staff member confirmed this as the current product",
  確認したDRAFT商品を現在の商品として記録しました:
    "Recorded the confirmed DRAFT product as the current product",
  ストア担当者が現在の商品には該当しないと確認しました:
    "A store staff member confirmed there is no corresponding current product",
  "以前に確認した商品が現在のShopifyにないため、再確認が必要です":
    "The previously confirmed product is no longer in Shopify and must be reviewed",
  "一致する可能性がある商品が複数あるため、自動では選びません":
    "Several products may match, so none was selected automatically",
  商品名だけでは安全に同じ商品と判断できません:
    "A product name alone is not enough to identify the same product safely",
  現在のShopifyに安全に一致するIDまたは一意のSKUがありません:
    "No authoritative ID or unique SKU safely matches a current Shopify product",
  "Shopify バリアントIDが一致する現在の商品があります":
    "A current product has the same Shopify variant ID",
  Shopify在庫アイテムIDが一致する現在の商品があります:
    "A current product has the same Shopify inventory item ID",
  同じSKUを持つ現在の商品が1件あります:
    "Exactly one current product has the same SKU",
  現在の商品として確認できるか判断してください:
    "Decide whether this can be confirmed as the current product",
  商品と仕入先の条件: "Product and supplier terms",
  "現在の商品に関する変更履歴はありません。":
    "No current-product confirmation history.",
  "revision {revision}: {target} を現在の商品として確認":
    "Revision {revision}: confirmed {target} as the current product",
  現在は見つからない商品: "Product not currently found",
  "revision {revision}: 現在の商品には該当しない":
    "Revision {revision}: no corresponding current product",
  "DRAFT商品を現在の商品として確認しました。発注設定・仕入条件・在庫数は未変更です。":
    "Confirmed the DRAFT product as the current product. Replenishment settings, supplier terms, and inventory quantities were not changed.",
  "要確認（{count}）": "Needs review ({count})",
  "解決済み（{count}）": "Resolved ({count})",
  解決済みを見る: "View resolved",
  "「{target}」は現在の商品としてすでに確認済みです。発注設定と仕入条件を確認して反映します。{settings} 現在の設定と異なる場合は何も保存せず停止します。{unchanged} よろしいですか？":
    "“{target}” has already been confirmed as the current product. Review and apply the replenishment settings and supplier terms. {settings} If they differ from the current settings, nothing will be saved and the operation will stop. {unchanged} Continue?",
  "保存商品と「{target}」が同じ商品だと確認し、発注設定と仕入条件を確認して反映します。{settings} 現在の設定と異なる場合は何も保存せず停止します。{unchanged} よろしいですか？":
    "Confirm that the saved product and “{target}” are the same product, then review and apply the replenishment settings and supplier terms. {settings} If they differ from the current settings, nothing will be saved and the operation will stop. {unchanged} Continue?",
  "保存商品と「{target}」が同じ商品だと確認します。在庫数、税率、棚卸し・在庫調整の履歴、既存の発注書、BOM、保存済み原本は変更しません。よろしいですか？":
    "Confirm that the saved product and “{target}” are the same product? Inventory quantities, tax rates, stocktake and inventory-adjustment history, existing purchase orders, BOMs, and saved sources will not change.",
  "この保存データは現在の商品には該当しないものとして記録します。発注設定・仕入条件、在庫数、税率、棚卸し・在庫調整の履歴、既存の発注書、BOM、保存済み原本は変更しません。よろしいですか？":
    "Record that this saved data has no corresponding current product? Replenishment settings, supplier terms, inventory quantities, tax rates, stocktake and inventory-adjustment history, existing purchase orders, BOMs, and saved sources will not change.",
  "SKU: {sku}・Shopify商品ID: {productId}・バリアントID: {variantId}・在庫アイテムID: {inventoryItemId}":
    "SKU: {sku} · Shopify product ID: {productId} · Variant ID: {variantId} · Inventory item ID: {inventoryItemId}",
  関連する保存データ: "Related saved data",
  "商品と仕入先の条件 {count}件": "Product and supplier terms: {count}",
  "発注設定 {count}件": "Replenishment settings: {count}",
  "棚卸し明細 {count}件": "Stocktake items: {count}",
  "現在のShopify商品・発注可能": "Current Shopify product · Orderable",
  "現在のShopify商品・発注対象外": "Current Shopify product · Not orderable",
  以前に確認した商品は現在のShopifyにありません:
    "The previously confirmed product is no longer in Shopify",
  在庫数を調整: "Adjust inventory",
  "現在の商品には該当しないと記録済みです。":
    "Recorded as having no corresponding current product.",
  "現在の商品はまだ選ばれていません。":
    "No current product has been selected yet.",
  以前に確認した商品が見つかりません:
    "The previously confirmed product was not found",
  "現在の商品を再検索して、同じ商品か確認し直してください。在庫数は自動では変更しません。":
    "Search current products again and reconfirm that it is the same product. Inventory quantities will not change automatically.",
  現在の商品に関する確認が別の画面で更新されました:
    "The current-product confirmation was updated elsewhere",
  最新状態を再読み込み: "Reload latest state",
  "最新状態を確認してから、もう一度選択してください。":
    "Review the latest state, then make your selection again.",
  現在の商品として確認した結果を保存できませんでした:
    "Could not save the current-product confirmation",
  "通信が途切れた場合は、同じ内容でもう一度押してください。二重には記録しません。":
    "If the connection was interrupted, submit the same choice again. It will not be recorded twice.",
  この保存商品をもとに新しいShopify商品を作る:
    "Create a new Shopify product from this saved product",
  "保存済みの商品名とSKUだけを初期値にしたDRAFT商品を作成します。確認後、このDRAFTの正確なバリエーションを現在の商品として自動で記録します。在庫数、発注設定、仕入条件、BOMはコピーも変更もしません。":
    "Create a DRAFT product prefilled only with the saved title and SKU. After confirmation, its exact variant is automatically recorded as the current product. Inventory quantities, replenishment settings, supplier terms, and BOMs are neither copied nor changed.",
  Shopifyに下書き商品を作成: "Create Shopify draft product",
  "保存済みの「{title}」の商品名とSKUを初期値にして、ShopifyへDRAFT商品を1件作成します。在庫数、発注設定、仕入条件、BOMはコピーしません。Shopifyで内容を確認した後、正確な商品を同期して現在の商品として記録します。よろしいですか？":
    "Create one Shopify DRAFT product prefilled with the title and SKU from “{title}”? Inventory quantities, replenishment settings, supplier terms, and BOMs will not be copied. After reviewing it in Shopify, sync the exact product and record it as the current product.",
  下書き商品の作成結果を確認しています:
    "Checking the draft-product creation result",
  "同じ作成記録だけを再確認します。新しい商品は作らず、少し待ってから「作成結果を再確認」を押してください。":
    "Only the same saved creation attempt will be checked. No new product will be created. Wait briefly, then choose Check creation result again.",
  同じ下書き商品の作成結果を再確認してください:
    "Check the creation result for the same draft product.",
  "新しい商品は同期済みです。現在の商品としての確認結果を再読み込みしてください":
    "The new product is synced. Reload the current-product confirmation result.",
  下書き商品の作成を開始できませんでした:
    "Could not start draft-product creation",
  "同じ作成記録を保持しています。権限と通信を確認してから再試行してください。":
    "The same creation record is retained. Check permissions and connectivity, then retry.",
  "Shopifyの下書き商品を作成・確認できませんでした":
    "Could not create or verify the Shopify draft product",
  "Shopifyの商品編集権限を確認できませんでした。権限を許可してから同じ処理を再試行してください。":
    "Shopify product-editing permission could not be confirmed. Grant the permission, then retry the same operation.",
  "同じ作成記録を使ってもう一度お試しください。":
    "Try again using the same creation record.",
  Shopifyの商品編集を開けませんでした: "Could not open Shopify product editing",
  "同じ下書き商品の編集をもう一度開いてください。":
    "Open the same draft product for editing again.",
  "下書き商品はそのまま残っています。重複作成せず、同じ商品の編集を再開できます。":
    "The draft product is still there. You can resume editing the same product without creating a duplicate.",
  Shopifyでの確認内容はまだ自動反映していません:
    "Your Shopify review has not been applied automatically",
  "Shopifyで保存した内容をUreyukiBoxの商品一覧へ同期し、現在の商品として記録するには、「確認して同期し、現在の商品として記録」を押してください。発注設定・仕入条件・在庫数は変更しません。":
    "To sync the saved Shopify details to the UreyukiBox catalog and record the product as the current product, choose Confirm, sync, and record as current product. Replenishment settings, supplier terms, and inventory quantities will not change.",
  "ShopifyでDRAFT商品の内容を保存したことを確認し、その商品とバリエーションをUreyukiBoxの商品一覧へ同期して、保存商品に対する現在の商品として記録します。発注設定、仕入条件、在庫数、BOMは変更しません。よろしいですか？":
    "Confirm that you saved the DRAFT product in Shopify, sync that exact product and variant to the UreyukiBox catalog, and record it as the current product for the saved product? Replenishment settings, supplier terms, inventory quantities, and BOMs will not change.",
  "新しいDRAFT商品を同期し、現在の商品として確認しました":
    "Synced the new DRAFT product and confirmed it as the current product",
  "このDRAFT商品を現在の商品として確認しました。発注設定、仕入条件、在庫数、BOMは変更していません。設定や在庫の反映は内容を確認して別に実行してください。":
    "Confirmed this DRAFT product as the current product. Replenishment settings, supplier terms, inventory quantities, and BOMs were not changed. Review the details and apply settings or inventory separately if needed.",
  新しい商品を同期できませんでした: "Could not sync the new product",
  "下書き商品は残っています。内容を確認してから同じ同期を再試行してください。":
    "The draft product remains. Review it, then retry the same sync.",
  下書き商品の作成を再開できます: "Draft-product creation can be resumed",
  "サーバーに保存した同じ作成記録を再確認します。別の商品は作成しません。":
    "The same server-saved creation record will be checked. Another product will not be created.",
  Shopifyに下書き商品を保存しています: "A draft product is saved in Shopify",
  "同じDRAFT商品をShopifyで確認・保存してください。次のボタンで正確な商品を同期し、この保存商品に対する現在の商品として自動で記録します。発注設定、仕入条件、在庫数、BOMは変わりません。":
    "Review and save the same DRAFT product in Shopify. The next button syncs the exact product and automatically records it as the current product for this saved product. Replenishment settings, supplier terms, inventory quantities, and BOMs do not change.",
  Shopifyで下書き商品を確認: "Review draft product in Shopify",
  "確認して同期し、現在の商品として記録":
    "Confirm, sync, and record as current product",
  "この操作はDRAFT商品とバリエーションをUreyukiBoxへ同期し、保存商品に対する現在の商品として自動で記録します。在庫数量、発注設定、仕入条件、BOMはコピーも変更もしません。":
    "This syncs the DRAFT product and variant to the UreyukiBox catalog and automatically records it as the current product for the saved product. Inventory quantities, replenishment settings, supplier terms, and BOMs are neither copied nor changed.",
  新しいDRAFT商品はUreyukiBoxに同期済みです:
    "The new DRAFT product is synced to UreyukiBox",
  "このDRAFT商品の正確なバリエーションを現在の商品として確認しました。発注設定、仕入条件、在庫数、BOMは変更していません。必要なら下で商品を確認し、設定反映は明示的に実行してください。":
    "Confirmed this DRAFT product's exact variant as the current product. Replenishment settings, supplier terms, inventory quantities, and BOMs were not changed. Review the product below and apply settings only with an explicit action if needed.",
  "通常の確認操作で、保存商品とこのDRAFT商品が同じだと確認済みです。発注設定と仕入条件も反映しました。在庫数・BOMは変更していません。":
    "The normal confirmation flow confirmed that the saved product and this DRAFT product are the same. Replenishment settings and supplier terms were also applied. Inventory quantities and BOMs were not changed.",
  "通常の確認操作で、保存商品とこのDRAFT商品が同じだと確認済みです。発注設定と仕入条件はまだ反映していません。在庫数・BOMも変更していません。":
    "The normal confirmation flow confirmed that the saved product and this DRAFT product are the same. Replenishment settings and supplier terms have not been applied. Inventory quantities and BOMs were not changed.",
  "この作成済み商品は従来の確認で同期済みですが、当時の説明どおり現在の商品としてはまだ記録していません。下の明示操作では、このレシートの正確な商品だけを現在の商品として確認します。発注設定、仕入条件、在庫数、BOMは変更しません。":
    "This previously created product was synced using the earlier confirmation flow, which intentionally did not record it as the current product. The explicit action below confirms only the exact product in this receipt as the current product. Replenishment settings, supplier terms, inventory quantities, and BOMs will not change.",
  "この保存商品の現在の商品としての確認状態を取得できません。画面を再読み込みしてから、現在の商品を確認してください。":
    "Could not load this saved product's current-product confirmation status. Reload the page, then confirm the current product.",
  この作成済み商品を現在の商品として確認:
    "Confirm this created product as the current product",
  "以前に作成・同期したこのDRAFT商品の正確なバリエーションを、現在の商品として確認します。Shopifyの商品作成・検索・選択は行わず、発注設定、仕入条件、在庫数、BOMも変更しません。よろしいですか？":
    "Confirm the exact variant of this previously created and synced DRAFT product as the current product? Shopify will not create, search for, or select a product, and replenishment settings, supplier terms, inventory quantities, and BOMs will not change.",
  作成済みDRAFT商品を現在の商品として確認しました:
    "Confirmed the created DRAFT product as the current product",
  "保存商品と現在の商品が同じだと確認しました。発注設定、仕入条件、在庫数、BOMは変更していません。設定や在庫の反映は内容を確認して別に実行してください。":
    "Confirmed that the saved product and the current product are the same. Replenishment settings, supplier terms, inventory quantities, and BOMs were not changed. Review the details and apply settings or inventory separately if needed.",
  作成済みDRAFT商品を現在の商品として確認できませんでした:
    "Could not confirm the created DRAFT product as the current product",
  "表示中の商品情報を更新してから、もう一度お試しください。":
    "Refresh the displayed product information, then try again.",
  Shopifyで商品をもう一度確認: "Review product again in Shopify",
  現在の商品を選ぶ: "Choose a current product",
  現在のShopify商品候補: "Current Shopify product suggestions",
  "一致する現在の商品はありません。": "No current products match.",
  "商品名・SKU・Shopify バリアントIDで検索してください。":
    "Search by product name, SKU, or Shopify variant ID.",
  現在のShopify商品を検索: "Search current Shopify products",
  "商品名・SKU・Shopify バリアントID":
    "Product name, SKU, or Shopify variant ID",
  この商品を現在の商品として確認する:
    "Confirm this product as the current product",
  発注設定と仕入条件を確認して反映:
    "Review and apply replenishment settings and supplier terms",
  "現在の商品として確認し、発注設定と仕入条件を反映":
    "Confirm as the current product and apply replenishment settings and supplier terms",
  "反映するもの: 発注設定 {reorderPolicies}件、商品と仕入先の条件 {supplierTerms}件。":
    "Will apply: {reorderPolicies} replenishment setting(s) and {supplierTerms} product-supplier term(s).",
  "在庫数、税率、棚卸し・在庫調整の履歴、既存の発注書、BOM、保存済み原本は変更しません。":
    "Inventory quantities, tax rates, stocktake and inventory-adjustment history, existing purchase orders, BOMs, and saved sources will not change.",
  "現在の商品としての確認は済んでいます。発注設定と仕入条件を確認して反映します。異なる現在の設定は上書きしません。{settings} {unchanged}":
    "The current-product confirmation is complete. Review and apply replenishment settings and supplier terms. Different current settings will not be overwritten. {settings} {unchanged}",
  "現在の商品としての確認と、発注設定・仕入条件の反映を同じ操作で行います。異なる現在の設定は上書きしません。{settings} {unchanged}":
    "This action confirms the current product and applies replenishment settings and supplier terms. Different current settings will not be overwritten. {settings} {unchanged}",
  "この商品を現在の商品として確認します。在庫数、税率、棚卸し・在庫調整の履歴、既存の発注書、BOM、保存済み原本は変更しません。":
    "Confirm this as the current product. Inventory quantities, tax rates, stocktake and inventory-adjustment history, existing purchase orders, BOMs, and saved sources will not change.",
  現在の商品に関する変更履歴を閉じる:
    "Hide current-product confirmation history",
  現在の商品に関する変更履歴を見る:
    "View current-product confirmation history",
  現在の商品としての確認と設定反映を分けて表示します:
    "Current-product confirmation and settings application are shown separately",
  "現在の商品としての確認が済んでいる場合は、発注設定と仕入条件の反映だけを明示的に実行できます。反映するのは旧商品の発注設定と仕入条件だけで、異なる現在の設定は上書きしません。税率、在庫数、棚卸し・在庫調整の履歴、既存の発注書、BOM、保存済み原本は変更しません。":
    "Once the current product is confirmed, you can explicitly apply only replenishment settings and supplier terms. Only those settings from the saved product are applied, and different current settings are not overwritten. Tax rates, inventory quantities, stocktake and inventory-adjustment history, existing purchase orders, BOMs, and saved sources will not change.",
  発注設定と仕入条件の反映には確認が必要です:
    "Confirm before applying replenishment settings and supplier terms",
  "このDRAFT商品の正確なバリエーションは、現在の商品としてすでに確認済みです。発注設定と仕入条件はまだ反映していません。表示中の商品と設定を確認して、「発注設定と仕入条件を確認して反映」を押してください。":
    "The exact variant of this DRAFT product has already been confirmed as the current product. Replenishment settings and supplier terms have not yet been applied. Review the displayed product and settings, then choose Review and apply replenishment settings and supplier terms.",
  "現在の商品としての確認は済んでいますが、以前の反映後に保存元の発注設定または仕入条件が変わりました。自動では上書きせず、Stockyから消えた設定もUreyukiBoxから自動削除しません。表示中の設定を確認して、「発注設定と仕入条件を確認して反映」を押してください。":
    "The current-product confirmation is complete, but the saved replenishment settings or supplier terms changed after the earlier application. They will not overwrite current settings automatically, and settings removed from Stocky will not be deleted from UreyukiBox automatically. Review the displayed settings, then choose Review and apply replenishment settings and supplier terms.",
  "現在の商品としての確認は済んでいます。発注設定と仕入条件はまだ反映していません。表示中の設定を確認して、「発注設定と仕入条件を確認して反映」を押してください。":
    "The current-product confirmation is complete. Replenishment settings and supplier terms have not yet been applied. Review the displayed settings, then choose Review and apply replenishment settings and supplier terms.",
  "旧商品に発注設定または仕入条件があります。現在の商品はまだ確認されていません。候補を選び、同じ商品か確認したうえで「現在の商品として確認し、発注設定と仕入条件を反映」を押してください。":
    "The saved product has replenishment settings or supplier terms. A current product has not yet been confirmed. Choose a candidate, confirm it is the same product, then choose Confirm as the current product and apply replenishment settings and supplier terms.",
  "安全な一致候補はありますが、現在の商品としてはまだ確認されていません。候補と設定を確認して、「現在の商品として確認し、発注設定と仕入条件を反映」を押してください。":
    "A safe match candidate is available, but it has not yet been confirmed as the current product. Review the candidate and settings, then choose Confirm as the current product and apply replenishment settings and supplier terms.",
  発注設定と仕入条件を反映できませんでした:
    "Could not apply replenishment settings and supplier terms",
  "現在の商品として確認済みの商品へ、発注設定と仕入条件を確認・反映し、要確認一覧から解決済みに移動しました。税率・在庫数・棚卸し・在庫調整の履歴・既存の発注書・BOM・保存済み原本は変更していません。":
    "Reviewed and applied replenishment settings and supplier terms to the already confirmed current product, and moved the item from Needs review to Resolved. Tax rates, inventory quantities, stocktake and inventory-adjustment history, existing purchase orders, BOMs, and saved sources were not changed.",
  "現在の商品として確認済みの商品へ、発注設定と仕入条件を確認・反映しました。税率・在庫数・棚卸し・在庫調整の履歴・既存の発注書・BOM・保存済み原本は変更していません。":
    "Reviewed and applied replenishment settings and supplier terms to the already confirmed current product. Tax rates, inventory quantities, stocktake and inventory-adjustment history, existing purchase orders, BOMs, and saved sources were not changed.",
  "保存商品と現在の商品が同じだと確認し、発注設定と仕入条件を確認・反映して、要確認一覧から解決済みに移動しました。税率・在庫数・棚卸し・在庫調整の履歴・既存の発注書・BOM・保存済み原本は変更していません。":
    "Confirmed that the saved product and the current product are the same, reviewed and applied replenishment settings and supplier terms, and moved the item from Needs review to Resolved. Tax rates, inventory quantities, stocktake and inventory-adjustment history, existing purchase orders, BOMs, and saved sources were not changed.",
  "保存商品と現在の商品が同じだと確認し、発注設定と仕入条件を確認・反映しました。税率・在庫数・棚卸し・在庫調整の履歴・既存の発注書・BOM・保存済み原本は変更していません。":
    "Confirmed that the saved product and the current product are the same, then reviewed and applied replenishment settings and supplier terms. Tax rates, inventory quantities, stocktake and inventory-adjustment history, existing purchase orders, BOMs, and saved sources were not changed.",
  "以前に現在の商品を確認しましたが、最新の保存元の発注設定と仕入条件はまだ確認・反映されていません。現在の商品をもう一度確認してください。":
    "The current product was confirmed earlier, but the latest source replenishment settings and supplier terms have not yet been reviewed or applied. Confirm the current product again.",
  "以前に確認した商品と設定反映の記録はありますが、その商品は現在のShopify商品ではありません。現在の商品を選び直してください。再試行で設定を重複反映していません。":
    "A previous current-product confirmation and settings-application record exists, but that product is no longer a current Shopify product. Select the current product again. This retry did not apply the settings twice.",
  "現在の商品には該当しないと記録しました。要確認一覧から解決済みに移動しました。在庫数や保存済み履歴は変更していません。":
    "Recorded that there is no corresponding current product and moved the item from Needs review to Resolved. Inventory and saved history were unchanged.",
  "現在の商品には該当しないと記録しました。在庫数や保存済み履歴は変更していません。":
    "Recorded that there is no corresponding current product. Inventory and saved history were unchanged.",
  商品不整合を検索: "Search product mismatches",
  "元または現在の商品名・SKU・商品ID":
    "Original or current product name, SKU, or product ID",
  "元の商品と、候補・確認した現在商品の両方を検索します。":
    "Searches the original product, current candidates, and confirmed current products.",
  "検索に一致する商品不整合はありません。":
    "No product mismatches match your search.",
  "確認が必要な商品不整合はありません。":
    "There are no product mismatches requiring review.",
  "このページ {count} 件": "{count} on this page",
  "このページ {count} 件 / 全 {total} 件":
    "{count} on this page / {total} total",
  Stockyの仕入税区分: "Stocky purchase tax types",
  入荷履歴: "Receiving history",
  "発注点・リードタイム・MOQ": "Reorder points, lead times, and MOQ",
  棚卸し明細: "Stocktake items",
  現在の仕入税率: "Current purchase tax rates",
  "発注書、明細、入荷履歴、支払予定":
    "Purchase orders, line items, receiving history, and upcoming payments",
  仕入先と連絡先: "Suppliers and contact details",
  棚卸し履歴と商品明細: "Stocktake history and item details",
  一般の在庫調整と明細: "General inventory adjustments and line items",
  発注設定: "Replenishment settings",
  "発注点・リードタイム・MOQ・仕入税区分・商品と仕入先":
    "Reorder points, lead times, MOQ, purchase tax types, and product-supplier links",
  "保存レポート・原本": "Saved reports and source files",
  保存済み原本: "Saved source files",
  "保存したレポート、CSV、貼り付け原文の内容":
    "Saved reports, CSV files, and pasted source text",
  "{label}（{count}件）": "{label} ({count})",
  "{count}件": "{count}",
  "過去のStockyデータと、これからのUreyukiBoxデータをまとめて確認できます":
    "Review past Stocky data and new UreyukiBox data together",
  "Stockyから保存・取り込んだ履歴に加え、UreyukiBoxで作成する発注書・入荷履歴・在庫調整・棚卸しも同じ種類別の一覧に入ります。仕入先、発注点・リードタイム・最低発注数量（MOQ）、現在の仕入税率もここで確認できます。":
    "Alongside history saved or imported from Stocky, the same type-based lists include purchase orders, receipt history, inventory adjustments, and stocktakes created in UreyukiBox. You can also review suppliers, reorder points, lead times, minimum order quantities (MOQs), and current purchase tax rates here.",
  このデータを検索: "Search this data",
  検索: "Search",
  "「{query}」の検索結果": "Results for “{query}”",
  "名前・識別子": "Name or identifier",
  バリエーション: "Variant",
  保存時の商品バリエーション名: "Saved product variant name",
  明細: "Line items",
  保存済み原本の汎用行: "Generic rows retained from the source file",
  "原本を運用データへ変換できない場合も、サーバーが保持した解析可能な行を表示します。":
    "Shows parseable rows retained by the server even when the source could not be converted into operational data.",
  明細はプランにより非表示です: "Line items are hidden by plan",
  取得可能: "Available",
  取得不可: "Unavailable",
  レポートカテゴリ: "Report category",
  レポート名: "Report name",
  レポート種別: "Report type",
  原本種別: "Source type",
  取得方法: "Capture method",
  "必要なプラン: {plan}": "Required plan: {plan}",
  この一覧はプランにより非表示です: "This list is hidden by plan",
  "取得開始時 {count} 件・必要なプラン: {plan}":
    "{count} at retrieval start · Required plan: {plan}",
  "固定プレースホルダを表示しています。保存済み {count} 件の実際の値はブラウザへ送信していません。":
    "This is a fixed placeholder. Actual values for all {count} saved records are not sent to the browser.",
  "保存済みのデータはありません。": "No saved data.",
  "このページ {count} 件 / 取得開始時 {total} 件":
    "{count} on this page / {total} when retrieval began",
  前のページ: "Previous page",
  次のページ: "Next page",
  プランにより非表示: "Hidden by plan",
  一部の値はプランにより非表示: "Some values are hidden by plan",
  "{resource} の保存済み詳細": "Saved {resource} details",
  "保存データ更新: {date}": "Saved data updated: {date}",
  保存済み詳細を表示できません: "Could not display the saved details",
  この詳細はプランにより非表示です: "These details are hidden by plan",
  "保存件数 {count} 件・必要なプラン: {plan}":
    "{count} saved · Required plan: {plan}",
  "このストアに属するレコードを確認できませんでした。":
    "Could not verify a record belonging to this store.",
  関連する保存済み原本: "Related saved source files",
  原本をダウンロードできませんでした: "Could not download the source file",
  CSVをダウンロード: "Download CSV",
  PDFをダウンロード: "Download PDF",
  "{filename} のダウンロードを開始しました。":
    "Started downloading {filename}.",
  明細CSVをダウンロード: "Download line items as CSV",
  明細PDFをダウンロード: "Download line items as PDF",
  明細を書き出せませんでした: "Could not export the line items",
  "PDFが上限を超える場合は、CSVをご利用ください。":
    "If the PDF exceeds the limit, use CSV instead.",
  データを書き出せませんでした: "Could not export the data",
  "PDFが上限を超える場合は、検索で絞るかCSVをご利用ください。":
    "If the PDF exceeds the limit, narrow the results with search or use CSV.",
  "このレコードに直接紐づく保存済み原本はありません。":
    "No saved source file is linked directly to this record.",
  行数不明: "Row count unknown",
  "{count}行": "{count} rows",
  "原本サイズ: {size} bytes・SHA-256: {sha256}":
    "Source size: {size} bytes · SHA-256: {sha256}",
  更新日時: "Updated",
  発注書番号: "Purchase order number",
  入荷日時: "Received at",
  入荷数量: "Received quantity",
  請求書番号: "Invoice number",
  すべて: "All",
  発注済み: "Ordered",
  入荷完了: "Fully received",
  支払待ち: "Awaiting payment",
  支払期限超過: "Overdue",
  状態不明: "Status unknown",
  "この状態の発注書はありません。":
    "There are no purchase orders with this status.",
  支払期日: "Payment due",
  金額不明: "Amount unknown",
  "通貨不明: {amount}": "Unknown currency: {amount}",
  "（うち金額不明 {count}件）": " ({count} with unknown amounts)",
  一覧を見る: "View list",
  今後の支払予定: "Upcoming payments",
  "Stockyに保存された支払期日を今後{weeks}週間、週ごとに表示します。":
    "Shows payment due dates saved in Stocky, grouped by week for the next {weeks} weeks.",
  "期日が分かる今後の支払予定はありません。":
    "There are no upcoming payments with a known due date.",
  支払期日がない発注書があります:
    "Some purchase orders have no payment due date",
  "{count}件は金額を保存していますが、期日を推測せずグラフから外しています。":
    "{count} purchase orders have saved amounts but no known due date, so they are excluded from the chart rather than estimated.",
  調整日時: "Adjustment time",
  開始日時: "Started",
  適用日時: "Applied",
  アーカイブ: "Archived",
  指定されたデータは見つかりません: "The requested data could not be found",
  "データを取得できませんでした。時間をおいて再試行してください。":
    "Could not retrieve data. Wait a moment and try again.",
  現在のプランを確認できませんでした: "We could not verify the current plan",
  "安全のため、データは表示していません。":
    "For safety, no data is being shown.",

  // Merchant notes. Source and provenance fields remain internal-only.
  "保存したメモを確認・編集できます。": "View and edit saved notes.",
  メモが別の画面で更新されました: "The note was updated elsewhere",
  "最新状態を再読み込みし、内容を確認してからもう一度編集してください。":
    "Reload the latest state, review the note, then edit it again.",
  社内メモを追加: "Add internal note",
  社内メモを編集: "Edit internal note",
  保存したメモ: "Saved notes",
  "保存したメモはありません。": "There are no saved notes.",
  "更新: {date}": "Updated: {date}",
  仕入先メモ: "Supplier note",
  仕入先の発注書用メモ: "Supplier purchase order note template",
  "この更新でメモを空にしました。": "This update cleared the note.",
  新しい本文を保存: "Save new text",
  現在の表示値を空にする: "Clear the current displayed value",
  メモ本文: "Note text",
  "現在のメモを空にします。以前の内容は履歴に保持されます。":
    "This clears the current note. Earlier content remains in its history.",
  メモを保存できませんでした: "Could not save the note",
  "メモ本文を入力してください。": "Enter the note text.",
  "メモ本文にNUL文字は使えません。":
    "The note text cannot contain a NUL character.",
  "メモ本文はUTF-8で64KiB以内にしてください。":
    "Keep the note text within 64 KiB in UTF-8.",
  "Stockyで確認して転記した発注書用メモを初期入力しました。編集した内容がPDF・CSV・メール・確定済み発注書に反映されます。":
    "A purchase-order note transcribed from Stocky has been prefilled. Your reviewed edits will appear in the PDF, CSV, email, and placed purchase order.",
  "Stockyの発注書用メモは2,000文字を超えるため自動入力していません。「データ」で保存内容を確認し、必要な内容を短くして入力してください。":
    "The Stocky purchase-order note exceeds 2,000 characters, so it was not prefilled. Review the saved content under Data and enter a shorter version if needed.",
  "保存済みのStocky仕入先メモを確認できませんでした。「データ」で保存内容を確認してください。":
    "The saved Stocky supplier notes could not be verified. Review the saved content under Data.",
  "Stockyの仕入先メモ（社内用）": "Stocky supplier note (internal)",
  "UreyukiBox内だけに表示します。発注書・PDF・CSV・メールには含めません。":
    "Shown only inside UreyukiBox. It is never included in purchase orders, PDFs, CSV files, or emails.",
  "読み取った商品のShopify IDが正しくありません。":
    "The scanned product has an invalid Shopify ID.",
  "保存日時: {date}": "Saved at: {date}",

  // Native saved purchase-order drafts.
  表示する注文書: "Purchase order view",
  保存済み下書き: "Saved drafts",
  "新しい注文書（既存提案）": "New purchase order (existing suggestions)",
  送信準備済み: "Ready to send",
  原本が変更されています: "Source changed",
  Stockyで確認済み: "Observed in Stocky",
  保存内容から推定: "Inferred from saved data",
  保存済み下書きを取得できませんでした: "Could not load saved drafts",
  保存済み下書きはありません: "There are no saved drafts",
  "Stockyから保存した発注書がある場合は、ここからUreyukiBoxの注文書として編集できます。":
    "When you have purchase orders saved from Stocky, you can edit them here as UreyukiBox purchase orders.",
  注文書の作業用下書きを開けませんでした:
    "Could not open the working purchase-order draft",
  番号なしの注文書: "Purchase order without a number",
  作業用下書き作成済み: "Working draft created",
  通貨: "Currency",
  "明細 {count}件": "{count} line items",
  原本の状態: "Source status",
  注文書として編集: "Edit as purchase order",
  "送信前に確認・修正が必要です":
    "Review and corrections are required before sending",
  "下書きを保存してから送信・確定してください。":
    "Save the draft before sending or marking it as ordered.",
  "仕入先を選択してください。": "Select a supplier.",
  "明細を1件以上追加してください。": "Add at least one line item.",
  "発注数量は1以上の整数で入力してください。":
    "Enter each order quantity as a whole number of at least 1.",
  "すべての明細で税率または「税なし」を選択してください。":
    "Choose a tax rate or No tax for every line item.",
  "選択した税率を確認してください。": "Review the selected tax rate.",
  "Stocky原本の税率: {name}": "Saved Stocky tax rate: {name}",
  保存済み下書きを表示できませんでした: "Could not display the saved draft",
  一覧へ戻る: "Back to list",
  主拠点: "Primary location",
  "未保存の編集を破棄して移動しますか？":
    "Discard the unsaved changes and leave?",
  税率を選択: "Select a tax rate",
  税なし: "No tax",
  Shopify在庫へ反映不可: "Cannot update Shopify inventory",
  "1以上の整数": "Whole number of at least 1",
  "0以上の数値": "Number of at least 0",
  "0以上・小数第2位までの数値":
    "Number of at least 0 with up to 2 decimal places",
  "3文字の英字コード": "Three-letter currency code",
  税: "Tax",
  送信する注文書メールを確認: "Review the purchase-order email to send",
  "この下書きを注文済みとしてマークしますか？": "Mark this draft as ordered?",
  "明細 {count}件を確定し、入荷待ち数量へ反映します。":
    "This confirms {count} line items and adds them to incoming quantities.",
  "発注メールはまだ送信されていません。確定してもメールは送信されません。":
    "The purchase-order email has not been sent. Marking the order does not send it.",
  "発注メールの送信結果を照合するまで、注文済みとしてマークできません。":
    "This draft cannot be marked as ordered until the purchase-order email result is reconciled.",
  "発注メールの送信結果が確定するまで、注文済みとしてマークできません。":
    "This draft cannot be marked as ordered until the purchase-order email result is final.",
  保存済み下書き一覧へ: "Back to saved drafts",
  下書きを保存: "Save draft",
  Stockyからコピーした作業用下書きです: "Working draft copied from Stocky",
  "編集内容はUreyukiBoxの作業用注文書に保存され、Stockyから保存した原本は変更されません。":
    "Your edits are saved to a working purchase order in UreyukiBox. The saved Stocky source remains unchanged.",
  データの発注書原本を見る: "View the saved source under Data",
  発注メールを送信中です: "Sending the purchase-order email",
  "結果が確定するまで、下書きの編集と注文済みへの変更を停止しています。":
    "Draft editing and marking the draft as ordered are paused until the result is final.",
  発注メールの送信結果を確認してください:
    "Review the purchase-order email result",
  "新しい内容や操作キーで再送せず、保存された送信記録を照合してください。下書きの編集と注文済みへの変更を停止しています。":
    "Do not resend with new content or a new operation key. Reconcile the saved delivery record. Draft editing and marking the draft as ordered are paused.",
  発注メールを送信しました: "Purchase-order email sent",
  "メール送信だけでは注文済みになりません。内容を確認して「注文済みとしてマーク」を実行してください。":
    "Sending the email does not mark the order as ordered. Review it, then choose Mark as ordered.",
  Shopify在庫へ反映できない明細があります:
    "Some line items cannot update Shopify inventory",
  "削除済みまたは未解決の商品は原本どおり明細とメールに残りますが、入荷時にShopify在庫へ反映できません。必要に応じて現在の商品へ置き換えるか削除してください。":
    "Deleted or unresolved products remain in the line items and email exactly as saved, but cannot update Shopify inventory when received. Replace them with a current product or remove them if needed.",
  "送信・確定できない理由": "Why this draft cannot be sent or marked as ordered",
  メール送信できない理由: "Why this draft cannot be emailed",
  "通貨コードを設定すると送信できます。":
    "Set the currency code to enable email sending.",
  "仕入先情報を確認しています。": "Checking the supplier details.",
  "仕入先のメールアドレスを確認できません。":
    "The supplier email address could not be verified.",
  注文書が別の画面で更新されました:
    "The purchase order was updated in another window",
  確認事項: "Review notes",
  注文書の操作を完了できませんでした:
    "Could not complete the purchase-order action",
  商品を追加: "Add product",
  商品名またはSKU: "Product name or SKU",
  一致する現在の商品が見つかりません: "No matching current product found",
  ダッシュボードで今すぐ同期: "Sync now on the dashboard",
  "Shopifyに商品があるのに候補へ表示されない場合は、ダッシュボードで「今すぐ同期」を実行してからもう一度検索してください。自動では対応付けません。":
    "If the product exists in Shopify but does not appear here, run Sync now on the dashboard and search again. It will not be matched automatically.",
  追加済み: "Added",
  "保存した下書きを編集・送信するか、新しい注文書を作成します。":
    "Edit and send a saved draft, or create a new purchase order.",
  指定された下書きは見つかりません: "The requested draft could not be found",
  指定されたStocky原本を確認できませんでした:
    "Could not verify the requested Stocky source",
  一部の保存済み下書きだけを表示しています:
    "Only some saved drafts are displayed",
  "一覧の上限を超えた下書きがあります。目的の注文書が見つからない場合はデータの発注書原本から開いてください。":
    "There are more drafts than this list can show. If you cannot find the purchase order, open it from its saved source under Data.",
  保存済みの仕入先: "Saved supplier",
  保存済みの配送先: "Saved destination",
  仕入先と配送先を確認しています: "Checking the supplier and destination",
  "確認が完了するまで、下書きの保存・メール送信・注文済みへの変更はできません。":
    "You cannot save, email, or mark this draft as ordered until the checks finish.",
  仕入先または配送先を確認できませんでした:
    "Could not verify the supplier or destination",
  "安全のため、確認が完了するまで保存・メール送信・注文済みへの変更はできません。":
    "For safety, saving, emailing, and marking the draft as ordered remain unavailable until verification succeeds.",
  商品を検索できませんでした: "Could not search for products",

  取り込める12項目: "The 12 importable data categories",
  "左列はUreyukiBoxでの保存先、右列は使える保存済みファイル・公式エクスポートです。ファイルにない項目は補完しません。":
    "The left column shows the UreyukiBox destination; the right shows compatible saved files and official exports. Missing fields are not inferred.",
  保存済みファイルだけでは完全取得できない項目があります:
    "Some categories cannot be captured completely from saved files",
  "商品・バリエーションはShopify同期を正とし、需要予測設定は保存済みファイルから取得できません。仕入先・仕入用税区分・商品と仕入先の紐付けは、公式ファイルがない場合に手元で作るCSVで補います。":
    "Shopify sync is authoritative for products and variants, and forecasting settings cannot be recovered from saved files. When no official file exists, supplement suppliers, purchase tax categories, and product-to-supplier links with CSV files you create.",
  取込先: "Import destination",
  "使う保存済みファイル・公式エクスポート": "Saved file or official export to use",
  入力欄へ進む: "Go to the input fields",
  "Stocky公式CSVなし。手元で作る suppliers.csv（仕入先名、会社名、担当者、メール、電話）で補います。":
    "Stocky has no official CSV. Supplement it with a suppliers.csv file you create containing supplier name, company, contact, email, and phone.",
  "Stocky公式CSVなし。手元で作る purchase_tax_types.csv（Tax name、Rate %、Purpose）で補います。":
    "Stocky has no official CSV. Supplement it with a purchase_tax_types.csv file you create containing Tax name, Rate %, and Purpose.",
  "Stocky公式CSVなし。手元で作る supplier_links.csv（SKU、仕入先名、単価、入数）で補います。":
    "Stocky has no official CSV. Supplement it with a supplier_links.csv file you create containing SKU, supplier name, unit cost, and pack size.",
  "保存済みの purchase_orders.csv と、各発注書の purchase_order_<PO番号>.csv を選びます。":
    "Select a saved purchase_orders.csv and purchase_order_<PO number>.csv for every purchase order.",
  "保存済みの stock_adjustments.csv を選びます。":
    "Select a saved stock_adjustments.csv.",
  "保存済みの low_stock_csv.csv を選びます。":
    "Select a saved low_stock_csv.csv.",
  "Low Stock・発注点と同じ low_stock_csv.csv の Vendor・Lead Time を使います。":
    "Use the Vendor and Lead Time columns in the same low_stock_csv.csv used for Low Stock and reorder points.",
  "終了前に保存した Order limits の表がある場合だけ、Vendorごとに貼り付けます。":
    "For each vendor, paste an Order limits table only if you saved it before shutdown.",
  "保存済みの download_csv.csv を、棚卸し1件につき1ファイル選びます。":
    "Select one saved download_csv.csv file per stocktake.",
  "保存済みファイルからは完全取得できません。現在のShopify商品はダッシュボードの「今すぐ同期」で更新します。":
    "Saved files cannot capture this completely. Update the current Shopify catalog with Sync now on the dashboard.",
  "Stocky公式エクスポートまたは終了前に保存した、Stock on hand・Average costなどの対応CSVを選びます。":
    "Select compatible CSV files such as Stock on hand and Average cost from Stocky's official export or files saved before shutdown.",
  "Stock on hand・Average costなど、StockyからダウンロードしたCSVを選びます。":
    "Select CSV files downloaded from Stocky, such as Stock on hand and Average cost.",
  "保存済みファイルからは取得できません。":
    "This data cannot be recovered from saved files.",
  "Stocky公式CSVはありません。手元で作る suppliers.csv の1行目に name を入れ、必要なら stocky_supplier_id、is_hidden、company_name、contact_name、contact_email、phone を追加します。name だけが必須です。空欄は既存値を消しません。":
    "Stocky has no official CSV. In the first row of a suppliers.csv file you create, include name and optionally stocky_supplier_id, is_hidden, company_name, contact_name, contact_email, and phone. Only name is required. Blank cells do not erase existing values.",
  "suppliers.csv（手元で作るCSV）": "suppliers.csv (a CSV file you create)",
  "Stocky公式CSVはありません。手元で作る supplier_links.csv の1行目に sku,supplier_name を入れ、必要なら is_preferred,unit_cost,pack_size,min_order_qty,lead_time_days を追加します。SKUと仕入先名が必須です。CSVにない既存の紐付けは削除しません。":
    "Stocky has no official CSV. In the first row of a supplier_links.csv file you create, include sku and supplier_name, and optionally is_preferred, unit_cost, pack_size, min_order_qty, and lead_time_days. SKU and supplier name are required. Existing links absent from the CSV are not deleted.",
  "supplier_links.csv（手元で作るCSV）":
    "supplier_links.csv (a CSV file you create)",
  Stockyから書き出せる仕入先CSVはありません:
    "Stocky does not provide an exportable supplier CSV",
  "下の2ファイルはStockyからダウンロードするCSVではありません。公式エクスポートに対象ファイルがなく、手元の情報で補う場合だけ作成してください。":
    "The two files below are not CSV exports downloaded from Stocky. Create them only when the official export has no matching file and you need to supplement the data you have.",
  "先に suppliers.csv、次に supplier_links.csv を選びます。各欄に必要なファイル名と1行目の列名を表示しています。":
    "Select suppliers.csv first, then supplier_links.csv. Each field shows the required filename and first-row column names.",
  "2. purchase_order_<PO番号>.csv（複数選択可）":
    "2. purchase_order_<PO number>.csv (multiple files allowed)",
  "選ぶファイル: stock_adjustments.csv（複数選択可）":
    "File to select: stock_adjustments.csv (multiple files allowed)",
  "Stockyで Reports → Purchase Orders → CSV を押し、purchase_orders.csvを1つ選びます。次に Purchases から各発注書を開き、Download → CSV で取得した purchase_order_<PO番号>.csvを選びます。Received Only / Not Received Onlyは使いません。個別CSVがない発注書は、明細未取得のまま親一覧と原本を保存できます。":
    "In Stocky, choose Reports → Purchase Orders → CSV and select one purchase_orders.csv file. Then open each purchase order under Purchases and select the purchase_order_<PO number>.csv file downloaded from Download → CSV. Do not use Received Only or Not Received Only. A purchase order without an individual CSV can still be saved with its parent-list record and source file, with line items marked as not retrieved.",
  "Stockyで Reports → Adjustments → CSV を押し、stock_adjustments.csvを選びます。期間や拠点を分けて書き出した場合は、取得したCSVをすべて選びます。一般の在庫調整と棚卸し履歴は別データです。":
    "In Stocky, choose Reports → Adjustments → CSV and select stock_adjustments.csv. If you exported separate periods or locations, select every downloaded CSV. General stock adjustments and stocktake history are separate data.",
  指定なし: "Not specified",
  Stockyでの取得場所: "Where to get it in Stocky",
  "Reports → Stock on hand → CSV": "Reports → Stock on hand → CSV",
  "Reports → Low Stock → SKU/Variants → CSV":
    "Reports → Low Stock → SKU/Variants → CSV",
  "Stocky公式CSVなし。上の仕入先欄で作成したCSVを選びます。":
    "Stocky has no official CSV. Select the CSV you created in the Suppliers field above.",
  "Stocky公式CSVなし。上の商品と仕入先の紐付け欄で作成したCSVを選びます。":
    "Stocky has no official CSV. Select the CSV you created in the Product-to-supplier links field above.",
  "Reports → Stock on handでAverage costを選択 → CSV":
    "Reports → Stock on hand → select Average cost → CSV",
  "Reports → Purchase Orders → CSV": "Reports → Purchase Orders → CSV",
  "Purchases → 対象の発注書 → Download → CSV":
    "Purchases → selected purchase order → Download → CSV",
  "Reports → Adjustments → CSV": "Reports → Adjustments → CSV",
  "low_stock_csv.csv（専用欄の上限を超えた場合だけ）":
    "low_stock_csv.csv (only when it exceeds the dedicated field limit)",
  "stock_on_hand.csv（Average costを選んで書き出したもの）":
    "stock_on_hand.csv (exported with Average cost selected)",
  Stockyから書き出せる仕入用税区分CSVはありません:
    "Stocky does not provide an exportable purchase-tax-category CSV",
  "公式エクスポートに対象ファイルがなく、手元の情報で補う場合だけ purchase_tax_types.csv を作ります。1行目は Tax name, Rate %, Tax ID, Purpose です。Tax IDは分からなければ空欄にし、Purposeにはpurchaseと入力します。":
    "Create purchase_tax_types.csv only when the official export has no matching file and you need to supplement the data you have. The first row must be Tax name, Rate %, Tax ID, Purpose. Leave Tax ID blank if unknown and enter purchase under Purpose.",
  "選ぶファイル: purchase_tax_types.csv（手元で作るCSV）":
    "File to select: purchase_tax_types.csv (a CSV file you create)",
  "Low Stock・発注点／リードタイム・棚卸し履歴・明細・最低発注数量（MOQ）":
    "Low Stock and reorder points / lead times, stocktake history and lines, and minimum order quantities (MOQ)",
  "仕入先・仕入用税区分・商品と仕入先の紐付け・発注書・明細・在庫調整・明細":
    "Suppliers, purchase tax categories, product-to-supplier links, purchase orders and lines, and stock adjustments and lines",
  "添付できるのは PDF、CSV、Excel、Word、PNG、JPEG のみです。":
    "Only PDF, CSV, Excel, Word, PNG, and JPEG files can be attached.",
  "空の添付ファイルは送信できません。": "Empty attachments cannot be sent.",
  "添付ファイル1件は 1 MiB 以下にしてください。":
    "Each attachment must be 1 MiB or smaller.",
  "添付ファイルを読み取れませんでした。": "The attachment could not be read.",
  "送信受付後のメールは編集・添付変更できません。結果を確認してください。":
    "An accepted email cannot be edited or changed. Check its result.",
  "添付は 3 件までです。": "You can attach up to 3 files.",
  "添付ファイル合計は 2 MiB 以下にしてください。":
    "Attachments must total 2 MiB or less.",
  "同じ名前の添付ファイルは1件だけにしてください。":
    "Attach only one file with the same name.",
  "添付ファイルを追加できませんでした。": "The attachment could not be added.",
  "宛先・件名・本文・添付を確認または編集してから送信してください。確認後に在庫や設定が変わった場合は送信せず、最新内容の再確認を求めます。":
    "Review or edit the recipient, subject, body, and attachments before sending. If inventory or settings change after review, sending stops and requires a new review.",
  "有効な宛先、改行を含まない件名、空白だけではない本文を入力してください。":
    "Enter a valid recipient, a subject without line breaks, and a non-blank body.",
  "本文は省略せず、そのまま送信されます。":
    "The full body is sent without shortening it.",
  添付ファイル: "Attachments",
  "purchase-order.pdf（発注書PDF）": "purchase-order.pdf (purchase-order PDF)",
  外す: "Remove",
  "添付なしで送信します。": "This email will be sent without attachments.",
  添付ファイルを選択: "Choose attachments",
  "PDF、CSV、Excel、Word、PNG、JPEG。3件まで、1件1 MiB、合計2 MiBまで。":
    "PDF, CSV, Excel, Word, PNG, or JPEG. Up to 3 files, 1 MiB each, 2 MiB total.",
  "保存済みの送信内容を安全に確認できません。ブラウザのストレージ設定を確認してください。":
    "The saved email content could not be verified safely. Check your browser storage settings.",
  送信内容を安全に保存できません:
    "The email content could not be stored safely",
  "送信内容と操作キーをこのタブに安全に保存できなかったため、メールは送信していません。ブラウザのストレージ設定を確認してください。":
    "The email content and operation key could not be stored safely in this tab, so no email was sent. Check your browser storage settings.",
  同じ内容で送信を再開: "Resume sending the same content",
  "宛先・件名・本文・添付を確認または編集してから送信してください。確認後に保存済み発注書が変わった場合は送信せず、最新内容の再確認を求めます。":
    "Review or edit the recipient, subject, body, and attachments before sending. If the saved purchase order changes after review, sending stops and requires a new review.",
  "現在は主拠点（{name}）のみの資金繰りカレンダーです。":
    "The cash-flow calendar currently covers only the primary location ({name}).",
  "現在は主拠点（{name}）のみの在庫評価額です。":
    "The inventory valuation currently covers only the primary location ({name}).",
  "現在は主拠点（{name}）のみの発注書です。":
    "Purchase orders currently cover only the primary location ({name}).",
};
